// Words and small rules shared by the Hiring screens (Master Data → Stores: the Hiring
// column, the Hiring tab, the Link hiring window, the Raise hiring window, the hiring
// request window and the hiring panel).
//
// Kept apart from the components so what a person reads — which pill, which sentence for
// which reason, how a moment is written, which opening may be picked — is in one place
// and tested on its own. The server remains the authority on every status and every
// refusal; these only choose the words for what it answered. Types only are imported, so
// the file also runs under a mocked api module.
import type {
  HiringAddressText,
  HiringAttentionItem,
  HiringCancelOutcome,
  HiringDashStatus,
  HiringLink,
  HiringLinkList,
  HiringLinkReason,
  HiringMessagesStatus,
  HiringNumbers,
  HiringPeopleSearch,
  HiringPersonOption,
  HiringPickerOpening,
  HiringPickerRound,
  HiringRaiseBlocking,
  HiringRaiseBudget,
  HiringRaiseCandidate,
  HiringRaiseCity,
  HiringRaiseInput,
  HiringRaisePreview,
  HiringRaiseReason,
  HiringRaiseTeam,
  HiringRaiseTeamOptions,
  HiringReplaceCard,
  HiringReplacePeople,
  HiringReviewTeam,
  HiringStaff,
  HiringRequest,
  HiringRequestStanding,
  HiringRoleAddressSource,
  HiringStageCounts,
  HiringStatus,
  HiringStoreOption,
  HiringStoreSearch,
  HiringTimelineEvent,
  HiringUnavailableReason,
} from '../../services/api';
import type { HiringMoveInput, HiringMoveOutcome } from '../../services/hiringRecruitmentApi';
import type { OpeningCandidateRow } from '../../services/hiringOpeningApi';

/* ─────────────────────────────── refusals ─────────────────────────────── */

/** The refusal codes the screens act on. Every other code only shows its sentence. */
export const HIRING_CODES = {
  notEnabled: 'HIRING_NOT_ENABLED',
  noAccess: 'HIRING_NO_ACCESS',
  recruitmentUnavailable: 'RECRUITMENT_UNAVAILABLE',
  linkNotLive: 'LINK_NOT_LIVE',
  linkNotFound: 'LINK_NOT_FOUND',
  alreadyLinked: 'OPENING_ALREADY_LINKED',
  sharedFull: 'SHARED_OPENING_FULL',
  notInBudget: 'OPENING_NOT_IN_BUDGET',
  noOpenPosition: 'OPENING_HAS_NO_OPEN_POSITION',
  severalPositions: 'OPENING_HAS_SEVERAL_POSITIONS',
  sharedNotAllowed: 'SHARED_NOT_ALLOWED',
  noRecruitmentIdentity: 'NO_RECRUITMENT_IDENTITY',
} as const;

export const RECRUITMENT_DID_NOT_ANSWER = 'Recruitment did not answer. Try again in a moment.';
// Recruitment DID answer, with a "no" or an error of its own. One reason on the wire
// covers a refusal for this person's 1Office account, a 403 and a server error there, so
// the sentence names both kinds and does not say that trying again will help.
const TELL_ADMINISTRATOR = 'If this continues, tell your administrator.';
export const RECRUITMENT_ANSWERED_WITH_ERROR =
  `Recruitment answered with a refusal or an error, so the numbers could not be read. ${TELL_ADMINISTRATOR}`;
export const RECRUITMENT_ANSWER_UNREADABLE = `Recruitment answered, but its answer could not be read. ${TELL_ADMINISTRATOR}`;

type Refusal = { response?: { status?: number; data?: { code?: unknown; message?: unknown } } };

export const hiringStatusOf = (err: unknown): number | undefined => (err as Refusal)?.response?.status;

export const hiringCodeOf = (err: unknown): string | undefined => {
  const code = (err as Refusal)?.response?.data?.code;
  return typeof code === 'string' ? code : undefined;
};

/** Hiring was switched off for the project (or the server) since the page asked. */
export const isHiringOff = (err: unknown): boolean =>
  hiringStatusOf(err) === 404 && hiringCodeOf(err) === HIRING_CODES.notEnabled;

/**
 * The team section's read FAILED - no answer, or Recruitment / 1Office failing (HTTP 5xx:
 * unavailable, an unreadable answer, a sign-in or token refusal there) - as against a refusal
 * about the budget (422: not the project's, not active, a round nobody can take), which says
 * what to fix. A failed read draws the Recruiters and Offer makers rows, each saying so in
 * its open list with "Try again". The server sends ONE error for the whole team, so a row
 * says its own list was the trouble only where `teamEmptyRoleOf` names it.
 */
export const isTeamReadFailure = (err: unknown): boolean => {
  const status = hiringStatusOf(err);
  return status === undefined || status >= 500;
};

/** The two role lists of the team section, by the answer's own field names. */
export type TeamRoleList = 'recruiters' | 'offerMakers';

/**
 * The role list 1Office answered EMPTY, when that is why the team could not be read: the
 * server never sends an empty role list - it answers 503 RECRUITMENT_UNAVAILABLE naming the
 * list in `detail.emptyRole` (15 Oct 2026). Null for every other failure.
 */
export const teamEmptyRoleOf = (err: unknown): TeamRoleList | null => {
  if (hiringCodeOf(err) !== HIRING_CODES.recruitmentUnavailable) return null;
  const role = hiringDetailOf(err).emptyRole;
  return role === 'recruiters' || role === 'offerMakers' ? role : null;
};

/**
 * The caller has no 1Office account, so nothing of Recruitment can be read in their name.
 * Asking again gives the same answer every time: no "Try again" is offered for it.
 */
export const isNoRecruitmentIdentity = (err: unknown): boolean => hiringCodeOf(err) === HIRING_CODES.noRecruitmentIdentity;

/**
 * What a failed hiring call says on screen: the server's own sentence, except where it
 * names something the screen calls by another word. `fallback` is for no answer at all.
 */
export const hiringErrorText = (err: unknown, fallback: string): string => {
  const code = hiringCodeOf(err);
  if (code === HIRING_CODES.recruitmentUnavailable) return RECRUITMENT_DID_NOT_ANSWER;
  if (code === HIRING_CODES.linkNotLive) return 'This opening is no longer being followed.';
  if (code === HIRING_CODES.linkNotFound) return 'This opening is not followed in this project.';
  const message = (err as Refusal)?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

/* ─────────────────────────────── moments ─────────────────────────────── */

// India time whatever the browser's zone, as on the other admin screens. The narrow
// space and the upper-case AM some browsers put in are evened out, so the same moment
// always reads the same.
const INDIA = 'Asia/Kolkata';
const tidy = (text: string): string =>
  text.replace(/[  ]/g, ' ').replace(/\b(AM|PM)\b/g, (m) => m.toLowerCase());

/** A moment as "09 Mar 2031, 10:00 am"; anything unreadable is shown as it came. */
export const formatHiringMoment = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return tidy(d.toLocaleString('en-IN', {
    timeZone: INDIA, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
  }));
};

/** A day as "09 Mar 2031". */
export const formatHiringDay = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return tidy(d.toLocaleDateString('en-IN', { timeZone: INDIA, day: '2-digit', month: 'short', year: 'numeric' }));
};

/* ─────────────────────────────── pills ─────────────────────────────── */

export interface HiringPill { text: string; className: string }

const GREY = 'border-gray-200 bg-gray-100 text-gray-600';

const PILL_CLASS: Partial<Record<HiringStatus, string>> = {
  hiring_in_progress: 'border-amber-200 bg-amber-100 text-amber-800',
  shortlisted_offered: 'border-sky-200 bg-sky-100 text-sky-800',
  joined_not_placed: 'border-green-200 bg-green-100 text-green-800',
  no_open_position: GREY,
  // Being raised from ChannelPlex: Recruitment has not confirmed an opening yet.
  raising: 'border-violet-200 bg-violet-100 text-violet-800',
  not_listed: 'border-rose-200 bg-rose-100 text-rose-800',
  // A message from Recruitment said the opening was deleted or closed there.
  closed_in_recruitment: 'border-rose-200 bg-rose-100 text-rose-800',
  numbers_unavailable: 'border-gray-300 bg-white text-gray-600',
};

/**
 * What each status is called on these screens. Every status the API can answer is
 * listed (the type makes a missing one a compile error); the three the screens have no
 * word of their own for carry the server's. The server's one status "Shortlisted /
 * Offered" is two words here, chosen by the figures: see statusWords. Its first half
 * reads "Selected", the name Recruitment gives that stage and the one the Candidates row
 * and the card's sentence use: one thing, one name.
 */
const STATUS_WORDS: Record<HiringStatus, string> = {
  hiring_in_progress: 'Hiring in progress',
  shortlisted_offered: 'Selected',
  joined_not_placed: 'Joined, not yet at this store',
  no_open_position: 'No open position',
  // A good read of the budget's list did not find it: 1Office leaves out only a deleted opening.
  not_listed: 'Deleted in Recruitment',
  closed_in_recruitment: 'Hiring closed in Recruitment',
  numbers_unavailable: 'Numbers not available',
  on_hold: 'Hiring on hold',
  raising: 'Raising hiring',
  no_hiring: 'No hiring',
};

export const HIRING_STATUSES = Object.keys(STATUS_WORDS) as HiringStatus[];

/** An offer is out on the opening, by Recruitment's own count of offers. */
const offerIsOut = (numbers: HiringNumbers | null | undefined): boolean => (numbers?.offered ?? 0) >= 1;

/**
 * A status in the screens' words. `numbers` are the figures shown with it: they decide
 * whether "Shortlisted / Offered" reads "Offered" (an offer is out) or "Selected"
 * (a candidate is at Selected, and no offer has been made yet).
 */
export const statusWords = (
  status: HiringStatus,
  subjectKind: 'store' | 'role',
  numbers?: HiringNumbers | null,
): string => {
  if (status === 'joined_not_placed' && subjectKind === 'role') return 'Joined, role not yet given';
  if (status === 'shortlisted_offered') return offerIsOut(numbers) ? 'Offered' : STATUS_WORDS.shortlisted_offered;
  return STATUS_WORDS[status] ?? 'Numbers not available';
};

export const NO_HIRING_PILL: HiringPill = { text: 'No hiring linked', className: GREY };

/** How a request to raise hiring ended when it made no link of its own. */
export const NOT_CREATED = 'Not created';
export const LINKED_BY_HAND_INSTEAD = 'Linked by hand instead';
/** A link whose opening Recruitment deleted because a person cancelled it from ChannelPlex. */
export const CANCELLED = 'Cancelled';
/**
 * A link the server says is no longer followed, when this screen does not know yet HOW it
 * ended: someone stopped following it (nothing changed in Recruitment), or someone
 * cancelled it (Recruitment deleted the opening). The pill says neither until the card's
 * timeline has said which. (It keeps these words for the two other ways a link ends - a
 * person closed it because Recruitment had deleted the opening, or its joiner was placed
 * and it was closed as filled - and the line under the card then says when: `closedHowOf`.)
 */
export const NO_LONGER_FOLLOWED = 'No longer followed';

/**
 * One link's pill. A link a person stopped following says so, and so do an opening that
 * was cancelled in Recruitment from here and a request to raise hiring that created
 * nothing (or whose opening was linked by hand meanwhile); any other closed link keeps
 * the server's word.
 */
export const linkPill = (link: HiringLink): HiringPill => {
  if (link.state === 'closed') {
    const closed = 'border-gray-200 bg-gray-100 text-gray-500';
    if (link.closedAs === 'released') return { text: 'Stopped following', className: closed };
    if (link.closedAs === 'cancelled') return { text: CANCELLED, className: closed };
    if (link.closedAs === 'create_failed') return { text: NOT_CREATED, className: closed };
    if (link.closedAs === 'superseded') return { text: LINKED_BY_HAND_INSTEAD, className: closed };
    return { text: link.statusLabel || STATUS_WORDS.no_hiring, className: GREY };
  }
  const className = PILL_CLASS[link.status];
  // A status with no colour of its own here is one these screens have no word for yet.
  if (!className) return { text: link.statusLabel || statusWords(link.status, link.subject.kind, link.numbers), className: GREY };
  return { text: statusWords(link.status, link.subject.kind, link.numbers), className };
};

/** The furthest-along first: which one status a store with several links shows. Mirrors the API. */
export const SUBJECT_ORDER: readonly HiringStatus[] = [
  'joined_not_placed', 'shortlisted_offered', 'hiring_in_progress', 'on_hold', 'raising',
  'no_open_position', 'closed_in_recruitment', 'not_listed', 'numbers_unavailable', 'no_hiring',
];

/** Recruitment no longer lists the opening: by a read that did not find it, or by a message that said it was deleted or closed. */
const isGone = (status: HiringStatus | null | undefined): boolean => status === 'not_listed' || status === 'closed_in_recruitment';

/**
 * Deleted in Recruitment (21 Oct 2026): a followed opening that a good read of its budget's
 * list did not find. 1Office's list (budget/fetchAllBudgetOpenings) has no size limit and
 * leaves an opening out only when it, or its budget, was deleted - so that is what it was.
 * Said of the link as the server sent it, and nothing else: once a read lists the opening
 * again (1Office restored it), the link's status says so and this is false.
 *
 * Not an opening ChannelPlex raised whose list has not shown it since (`attention`
 * 'not_listed'): that one has its own question, and Recruitment's read copy may be behind.
 */
export const deletedInRecruitment = (link: Pick<HiringLink, 'state' | 'status' | 'attention'>): boolean =>
  link.state === 'live' && link.status === 'not_listed' && !(link.attention ?? []).includes('not_listed');

const openingWords = (link: HiringLink): string =>
  `${link.openingId != null ? `Opening ${link.openingId}` : 'Opening'}${link.shared ? ' (shared)' : ''}`;

/** What changed in Recruitment on any of these links, in the words used beside a pill. */
export const flagWords = (links: readonly HiringLink[]): string[] => {
  const words: string[] = [];
  if (links.some((l) => l.flags?.addressChanged || l.flags?.positionsChanged)) words.push('changed in Recruitment');
  if (links.some((l) => l.flags?.joinerExiting)) words.push('joiner exiting');
  // An opening ChannelPlex raised that Recruitment did not finish setting up.
  if (links.some((l) => l.state === 'live' && l.attention?.includes('created_incomplete'))) words.push(RAISE_NOT_FULLY_SET_UP);
  // A message from Recruitment left a question only a person can answer.
  if (links.some((l) => l.state === 'live' && (l.attentionItems?.length ?? 0) > 0)) words.push(ATTENTION_WORDS.storeLine);
  return words;
};

const counted = (n: number, one: string): string => `${n} ${n === 1 ? one : `${one}s`}`;

const inProcessWords = (n: number | null | undefined): string | null =>
  (typeof n === 'number' && n > 0 ? `${counted(n, 'candidate')} in process` : null);

/**
 * "4 candidates in process": everyone still moving on an opening (screening, interview
 * or selected). Null when the counts are not known, or nobody is in process: the short
 * lines then say nothing about candidates rather than "0".
 */
export const candidatesInProcessWords = (stages: HiringStageCounts | null | undefined): string | null =>
  inProcessWords(stages?.inProcess);

export interface StoreHiringSummary { pill: HiringPill; line: string | null }

/**
 * A store's Hiring cell from its live links: one pill and the line under it.
 *
 * Several links add their open positions up only when every figure was read just now
 * and none is a shared opening's: a shared opening's figures are for the whole opening,
 * and a link with no figures would silently drop out of the total. Their candidates in
 * process add up under the same rule, and only when every opening's counts are known.
 */
export const summariseStoreHiring = (links: readonly HiringLink[]): StoreHiringSummary => {
  const live = links.filter((l) => l.state !== 'closed');
  if (live.length === 0) return { pill: NO_HIRING_PILL, line: null };

  const parts: string[] = [];
  let shown = live[0];
  if (live.length === 1 && shown.state === 'sending') {
    // Being raised: there is no opening yet, only where the request stands.
    const standing = raiseShortLine(shown);
    if (standing) parts.push(standing);
  } else if (live.length === 1) {
    parts.push(openingWords(shown));
    const joining = offerJoiningWords(shown);
    if (joining) {
      // Recruitment said when the person offered is expected to join: that says more than "0 open".
      parts.push(joining);
    } else if (shown.numbers && !isGone(shown.status)) {
      const figures = [`${shown.numbers.open} open`];
      const candidates = candidatesInProcessWords(shown.stages);
      if (candidates) figures.push(candidates);
      // The figures and the counts of one read: old ones say so once, as on the Hiring tab.
      parts.push(shown.numbersFrom === 'live' ? figures.join(' · ') : `${figures.join(' · ')} when last read`);
    }
  } else {
    const furthest = SUBJECT_ORDER.find((status) => live.some((l) => l.status === status));
    shown = live.find((l) => l.status === furthest) ?? shown;
    const raising = live.filter((l) => l.state === 'sending').length;
    // A request being raised is not an opening yet: it is counted apart.
    parts.push(raising === 0 ? `${live.length} openings` : `${counted(live.length - raising, 'opening')} · ${raising} being raised`);
    if (live.every((l) => l.numbers && l.numbersFrom === 'live' && !l.shared)) {
      parts.push(`${live.reduce((sum, l) => sum + (l.numbers?.open ?? 0), 0)} open`);
      if (live.every((l) => l.stages)) {
        const candidates = inProcessWords(live.reduce((sum, l) => sum + (l.stages?.inProcess ?? 0), 0));
        if (candidates) parts.push(candidates);
      }
    }
  }
  parts.push(...flagWords(live));
  // A cancel Recruitment has not confirmed, on any of its openings: said on the row, where it is opened to check.
  const cancelling = live.map((l) => cancelShortLine(l)).find((line) => !!line);
  if (cancelling) parts.push(cancelling);
  return { pill: linkPill(shown), line: parts.length > 0 ? parts.join(' · ') : null };
};

/**
 * One link's figures on the Hiring tab, with its candidates in process after them. Old
 * figures say so; a link never read has none.
 */
export const numbersWords = (link: HiringLink): string | null => {
  const n = link.numbers;
  if (!n) return null;
  const parts = [counted(n.positions, 'position'), `${n.filled} filled`, `${n.offered} offered`, `${n.open} open`];
  const candidates = candidatesInProcessWords(link.stages);
  if (candidates) parts.push(candidates);
  const figures = parts.join(' · ');
  return link.state !== 'closed' && link.numbersFrom === 'live' ? figures : `${figures} when last read`;
};

/* ─────────────────────────────── candidates, as counts ─────────────────────────────── */

export const CANDIDATES_HEADING = 'Candidates';
export const NO_CANDIDATES_YET = 'No candidates yet';
export const NO_CANDIDATES_IN_PROCESS = 'No candidates in process';
export const CANDIDATES_NOT_AVAILABLE = 'Candidate counts are not available right now.';
/**
 * What the counts are, and what they leave out. Shown under them.
 *
 * The second note, as the 1Office team confirmed it (their reply of 12 October 2026):
 * Recruitment's read counts APPLICATIONS (a candidate on two openings is one on each),
 * and hides every application still waiting to be screened that names where it came
 * from - the jobs portal, auto-assign, screener sourcing, a re-hire, a direct offer -
 * not only people who applied by themselves. One with no source on it is counted, hence
 * "mostly". It must not say "who applied on their own": that names too few.
 */
export const CANDIDATE_NOTES: readonly string[] = [
  'Counts only. Names are in Recruitment.',
  'Applications that have not been screened yet are mostly not counted here.',
];

/**
 * Where a card's kept figures (`what`: its numbers, or its candidate counts) came from.
 *
 * Dated only when the date on the link is that of the read the figures came from. It is
 * not for an opening Recruitment no longer lists: the server keeps the figures of the
 * last listing and moves the date on at each later read that finds it gone. A link nobody
 * follows any more may have been one of those, and the answer does not say, so it is not
 * dated either.
 */
const keptFiguresLine = (link: HiringLink, what: 'numbers' | 'counts'): string => {
  if (link.state === 'closed') return `These ${what} are the last ones read while this opening was followed.`;
  if (isGone(link.status) || isGone(link.lastKnownStatus)) {
    return `These ${what} are from the last time Recruitment listed this opening.`;
  }
  const when = link.seenAt ? formatHiringMoment(link.seenAt) : null;
  return when ? `These ${what} were last read on ${when}.` : `These ${what} are from the last read.`;
};

/** The counts a card shows, in the order a candidate moves through them. */
const CANDIDATE_COUNTS: readonly { key: keyof HiringStageCounts; label: string }[] = [
  { key: 'screening', label: 'Screening' },
  { key: 'interview', label: 'Interview' },
  { key: 'selected', label: 'Selected' },
  { key: 'offered', label: 'Offered' },
  { key: 'joined', label: 'Joined' },
  { key: 'dropped', label: 'No longer in process' },
];
export const CANDIDATE_COUNT_LABELS = CANDIDATE_COUNTS.map((c) => c.label);

/** Every count the API sends for an opening. Held to the API's own list by the contract test. */
const STAGE_KEYS = {
  inProcess: true, screening: true, interview: true, selected: true, offered: true,
  joined: true, dropped: true, leftAfterJoining: true, other: true,
} satisfies Record<keyof HiringStageCounts, true>;
export const CANDIDATE_STAGE_KEYS = Object.keys(STAGE_KEYS) as (keyof HiringStageCounts)[];

export interface CandidatesRow {
  /** The stages with someone at them. A stage with nobody is left out. */
  counts: { label: string; count: number }[];
  /** Said in place of counts: nobody yet, nobody in process, or the counts could not be read. */
  sentence: string | null;
  /** Said under counts kept from an earlier read; null when they were read just now. */
  lastRead: string | null;
  /** What the counts are and what they leave out; empty when there are none to explain. */
  notes: readonly string[];
}

/**
 * The "Candidates" row of a card. Counts only: the API sends no names, and none is shown.
 *
 * Null (no row) only for a link nobody follows any more that kept no counts: "not
 * available right now" would promise a later answer it will never get.
 */
export const candidatesRow = (link: HiringLink): CandidatesRow | null => {
  const stages = link.stages;
  if (!stages) {
    return link.state === 'closed' ? null : { counts: [], sentence: CANDIDATES_NOT_AVAILABLE, lastRead: null, notes: [] };
  }
  const counts = CANDIDATE_COUNTS
    .map(({ key, label }) => ({ label, count: stages[key] }))
    .filter((c) => c.count > 0);
  // "Yet" only when nobody was ever put forward. Someone who joined and has since left,
  // or stands at a stage the server does not know, is not "nobody".
  const nobodyAtAll = CANDIDATE_STAGE_KEYS.every((key) => !(stages[key] > 0));
  return {
    counts,
    sentence: counts.length > 0 ? null : nobodyAtAll ? NO_CANDIDATES_YET : NO_CANDIDATES_IN_PROCESS,
    lastRead: link.stagesFrom === 'last_seen' ? keptFiguresLine(link, 'counts') : null,
    // Kept counts that a message from Recruitment brought count every application, screened
    // or not: the second note is true only of the counts ChannelPlex reads itself.
    notes: link.stagesFrom === 'last_seen' && link.seenSource === 'message' ? CANDIDATE_NOTES.slice(0, 1) : CANDIDATE_NOTES,
  };
};

/* ─────────────────────────────── a link in full ─────────────────────────────── */

const REASON_SENTENCE: Record<HiringUnavailableReason, string> = {
  no_identity: 'Your sign-in has no 1Office account, so Recruitment cannot be read in your name.',
  sign_in_failed: 'Recruitment did not accept your 1Office sign-in. Sign out and sign in again.',
  budget_not_in_project: 'This opening’s budget is no longer one of this project’s active budgets in Recruitment.',
  unreachable: RECRUITMENT_DID_NOT_ANSWER,
  timed_out: RECRUITMENT_DID_NOT_ANSWER,
  // It answered: "did not answer, try again" would be untrue, and for a refusal useless.
  recruitment_error: RECRUITMENT_ANSWERED_WITH_ERROR,
  malformed: RECRUITMENT_ANSWER_UNREADABLE,
  // Phase 2. Neither is Recruitment failing to answer.
  not_listed_yet: 'This opening was raised a moment ago and is not in Recruitment’s list yet.',
  not_read: 'The numbers were not read with this answer. Refresh to read them.',
};

export const UNAVAILABLE_REASONS = Object.keys(REASON_SENTENCE) as HiringUnavailableReason[];

/** Why a link's numbers could not be read just now. */
export const reasonSentence = (reason: HiringUnavailableReason | null | undefined): string =>
  (reason && REASON_SENTENCE[reason]) || RECRUITMENT_DID_NOT_ANSWER;

/**
 * The lines under a card's numbers: why they could not be read (when they could not),
 * then where the figures shown came from. The reason leads, so the line about old
 * figures never says Recruitment "did not answer" when it was never asked. Old figures
 * are dated only where the date is theirs: see keptFiguresLine.
 */
export const readingLines = (link: HiringLink): string[] => {
  const when = link.seenAt ? formatHiringMoment(link.seenAt) : null;
  const lastRead = keptFiguresLine(link, 'numbers');
  if (link.state === 'closed' || isGone(link.status)) return link.numbers ? [lastRead] : [];
  if (link.status === 'numbers_unavailable') {
    const lines = [reasonSentence(link.unavailableReason)];
    lines.push(link.numbers ? lastRead : 'Recruitment has not been read for this opening yet.');
    if (link.lastKnownStatus) lines.push(`Last known: ${statusWords(link.lastKnownStatus, link.subject.kind, link.numbers)}`);
    return lines;
  }
  return link.numbers && link.numbersFrom === 'live' && when ? [`Read from Recruitment on ${when}`] : [];
};

/** What the status means, where the pill alone does not say it. */
export const statusSentence = (link: HiringLink): string | null => {
  if (link.state === 'closed') return null;
  switch (link.status) {
    case 'shortlisted_offered':
      return offerIsOut(link.numbers)
        ? 'An offer has been made on this opening.'
        : 'A candidate has been selected on this opening. No offer has been made yet.';
    case 'joined_not_placed':
      return 'Someone has joined on this opening since it was linked. ChannelPlex does not know who yet.';
    case 'no_open_position': return 'This opening has no open position now.';
    case 'not_listed': return DELETED_WORDS.notice;
    default: return null;
  }
};

/**
 * The reasons the API can hold on a link made by hand, in words. The Link hiring window
 * no longer asks for one (product owner, 14 October 2026): a hand link is now always
 * saved as `existing_opening`, which says nothing and is never drawn. A link saved
 * before then with another reason still shows the one it was saved with.
 */
export const LINK_REASON_WORDS: readonly { value: HiringLinkReason; label: string }[] = [
  { value: 'existing_opening', label: 'Existing opening' },
  { value: 'replacement', label: 'Replacement' },
  { value: 'new_store', label: 'New store' },
  { value: 'extra_headcount', label: 'Extra headcount' },
];

export const reasonLabel = (reason: string | null | undefined): string =>
  LINK_REASON_WORDS.find((o) => o.value === reason)?.label ?? 'Other';

/** What a hand link is saved with when nobody gave a reason: not worth a line. */
const SAYS_NOTHING: HiringLinkReason = 'existing_opening';

/**
 * "Linked by 910002 on 09 Mar 2031, 11:30 am", with " · Reason: Replacement · Replacing
 * 910001" after it only for an older hand link saved with a reason of its own.
 */
export const linkedByLine = (link: HiringLink): string => {
  // An opening ChannelPlex raised was never "linked" by anyone: it says who raised it,
  // with the reason in the words of the Raise hiring window.
  const raised = link.origin === 'raised';
  const parts = [`${raised ? 'Raised' : 'Linked'} by ${link.raisedBy} on ${formatHiringMoment(link.raisedAt)}`];
  if (raised) parts.push(`Reason: ${raiseReasonLabel(link.reason)}`);
  else if (link.reason !== SAYS_NOTHING) parts.push(`Reason: ${reasonLabel(link.reason)}`);
  if (link.replacesEmpCode) parts.push(`Replacing ${link.replacesEmpCode}`);
  return parts.join(' · ');
};

export const stoppedByLine = (link: HiringLink): string | null => {
  // No moment: a link this screen only knows to be stopped (someone else stopped it
  // first). Its timeline says who and when; this line would only say "on —".
  if (link.state !== 'closed' || !link.closedAt) return null;
  const when = formatHiringMoment(link.closedAt);
  // Cancelled: by the person who SENT the cancel, whoever pressed Check again when Recruitment's list confirmed it.
  if (link.closedAs === 'cancelled' && link.closedBy) return `Cancelled by ${link.closedBy} on ${when}`;
  return link.closedAs === 'released' && link.closedBy ? `Stopped by ${link.closedBy} on ${when}` : `Closed on ${when}`;
};

const addressText = (address: HiringAddressText | null | undefined): string | null => {
  const text = [address?.location, address?.cityName].map((part) => String(part ?? '').trim()).filter(Boolean).join(', ');
  return text || null;
};

/**
 * The two addresses of an "Address changed in Recruitment" box. Either can be missing
 * (an API that does not send them, a link made before addresses were kept): the box
 * then shows what it has. `sameText` = the words match, so only the map point moved.
 */
export const addressLines = (link: HiringLink): { linked: string | null; now: string | null; sameText: boolean } => {
  const linked = addressText(link.address?.linked);
  const now = addressText(link.address?.now);
  return { linked, now, sameText: !!linked && !!now && linked.toLowerCase() === now.toLowerCase() };
};

/* ─────────────────────────────── timeline ─────────────────────────────── */

export interface TimelineLine { title: string; details: string[]; note: string | null }

const textOf = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null);

/** One timeline entry in words. A kind these screens do not know yet is not guessed at. */
export const timelineLine = (event: HiringTimelineEvent): TimelineLine => timelineLineFor(event, 'store');

/**
 * The same, for a link whose subject is known: `subjectKind` is what the link is for, for
 * the one line that names it ("still this store" / "still this budget", for a hiring with no store).
 */
export const timelineLineFor = (event: HiringTimelineEvent, subjectKind: 'store' | 'role'): TimelineLine => {
  const detail = event.detail ?? {};
  const opening = event.openingId ?? (typeof detail.openingId === 'number' ? detail.openingId : null);
  const openingText = opening != null ? `opening ${opening}` : 'an opening';
  const note = textOf(detail.note);
  if (event.type === 'linked') {
    const budget = textOf(detail.budgetName);
    const reason = textOf(detail.reason);
    const replaces = textOf(detail.replacesEmpCode);
    const details: string[] = [];
    // As on the card: "Existing opening" is not written; an older link's own reason, and its note, still are.
    const said = [reason && reason !== SAYS_NOTHING ? `Reason: ${reasonLabel(reason)}` : null, replaces ? `Replacing ${replaces}` : null]
      .filter((part): part is string => !!part);
    if (said.length > 0) details.push(said.join(' · '));
    if (detail.shared === true) details.push('Linked as a shared opening');
    return { title: `Linked to ${openingText}${budget ? ` · ${budget}` : ''}`, details, note };
  }
  if (event.type === 'released') {
    // Stopped while a cancel of its opening was still open: said, so the next line (how that cancel ended) makes sense.
    return { title: `Stopped following ${openingText}`, details: detail.cancelUnresolved === true ? [RELEASED_WITH_CANCEL_OPEN] : [], note };
  }
  return recruitmentTimelineLine(event, subjectKind) ?? raiseTimelineLine(event) ?? requestTimelineLine(event) ?? cancelTimelineLine(event)
    ?? moveTimelineLine(event) ?? { title: 'Change recorded', details: [], note: null };
};

/* ─────────────────────────────── the Hiring tab ─────────────────────────────── */

const FROM_LAST_READ = 'are from the last read, where there is one.';

/** The amber line over the Hiring tab's table when some or all numbers could not be read. */
export const recruitmentBanner = (recruitment: HiringLinkList['recruitment'] | null | undefined): string | null => {
  if (!recruitment || recruitment.read === 'ok') return null;
  const all = recruitment.read === 'unavailable';
  const tail = all ? `Numbers shown ${FROM_LAST_READ}` : `Numbers shown for them ${FROM_LAST_READ}`;
  switch (recruitment.reason) {
    case 'budget_not_in_project':
      return `${all ? 'These' : 'Some'} openings’ budgets are no longer active in Recruitment. ${tail}`;
    case 'no_identity':
    case 'sign_in_failed':
      return `${reasonSentence(recruitment.reason)} ${tail}`;
    // Recruitment answered, so neither line says it "did not answer".
    case 'recruitment_error':
      return all
        ? `${RECRUITMENT_ANSWERED_WITH_ERROR} ${tail}`
        : `Recruitment answered with a refusal or an error for some openings. ${TELL_ADMINISTRATOR} ${tail}`;
    case 'malformed':
      return all
        ? `${RECRUITMENT_ANSWER_UNREADABLE} ${tail}`
        : `Recruitment’s answer could not be read for some openings. ${TELL_ADMINISTRATOR} ${tail}`;
    // Phase 2. Recruitment answered; an opening raised a moment ago is just not in its list yet.
    case 'not_listed_yet':
      return all
        ? 'These openings were raised a moment ago and are not in Recruitment’s list yet. Their numbers are from the last read.'
        : 'An opening raised a moment ago is not in Recruitment’s list yet. Its numbers are from the last read.';
    // The numbers are read by the list, not by the answer these links came with.
    case 'not_read':
      return `${all ? 'These' : 'Some'} openings’ numbers were not read just now. Refresh to read them.`;
    default:
      return all
        ? `${RECRUITMENT_DID_NOT_ANSWER} ${tail}`
        : `Recruitment did not answer for some openings. ${tail}`;
  }
};

/* ─────────────────────────────── picking an opening ─────────────────────────────── */

/** Used when an API does not say how many positions an opening of its own may have. */
export const DEFAULT_POSITIONS_PER_OPENING = 1;

export const positionsPerOpeningOf = (answer: { positionsPerOpening?: unknown } | null | undefined): number => {
  const n = answer?.positionsPerOpening;
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 ? n : DEFAULT_POSITIONS_PER_OPENING;
};

export interface PickState {
  pickable: boolean;
  /** The link would be a shared one (several stores, display only). */
  shared: boolean;
  tag: string | null;
  /** Kept out of the list unless asked for: nothing left to hire for on it. */
  noOpenPosition: boolean;
}

const NOT_REFUSED: ReadonlyMap<number, string> = new Map();
const NONE_FOLLOWED: ReadonlySet<number> = new Set();

const ALREADY_LINKED = 'Already linked';
export const MAY_BE_BEING_RAISED = 'May be the opening being raised';

/**
 * What a refused link says about its opening for as long as the window stays open: the
 * words its row then carries, or null when the refusal says nothing lasting about it.
 * These two are the refusals a fresh read of the openings cannot always show: a shared
 * opening looks the same after this store was told it already holds it, or that it is full.
 */
export const refusedOpeningTag = (code: string | undefined): string | null => {
  if (code === HIRING_CODES.alreadyLinked) return ALREADY_LINKED;
  if (code === HIRING_CODES.sharedFull) return 'Every position is already linked';
  return null;
};

/**
 * Whether an opening may be picked for a store or a role, and what to say when not.
 * Mirrors what the server refuses, so a person is not offered a row only to be told no:
 * an opening this store already follows; an opening already linked on its own; several
 * positions for a role; a shared opening with no second position to share; an opening of
 * its own with no position at all, or with none open and no offer.
 *
 * `refused` = openings the server has refused to link from this window, with the words
 * for each (see refusedOpeningTag). `followed` = the openings the store already follows,
 * when its owner knows them.
 */
export const pickState = (
  opening: HiringPickerOpening,
  subjectKind: 'store' | 'role',
  positionsPerOpening: number,
  refused: ReadonlyMap<number, string> = NOT_REFUSED,
  followed: ReadonlySet<number> = NONE_FOLLOWED,
): PickState => {
  const off = (tag: string, noOpenPosition = false): PickState => ({ pickable: false, shared: false, tag, noOpenPosition });
  // A shared opening is linked once per store: this store's own would be refused again.
  if (followed.has(opening.openingId)) return off('Already linked to this store');
  if (opening.linked === 'exclusive') return off(ALREADY_LINKED);
  // It appeared after a request under this budget was sent, and that request is still
  // open: it may be the opening being raised, and is linked by itself once confirmed.
  if (opening.linked === 'raising') return off(MAY_BE_BEING_RAISED);
  const told = refused.get(opening.openingId);
  if (told) return off(told);
  const several = opening.positions > positionsPerOpening || opening.linked === 'shared';
  if (several) {
    // Held as shared, and since reduced to one position: it cannot be shared again, and
    // it cannot be anyone's own while another store holds it.
    if (opening.positions < 2) return off(ALREADY_LINKED);
    if (subjectKind === 'role') return off('Has several positions. Only stores can be linked to it.');
    return { pickable: true, shared: true, tag: 'Shared opening', noOpenPosition: false };
  }
  // No position at all is refused even with an offer out on it.
  if (opening.positions < 1 || (opening.open < 1 && opening.offered === 0)) return off('No open position', true);
  return { pickable: true, shared: false, tag: null, noOpenPosition: false };
};

/** "Opening 48213 is selected: 5 Sample Road, Sample City." — what Link will link, in words. */
export const selectedOpeningLine = (opening: HiringPickerOpening, onShow: boolean): string => {
  const where = [opening.location, opening.cityName].map((part) => String(part ?? '').trim()).filter(Boolean).join(', ');
  const line = `Opening ${opening.openingId} is selected${where ? `: ${where}` : ''}.`;
  return onShow ? line : `${line} It is not in the rows shown above.`;
};

export const sharedLine = (opening: HiringPickerOpening): string =>
  `This opening has ${counted(opening.positions, 'position')}. It will be linked as a shared opening: `
  + 'several stores can share it, and its numbers are for the whole opening.';

/** Does an opening match what was typed: its number, city or address. */
export const openingMatches = (opening: HiringPickerOpening, typed: string): boolean => {
  const q = typed.trim().toLowerCase();
  if (!q) return true;
  return [String(opening.openingId), opening.cityName ?? '', opening.location ?? ''].some((v) => v.toLowerCase().includes(q));
};

/* ── one opening's row in "Link an opening"'s table (approved option A, 16 Oct 2026) ── */

/**
 * The words of one opening's row in "Link an opening": Address | City | State | Open |
 * Recruiters | Offer makers | Interviewers. Openings can share an address, a city and a State
 * and differ only in who recruits, makes offers and interviews on them, so the table shows
 * those side by side; the opening's number is not a column (the product owner, 16 Oct 2026).
 * It is on the row's hover, in the radio's name and in what is sent.
 */
export const OPENING_ROW_WORDS = {
  noAddress: 'No address in Recruitment',
  /** Recruitment's own Open figure, of the opening's positions: "2 of 4". */
  openOf: (open: number, positions: number): string => `${open} of ${positions}`,
  /** What Open means, on the (i) of the Open column (1Office counts it this way in its openings list). */
  openHover: 'Open = positions - joined - offered: positions with nobody joined and no offer out',
  /** A round, short, before its interviewer: "R1". Its hover says "Round 1". */
  round: (n: number): string => `R${n}`,
  roundHover: (n: number): string => `Round ${n}`,
  client: 'Client',
  /** For a place, or a role on the team, that is not known or names nobody. */
  nobody: '–',
  /** The row's hover starts with its number: the only place it is shown. */
  hover: (openingId: number): string => `Opening ${openingId}`,
  /** The radio's name for a screen reader: its number, then its address. */
  radioName: (openingId: number, address: string): string => `Opening ${openingId}, ${address}`,
} as const;

/** The address as Recruitment lists it, or "No address in Recruitment". */
export const openingAddress = (opening: Pick<HiringPickerOpening, 'location'>): string =>
  String(opening.location ?? '').trim() || OPENING_ROW_WORDS.noAddress;

/**
 * A City or State cell: the name, or "–" when it is not known (Recruitment's city list could not
 * be read, or does not hold the city, or the opening has none), so the reader sees it is missing.
 */
export const openingPlaceCell = (name: string | null | undefined): string => String(name ?? '').trim() || OPENING_ROW_WORDS.nobody;

/** The names in a Recruiters or Offer makers cell, one per line; none when the role names nobody (the cell reads "–"). */
export const openingNames = (people: ReadonlyArray<{ name: string }> | null | undefined): string[] =>
  (people ?? []).map((p) => String(p.name ?? '').trim()).filter(Boolean);

/** One line of the Interviewers cell: "R1" and the interviewer, or "R2" and "Client". */
export interface OpeningRoundLine { round: number; label: string; who: string }

/**
 * The Interviewers cell, one round per line in round order: "R1 Sample Interviewer A", "R2
 * Client", or "R2 Client (name)" when the server names the client (to a holder of "Raise &
 * cancel hiring" only: the server decides). None when no round names anyone (the cell reads "–").
 */
export const openingRounds = (team: HiringPickerOpening['team']): OpeningRoundLine[] =>
  (team?.rounds ?? []).map((round: HiringPickerRound) => {
    const label = OPENING_ROW_WORDS.round(round.round);
    const name = String(round.name ?? '').trim();
    if (round.kind === 'interviewer') return { round: round.round, label, who: name || OPENING_ROW_WORDS.nobody };
    return { round: round.round, label, who: name ? `${OPENING_ROW_WORDS.client} (${name})` : OPENING_ROW_WORDS.client };
  });

/** What a row's search looks in: the address, the city, the State and every name on its team - lower case. */
export const openingSearchText = (opening: Pick<HiringPickerOpening, 'location' | 'cityName' | 'stateName' | 'team'>): string => {
  const team = opening.team ?? null;
  return [
    opening.location, opening.cityName, opening.stateName,
    ...(team?.recruiters ?? []).map((p) => p.name),
    ...(team?.offerMakers ?? []).map((p) => p.name),
    ...(team?.rounds ?? []).map((r) => r.name),
  ].map((part) => String(part ?? '').trim().replace(/\s+/g, ' ')).filter(Boolean).join('\n').toLowerCase();
};

/* ─────────────────────────────── Manage Budgets, in 1Office Project-Management ─────────────────────────────── */
//
// The hiring screens show counts only. A budget's openings with their candidates by name,
// and adding positions, reducing positions and deleting an opening, are on the Manage
// Budgets page of 1Office Project-Management. ChannelPlex opens that page, already
// signed in, for a person who holds "Raise & cancel hiring". Since #715, when the link
// carries a budget (and an opening), 1Office opens it on that budget with the opening
// highlighted, if the person can see that budget there; without one (the dashboard's
// header) it opens the page on the project. So the hover says both plainly, and an
// opening's card still says what to look for, for the times it opens on the page.

export const BUDGETS_PAGE_WORDS = {
  /** The control itself. */
  open: 'Open Manage Budgets',
  /** Read out with the control's new-tab mark, for a person who cannot see the mark. */
  newTab: 'opens in a new tab',
  /** Shown on hover. */
  hover: 'Opens the Manage Budgets page of 1Office Project-Management in a new tab, on this budget when ChannelPlex knows it.',
} as const;

/**
 * What to look for on the Manage Budgets page, said beside the control on an opening's
 * card and on a request still being raised: the budget's name and, once there is one, the
 * opening number. Null when the budget's name is not known: that page lists budgets by
 * name, so an opening number alone gives the person nothing to find there.
 */
export const budgetsPageLookFor = (budgetName: string | null | undefined, openingId: number | null | undefined): string | null => {
  const name = String(budgetName ?? '').trim().replace(/\s+/g, ' ');
  const opening = typeof openingId === 'number' && Number.isFinite(openingId) ? `opening ${openingId}` : '';
  const what = [name, opening].filter(Boolean).join(', ');
  return name ? `Look for ${what}.` : null;
};

/* ═══════════════════════════════ Raise hiring (phase 2) ═══════════════════════════════ */
//
// ChannelPlex asks Recruitment to CREATE an opening for a store, or for a manager role.
// That is a real, live opening: Recruitment mails its recruiters and can alert
// candidates, and ChannelPlex cannot take it back. So what a person reads before they
// confirm, and after, is the server's own sentence wherever it wrote one (the review's
// lines, every refusal, every outcome). What follows are the screens' own words around
// those, and the small rules that decide what is offered next.

/** The refusal codes of a raise that the screens act on. Every other code only shows its sentence. */
export const RAISE_CODES = {
  raiseNotEnabled: 'RAISE_NOT_ENABLED',
  storeHasHiring: 'STORE_ALREADY_HAS_HIRING',
  roleHasHiring: 'ROLE_ALREADY_HAS_HIRING',
  openingExists: 'OPENING_ALREADY_EXISTS',
  staffingHold: 'STORE_STAFFING_HOLD',
  replacedNotAtStore: 'REPLACED_NOT_AT_STORE',
  cityNotMatched: 'CITY_NOT_MATCHED',
  cityMatchedSeveral: 'CITY_MATCHED_SEVERAL',
  cityNotFound: 'CITY_NOT_FOUND',
  storeFarFromCity: 'STORE_FAR_FROM_CITY',
  previewStale: 'PREVIEW_STALE',
  requestKeyReused: 'REQUEST_KEY_REUSED',
  budgetBusy: 'RAISE_IN_PROGRESS_FOR_BUDGET',
  /** The budget has no free position. Positions are added on the Manage Budgets page, so its link is offered beside this one. */
  noFreePosition: 'NO_FREE_POSITION',
  candidateExists: 'CANDIDATE_EXISTS',
  notACandidate: 'NOT_A_CANDIDATE',
  linkNotSending: 'LINK_NOT_SENDING',
  raiseInProgress: 'RAISE_IN_PROGRESS',
  /** The hand link's own refusal while a request under the opening's budget is unresolved. */
  openingBeingRaised: 'OPENING_BEING_RAISED',
} as const;

type RefusalWithDetail = { response?: { data?: { detail?: unknown } } };

/** What a refusal carries for the screen to act on, when it carries anything. */
export const hiringDetailOf = (err: unknown): Record<string, unknown> => {
  const detail = (err as RefusalWithDetail)?.response?.data?.detail;
  return detail && typeof detail === 'object' && !Array.isArray(detail) ? (detail as Record<string, unknown>) : {};
};

/** Raise was switched off, for the project or on the server, since the page asked. */
export const isRaiseOff = (err: unknown): boolean =>
  hiringStatusOf(err) === 404 && hiringCodeOf(err) === RAISE_CODES.raiseNotEnabled;

/** The refusal that the budget has no free position: the one refusal the Manage Budgets link is offered beside. */
export const isNoFreePosition = (err: unknown): boolean => hiringCodeOf(err) === RAISE_CODES.noFreePosition;

/**
 * The server writes a moment into a sentence as "2031-03-14 04:45 UTC". On screen it
 * reads in India time, like every other moment on these screens. A sentence with no
 * such moment is returned as it came.
 */
export const localiseRaiseMessage = (message: string | null | undefined): string =>
  String(message ?? '').replace(/(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}) UTC/g, (_all, day: string, time: string) =>
    formatHiringMoment(`${day}T${time}:00.000Z`));

/**
 * What a refused raise call says: the server's own sentence, with its moments in India
 * time. Not the phase 1 wording for "Recruitment did not answer": a raise's refusals say
 * what was being checked and whether anything was sent, and that must not be lost.
 */
export const raiseErrorText = (err: unknown, fallback: string): string => {
  const message = (err as Refusal)?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? localiseRaiseMessage(message) : fallback;
};

/* ── the form ── */

/** Why hiring is raised. No default: the person says which. Every reason the server takes, with its label. */
export const RAISE_REASON_OPTIONS: readonly { value: HiringRaiseReason; label: string }[] = [
  { value: 'replacement', label: 'Replacing someone who left' },
  { value: 'new_store', label: 'New store' },
  { value: 'extra_headcount', label: 'One more person' },
];

/**
 * The reason cards offered, for a store and for a manager role alike: "Replacing someone
 * who left" and "One more person" (product owner, 14 Oct 2026). "New store" is never a
 * card: it is offered only where a store was just added, as the window's `fixedReason`.
 */
export const raiseReasonOptions = (_subjectKind: 'store' | 'role') =>
  RAISE_REASON_OPTIONS.filter((option) => option.value !== 'new_store');

/** What a reason card says on hover. */
export const raiseReasonHover = (reason: HiringRaiseReason, subjectKind: 'store' | 'role'): string | null => {
  if (reason === 'replacement') return subjectKind === 'store' ? 'Someone at this store left or is leaving.' : 'Someone left or is leaving, and the opening has no store.';
  if (reason === 'extra_headcount') return subjectKind === 'store' ? 'One more person, apart from the people deployed here.' : 'One more person under the budget, with no store.';
  return null;
};

export const raiseReasonLabel = (reason: string | null | undefined): string =>
  RAISE_REASON_OPTIONS.find((o) => o.value === reason)?.label ?? 'Other';

/** Where a manager role's address comes from, in the order the server tries them. */
export const ROLE_ADDRESS_OPTIONS: readonly { value: HiringRoleAddressSource; label: string }[] = [
  { value: 'leaver_opening', label: 'The address of the opening the person was hired on' },
  { value: 'budget_default', label: 'The budget’s default address in Recruitment' },
  { value: 'typed', label: 'Type it' },
];

/**
 * The address sources offered. The opening the person was hired on only for a replacement
 * whose card says 1Office lists that opening (`leaverOpening`): someone resigned with the
 * exit not started, or on notice by the employee list alone, has none to offer, and the
 * review would refuse it.
 */
export const roleAddressOptions = (reason: HiringRaiseReason | '', person?: { leaverOpening?: boolean } | null) =>
  ROLE_ADDRESS_OPTIONS.filter((option) => option.value !== 'leaver_opening' || (reason === 'replacement' && person?.leaverOpening === true));

/** The source chosen to begin with: the first of those offered that the server would try. */
export const defaultRoleAddressSource = (reason: HiringRaiseReason | '', person?: { leaverOpening?: boolean } | null): HiringRoleAddressSource =>
  (reason === 'replacement' && person?.leaverOpening === true ? 'leaver_opening' : 'budget_default');

export const TYPED_ADDRESS_MIN = 5;
export const TYPED_ADDRESS_MAX = 300;
export const TYPED_ADDRESS_HAS_NUMBER =
  'The address cannot hold a number of seven or more digits. Recruiters and candidates see this address, so leave a phone number out of it.';
export const RAISE_ROLE_NAME_MAX = 150;
const PINCODE = /^[1-9]\d{5}$/;

/** A map point, as Google's place details give it: decimal degrees, as text. */
export interface HiringMapPoint { lat: string; lng: string }

/** A point both of whose halves are decimal degrees in range; null otherwise. */
export const mapPointOf = (point: { lat?: unknown; lng?: unknown } | null | undefined): HiringMapPoint | null => {
  if (!point) return null;
  const lat = String(point.lat ?? '').trim();
  const lng = String(point.lng ?? '').trim();
  const number = /^-?\d{1,3}(\.\d+)?$/;
  if (!number.test(lat) || !number.test(lng)) return null;
  if (Math.abs(Number(lat)) > 90 || Math.abs(Number(lng)) > 180) return null;
  return { lat, lng };
};

/** A 6-digit PIN code from place details, or null. */
export const placePincode = (pincode: unknown): string | null => {
  const text = String(pincode ?? '').trim();
  return PINCODE.test(text) ? text : null;
};

/**
 * The names in a picked address that may be its city, most likely first: Google's locality
 * and district where the answer carries them, then the parts of the formatted address from
 * its end ("…, Indiranagar, Bengaluru, Karnataka 560038, India" → Bengaluru, Indiranagar),
 * without the country and without the "State PIN" part. At most three.
 */
export const addressCityCandidates = (
  formatted: string,
  named: { locality?: unknown; district?: unknown } = {},
): string[] => {
  const clean = (part: unknown) => String(part ?? '').replace(/\d+/g, ' ').replace(/\s+/g, ' ').trim();
  const parts = formatted.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length > 0 && /^india$/i.test(parts[parts.length - 1])) parts.pop();
  // "Karnataka 560038": the state, with the PIN code. Without a PIN the last part is still the state.
  if (parts.length > 1) parts.pop();
  const fromText = parts.reverse().map(clean);
  const out: string[] = [];
  for (const name of [clean(named.locality), clean(named.district), ...fromText]) {
    if (name.length < 2 || out.some((seen) => seen.toLowerCase() === name.toLowerCase())) continue;
    out.push(name);
    if (out.length === 3) break;
  }
  return out;
};

/** India's states and union territories, as an address ends with them (a few older spellings too). */
const INDIA_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh',
  'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Orissa', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand',
  'Uttaranchal', 'West Bengal', 'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Dadra and Nagar Haveli', 'Daman and Diu', 'Delhi', 'NCT of Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep',
  'Puducherry', 'Pondicherry',
];
const placeKey = (text: unknown): string => String(text ?? '').replace(/&/g, ' and ').replace(/\s+/g, ' ').trim().toLowerCase();
const INDIA_STATE_KEYS = new Set(INDIA_STATES.map(placeKey));

/**
 * The street part of a picked address, for the Address box (15 Oct 2026): Google's whole
 * address ends with the locality, the state, the PIN code and "India", which the window
 * already holds as its City and PIN - kept, the review read "…, Bengaluru, Karnataka 560034,
 * India · Bengaluru · 560034" and that long text was sent as the address. Only trailing
 * parts are dropped, each one that is "India", a 6-digit PIN code (alone or after the state's
 * name), a state of India, or one of the names given (the city picked, its state, and the
 * place's locality and district where the answer carries them). The first part always stays.
 */
export const streetOfPickedAddress = (
  formatted: string,
  known: { city?: unknown; state?: unknown; locality?: unknown; district?: unknown } = {},
): string => {
  const names = new Set([known.city, known.state, known.locality, known.district].map(placeKey).filter(Boolean));
  const parts = String(formatted ?? '').split(',').map((part) => part.replace(/\s+/g, ' ').trim()).filter(Boolean);
  const dropped = (part: string): boolean => {
    const key = placeKey(part);
    return key === 'india' || /(^|\s)[1-9]\d{5}$/.test(key) || INDIA_STATE_KEYS.has(key) || names.has(key);
  };
  while (parts.length > 1 && dropped(parts[parts.length - 1])) parts.pop();
  return parts.join(', ');
};

/**
 * The one city of Recruitment's list whose name is exactly `name` (letter case and spaces
 * aside). Two of the same name: the one whose state is in the address, else none - the
 * person picks.
 */
export const exactCityOf = <C extends { cityName: string; stateName?: string | null }>(cities: C[], name: string, address: string): C | null => {
  const key = (text: unknown) => String(text ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  const same = cities.filter((city) => key(city.cityName) === key(name));
  if (same.length === 1) return same[0];
  const inAddress = same.filter((city) => key(city.stateName) && key(address).includes(key(city.stateName)));
  return inAddress.length === 1 ? inAddress[0] : null;
};

/** What is still missing from a typed address, in words; null when it can be reviewed. */
export const typedAddressProblem = (typed: { cityId: number | null; address: string; pincode: string }): string | null => {
  if (typed.cityId == null) return 'Pick the city from Recruitment’s list.';
  const address = typed.address.trim().replace(/\s+/g, ' ');
  if (address.length < TYPED_ADDRESS_MIN || address.length > TYPED_ADDRESS_MAX) {
    return `Type the address (${TYPED_ADDRESS_MIN} to ${TYPED_ADDRESS_MAX} characters).`;
  }
  // The server refuses it too: recruiters and candidates see this address as it is typed.
  if (/\d{7,}/.test(address)) return TYPED_ADDRESS_HAS_NUMBER;
  if (!PINCODE.test(typed.pincode.trim())) return 'Type the 6-digit PIN code.';
  return null;
};

/* ── "Who is being replaced?": the people, as cards ── */
//
// The server sends the cards in the window's order (serving notice, soonest last day
// first; resigned; left, most recent first; not resigned, the store's primary person
// first; the greyed ones last), with one tag each and the card to open selected. The
// screen draws them as they come. People are employees, never candidates: a name is the
// one ChannelPlex holds, or 1Office's employee list's, and it is shown, never kept.

/** How a tag is drawn: amber for a notice, grey for someone gone, outlined for someone working, darker grey for a greyed card. */
export type ReplaceTagTone = 'notice' | 'left' | 'working' | 'blocked';

export interface ReplaceTagWords {
  text: string;
  tone: ReplaceTagTone;
  /** On the tag of a card that can be picked, said on hover. */
  tagHover: string | null;
  /** On a greyed card: why it cannot be picked, said on hover of the whole card. */
  cardHover: string | null;
}

/** The one tag of a card, in words. A tag this screen does not know reads as "Not checked": nothing is claimed about a notice. */
export const replaceTagWords = (card: Pick<HiringReplaceCard, 'tag' | 'day' | 'openingId' | 'requestedBy'>, subjectKind: 'store' | 'role' = 'store'): ReplaceTagWords => {
  const day = card.day ? formatHiringDay(card.day) : null;
  switch (card.tag) {
    case 'serving_notice':
      return { text: day ? `Serving notice · last day ${day}` : 'Serving notice', tone: 'notice', tagHover: null, cardHover: null };
    case 'resigned':
      return {
        text: 'Resigned · exit not started', tone: 'notice', cardHover: null,
        tagHover: 'A resignation is on file in 1Office. HR has not processed the exit, and the list gives no date.',
      };
    case 'left':
      return {
        text: day ? `Left · ${day}` : 'Left', tone: 'left', cardHover: null,
        tagHover: subjectKind === 'store' ? 'Their last working day, or the day they left this store.' : 'Their last working day.',
      };
    case 'not_resigned':
      return { text: 'Not resigned', tone: 'working', tagHover: '1Office lists no resignation or exit for them.', cardHover: null };
    case 'hiring_raised':
      return {
        text: 'Hiring already raised', tone: 'blocked', tagHover: null,
        cardHover: card.openingId != null ? `Opening ${card.openingId} already replaces them.` : 'Hiring to replace them is already being raised.',
      };
    case 'waiting':
      return {
        text: 'Waiting for confirmation', tone: 'blocked', tagHover: null,
        cardHover: card.requestedBy
          ? `${card.requestedBy} asked for their replacement. It is waiting for confirmation.`
          : 'A hiring request to replace them is waiting for confirmation.',
      };
    case 'already_replaced':
      return { text: 'Already replaced in 1Office', tone: 'blocked', tagHover: null, cardHover: 'Recruitment already marks them as replaced.' };
    default:
      return { text: 'Not checked', tone: 'working', tagHover: '1Office could not be read for them, so a notice may not show.', cardHover: null };
  }
};

/** The card's first line: the name, or the employee code when no name is known. */
export const replaceCardTitle = (card: Pick<HiringReplaceCard, 'empCode' | 'name'>): string =>
  (card.name && card.name.trim() ? card.name.trim() : card.empCode);

/** The card's second line: "910103 · Promoter" (the code is left out when it is already the first line). */
export const replaceCardLine = (card: Pick<HiringReplaceCard, 'empCode' | 'name' | 'designation'>): string =>
  [card.name && card.name.trim() ? card.empCode : null, card.designation && card.designation.trim() ? card.designation.trim() : null]
    .filter(Boolean).join(' · ');

/** What the card is called to a screen reader, and in a test: "Sample Person C (910103)", or the code alone. */
export const replaceCardName = (card: Pick<HiringReplaceCard, 'empCode' | 'name'>): string =>
  (card.name && card.name.trim() ? `${card.name.trim()} (${card.empCode})` : card.empCode);

/** The line under the cards when 1Office could not be read for everyone; null when it was. */
export const replaceReadLine = (answer: Pick<HiringReplacePeople, 'oneOffice' | 'notChecked'> | null | undefined): string | null => {
  if (!answer || answer.oneOffice === 'read') return null;
  const n = typeof answer.notChecked === 'number' ? answer.notChecked : 0;
  return n > 0
    ? `1Office could not be read for ${n === 1 ? '1 person' : `${n} people`}, so a notice may not show.`
    : '1Office could not be read just now, so a notice may not show.';
};

/* ── the budget, as cards ── */

/** A budget with no free position: drawn greyed as "Full", and not picked. */
export const budgetIsFull = (budget: Pick<HiringRaiseBudget, 'free'>): boolean => !(budget.free > 0);

/** Above this many budgets, a search box is drawn above the cards. */
export const BUDGET_SEARCH_AFTER = 6;

/**
 * The budget cards in order: the person being replaced's own budget first, when it is
 * offered; the rest as the server sent them.
 */
export const orderBudgets = (budgets: readonly HiringRaiseBudget[], ownBudgetId: number | null | undefined): HiringRaiseBudget[] => {
  const own = ownBudgetId == null ? undefined : budgets.find((b) => b.budgetId === ownBudgetId);
  return own ? [own, ...budgets.filter((b) => b !== own)] : [...budgets];
};

/** Whether a budget's name holds what was typed in the search box. */
export const budgetMatches = (budget: Pick<HiringRaiseBudget, 'budgetName'>, typed: string): boolean => {
  const q = typed.trim().toLowerCase();
  return !q || String(budget.budgetName ?? '').toLowerCase().includes(q);
};

/* ── the Recruitment team ── */
//
// The person picks the recruiters, offer makers and one interviewer per round; the section
// opens with the budget's saved defaults (the team route's `prefill`). The server checks it
// again at the review and at the confirm, and is the judge; what follows only says what is
// still missing, in the words 1Office Project-Management's own window uses.

/** One round as the section holds it: a member of staff (or nobody yet), or the client's contact as typed. */
export type TeamDraftRound =
  | { kind: 'staff'; userId: number | null }
  | { kind: 'client'; name: string; email: string; mobile: string };

export interface TeamDraft {
  recruiters: number[];
  offerMakers: number[];
  rounds: TeamDraftRound[];
}

/** The section as it opens: the budget's defaults, less anyone the lists no longer hold; a client round empty. */
export const teamDraftOf = (options: Pick<HiringRaiseTeamOptions, 'recruiters' | 'offerMakers' | 'rounds' | 'prefill'>): TeamDraft => {
  const listed = (ids: readonly number[] | undefined, people: readonly HiringStaff[]) =>
    (ids ?? []).filter((id, i, all) => all.indexOf(id) === i && people.some((p) => p.userId === id));
  const prefillRounds = options.prefill?.rounds ?? [];
  return {
    recruiters: listed(options.prefill?.recruiters, options.recruiters ?? []),
    offerMakers: listed(options.prefill?.offerMakers, options.offerMakers ?? []),
    rounds: (options.rounds ?? []).map((round, i): TeamDraftRound => {
      if (round.kind === 'client') return { kind: 'client', name: '', email: '', mobile: '' };
      const saved = prefillRounds[i];
      const userId = saved && 'userId' in saved && round.interviewers.some((p) => p.userId === saved.userId) ? saved.userId : null;
      return { kind: 'staff', userId };
    }),
  };
};

/** The team as it is sent: the same shape the server's `prefill` has. Recruiters go as the schedulers too. */
export const teamOfDraft = (draft: TeamDraft): HiringRaiseTeam => ({
  recruiters: [...draft.recruiters],
  offerMakers: [...draft.offerMakers],
  rounds: draft.rounds.map((round) => {
    if (round.kind === 'staff') return round.userId != null ? { userId: round.userId } : null;
    const mobile = round.mobile.replace(/\D/g, '');
    return { client: { name: round.name.trim().replace(/\s+/g, ' '), email: round.email.trim(), mobile: mobile || null } };
  }),
});

/** A client's name and e-mail fit in Recruitment's columns of this many characters. */
export const CLIENT_TEXT_MAX = 100;
/** The domains a client's e-mail may not be on (1Office Project-Management's own rule). */
const PUBLIC_MAIL = ['gmail', 'yahoo', 'hotmail', 'outlook', 'aol', 'icloud', 'channelplay'];
const CLIENT_EMAIL = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/;

/** What is wrong with a client's typed contact, in the server's words; null when it can be reviewed. */
export const clientContactProblem = (client: { name: string; email: string; mobile: string }): string | null => {
  const name = client.name.trim().replace(/\s+/g, ' ');
  if (!name) return 'Type the client’s name.';
  if (name.length > CLIENT_TEXT_MAX) return `The client’s name can be up to ${CLIENT_TEXT_MAX} characters.`;
  if (/\d{7,}/.test(name)) return 'The client’s name cannot hold a number of seven or more digits.';
  const email = client.email.trim();
  if (!email) return 'Type the client’s email.';
  if (email.length > CLIENT_TEXT_MAX) return `The client’s email can be up to ${CLIENT_TEXT_MAX} characters.`;
  if (!CLIENT_EMAIL.test(email) || /[^\x21-\x7e]/.test(email)) return 'That email is not valid.';
  const domain = email.slice(email.lastIndexOf('@') + 1).toLowerCase();
  if (PUBLIC_MAIL.some((word) => domain.includes(word))) {
    return 'Use the client’s company email, not gmail, yahoo, hotmail, outlook, aol, icloud or channelplay.';
  }
  const mobile = client.mobile.replace(/\D/g, '');
  if (mobile && mobile.length !== 10) return 'Mobile must be 10 digits.';
  return null;
};

/** What the team still needs before it can be reviewed, as one sentence; null when it is complete. */
export const teamProblem = (draft: TeamDraft | null): string | null => {
  if (!draft) return null;
  if (draft.recruiters.length === 0) return 'Pick at least one recruiter.';
  if (draft.offerMakers.length === 0) return 'Pick at least one offer maker.';
  for (let i = 0; i < draft.rounds.length; i += 1) {
    const round = draft.rounds[i];
    if (round.kind === 'staff') {
      if (round.userId == null) return `Pick the round ${i + 1} interviewer.`;
    } else {
      const problem = clientContactProblem(round);
      if (problem) return `Round ${i + 1}: ${problem}`;
    }
  }
  return null;
};

/** "Sample Recruiter A (910021)": a member of Recruitment's staff, as the section and the review name them. */
export const staffLabel = (person: Pick<HiringStaff, 'name' | 'empCode'>): string => {
  const name = String(person.name ?? '').trim();
  const code = String(person.empCode ?? '').trim();
  return name && code ? `${name} (${code})` : name || code;
};

/** Whether a member of staff matches what was typed, by name or by employee code. */
export const staffMatches = (person: Pick<HiringStaff, 'name' | 'empCode'>, typed: string): boolean => {
  const q = typed.trim().toLowerCase();
  if (!q) return true;
  return String(person.name ?? '').toLowerCase().includes(q) || String(person.empCode ?? '').toLowerCase().includes(q);
};

/** The people a refusal says no longer hold their role: marked on their cards until they are removed or the team is read again. */
export interface TeamNotAllowed { recruiters: number[]; offerMakers: number[]; interviewers: Array<{ round: number; userId: number }> }
export const NOBODY_NOT_ALLOWED: TeamNotAllowed = { recruiters: [], offerMakers: [], interviewers: [] };

/** The refusal codes of a raise that are about the team the person picked: answered in the Recruitment team section. */
export const TEAM_CODES = {
  teamIncomplete: 'TEAM_INCOMPLETE',
  personNotAllowed: 'TEAM_PERSON_NOT_ALLOWED',
  roundsChanged: 'ROUNDS_CHANGED',
  clientContactInvalid: 'CLIENT_CONTACT_INVALID',
} as const;

/** A refusal the Recruitment team section answers: someone to pick, remove or type again, or the rounds to read again. */
export const isTeamRefusal = (err: unknown): boolean =>
  (Object.values(TEAM_CODES) as string[]).includes(hiringCodeOf(err) ?? '');

/** The people a TEAM_PERSON_NOT_ALLOWED refusal names; nobody for any other refusal. */
export const teamNotAllowedOf = (err: unknown): TeamNotAllowed => {
  if (hiringCodeOf(err) !== TEAM_CODES.personNotAllowed) return NOBODY_NOT_ALLOWED;
  const detail = hiringDetailOf(err);
  const ids = (list: unknown): number[] => (Array.isArray(list) ? list.filter((id): id is number => Number.isInteger(id)) : []);
  const interviewers = Array.isArray(detail.interviewers)
    ? detail.interviewers.filter((one): one is { round: number; userId: number } =>
      !!one && typeof one === 'object' && Number.isInteger((one as { round?: unknown }).round) && Number.isInteger((one as { userId?: unknown }).userId))
    : [];
  return { recruiters: ids(detail.recruiters), offerMakers: ids(detail.offerMakers), interviewers };
};

/** The rows of the review's Recruitment team block: "Recruiters: Sample Recruiter A (910021), …", one per role and round. */
export const reviewTeamRows = (team: HiringReviewTeam): Array<{ label: string; value: string }> => {
  const rows: Array<{ label: string; value: string }> = [];
  const recruiters = team.recruiters ?? [];
  const offerMakers = team.offerMakers ?? [];
  if (recruiters.length > 0) rows.push({ label: recruiters.length === 1 ? 'Recruiter' : 'Recruiters', value: recruiters.map(staffLabel).join(', ') });
  if (offerMakers.length > 0) rows.push({ label: offerMakers.length === 1 ? 'Offer maker' : 'Offer makers', value: offerMakers.map(staffLabel).join(', ') });
  for (const round of team.rounds ?? []) {
    rows.push({ label: `Round ${round.round}`, value: round.kind === 'client' ? `${round.name} (client)` : staffLabel(round) });
  }
  return rows;
};

export const raiseCityLabel = (city: Pick<HiringRaiseCity, 'cityName' | 'stateName'>): string =>
  [city.cityName, city.stateName].map((part) => String(part ?? '').trim()).filter(Boolean).join(', ');

/**
 * The ticks a refusal can ask for. Each label is the one the server's own sentence names
 * ("tick …"), word for word, so a person told to tick it finds it.
 */
export const RAISE_TICKS = {
  // A hiring with no store: the words of the server's own sentence that names this tick (hiring-link-raise.logic openingAlreadyExists).
  additional: (subjectKind: 'store' | 'role'): string => (subjectKind === 'store' ? 'This store needs one more person' : 'Raise one more with no store'),
  status: (staffingStatus: string): string => `Raise although the store is marked ${staffingStatus}`,
  noRecord: (empCode: string): string => `ChannelPlex has no record of ${empCode} at this store - raise anyway`,
  // A straight apostrophe, as in the server's sentence that names this tick.
  noneOfThese: 'None of these is this request\'s opening',
} as const;

/** What a refused review asked the form to show. Once shown, it stays for as long as the window is open. */
export interface RaiseFormNeeds {
  /** The "needs one more person" tick. */
  additional: boolean;
  /** "Raise although the store is marked …", for this staffing status. */
  staffingStatus: string | null;
  /** "ChannelPlex has no record of … at this store - raise anyway", for this employee code. */
  noRecordFor: string | null;
  /** The Recruitment city picker (a store whose own city did not match one, or is far from it). */
  cityPicker: boolean;
}

export const NO_RAISE_NEEDS: RaiseFormNeeds = { additional: false, staffingStatus: null, noRecordFor: null, cityPicker: false };

const CITY_PICKER_CODES: readonly string[] = [
  RAISE_CODES.cityNotMatched, RAISE_CODES.cityMatchedSeveral, RAISE_CODES.cityNotFound, RAISE_CODES.storeFarFromCity,
];
const ONE_MORE_CODES: readonly string[] = [RAISE_CODES.storeHasHiring, RAISE_CODES.roleHasHiring, RAISE_CODES.openingExists];

/**
 * What the form must show after a refusal, added to what it already shows. A tick is
 * offered only when the server's sentence asks for it: where the server says a tick will
 * not get past the refusal, none is drawn.
 */
export const raiseNeedsAfter = (
  needs: RaiseFormNeeds,
  err: unknown,
  form: { subjectKind: 'store' | 'role'; replacesEmpCode: string },
): RaiseFormNeeds => {
  const code = hiringCodeOf(err);
  if (!code) return needs;
  const message = String((err as Refusal)?.response?.data?.message ?? '');
  const next = { ...needs };
  if (ONE_MORE_CODES.includes(code)) next.additional = true;
  if (code === RAISE_CODES.staffingHold) {
    const status = hiringDetailOf(err).staffingStatus;
    if (typeof status === 'string' && status.trim()) next.staffingStatus = status.trim();
  }
  if (code === RAISE_CODES.replacedNotAtStore && form.replacesEmpCode && message.includes(RAISE_TICKS.noRecord(form.replacesEmpCode))) {
    next.noRecordFor = form.replacesEmpCode;
  }
  if (form.subjectKind === 'store' && CITY_PICKER_CODES.includes(code)) next.cityPicker = true;
  return next;
};

const openingsList = (ids: readonly number[]): string =>
  (ids.length === 1 ? `Opening ${ids[0]}` : `Openings ${listOf(ids.map(String))}`);

/**
 * What the review says, beside the server's own lines, about hiring that is already
 * there: the links the store or role has open, and openings Recruitment lists unlinked
 * with its name on them. Both are in the review's answer, and both are what "one more
 * person" was ticked beside. A link on the opening the person being replaced was hired
 * on is the seat being replaced, not hiring in progress: the server's own line says so,
 * and it is left out here.
 */
export const existingHiringLines = (
  // Read defensively: a review that lacks either list says nothing about it, never something false.
  preview: {
    existingHiring?: Partial<HiringRaisePreview['existingHiring'][number]>[] | null;
    existingUnlinked?: HiringRaisePreview['existingUnlinked'] | null;
  },
  subjectKind: 'store' | 'role',
): string[] => {
  const lines: string[] = [];
  const inProgress = (preview.existingHiring ?? []).filter((link) => link.seatBeingReplaced !== true);
  if (inProgress.length > 0) {
    const numbers = inProgress.map((link) => link.openingId).filter((id): id is number => typeof id === 'number');
    const where = numbers.length === inProgress.length ? ` on ${openingsList(numbers)}` : '';
    lines.push(`This ${subjectKind} already has hiring in progress${where}. This request is for one more person.`);
  }
  const unlinked = (preview.existingUnlinked ?? []).map((opening) => opening.openingId).filter((id) => typeof id === 'number');
  if (unlinked.length > 0) {
    const carrying = subjectKind === 'store' ? 'this store’s name' : 'this budget and address';
    lines.push(`Recruitment already lists ${openingsList(unlinked)} with ${carrying} on it, not linked here. This request creates one more opening.`);
  }
  return lines;
};

/**
 * What the Raise hiring window does when a review or a confirm is refused - for EVERY
 * refusal code the API has. The list is closed: hiringText.contract.test.ts holds it to
 * the contract file's own list of codes, so a code the server adds has no step here
 * until someone decides one.
 *
 *   window_closes  hiring, or raising, is no longer on here: the window closes.
 *   form           answered on the form - a tick, a city, another person, reason or
 *                  address - or by correcting the store first. A refused confirm goes back
 *                  to the form: what was reviewed is not what would be sent.
 *   review_again   what was reviewed no longer holds. Back to the form; the next review
 *                  gets a key of its own, and the old key is never sent with another review.
 *   wait           a request that is already out is in the way. Nothing to change here;
 *                  the owner's list is read again so the page shows that request.
 *   same_request   nothing on the form answers it: the budget, Recruitment or the person's
 *                  own access has to change first. The sentence is shown where the person
 *                  is, and the only thing a confirm offers repeats the SAME request, with
 *                  the same key - which the server sends to Recruitment at most once.
 *
 * In every one of these the refused call sent nothing to Recruitment. A code that is not
 * in the table reads as same_request: never a new request, and never a new key.
 */
export type RaiseRefusalStep = 'window_closes' | 'form' | 'review_again' | 'wait' | 'same_request';

const RAISE_REFUSAL_STEPS: Record<string, RaiseRefusalStep> = {
  // Switched off, for the project or on the server.
  HIRING_NOT_ENABLED: 'window_closes',
  RAISE_NOT_ENABLED: 'window_closes',

  // What was typed; the store or role itself; a tick the sentence names; a city; an address; who is replaced.
  BAD_REQUEST: 'form',
  STORE_NOT_ACTIVE: 'form',
  STORE_MARKED_REDEPLOY: 'form',
  STORE_STAFFING_HOLD: 'form',
  STORE_ADDRESS_INCOMPLETE: 'form',
  ADDRESS_HAS_BAD_CHARACTER: 'form',
  ADDRESS_HAS_LONG_NUMBER: 'form',
  STORE_ALREADY_HAS_HIRING: 'form',
  ROLE_ALREADY_HAS_HIRING: 'form',
  OPENING_ALREADY_EXISTS: 'form',
  REPLACED_NOT_AT_STORE: 'form',
  REPLACEMENT_ALREADY_RAISED: 'form',
  // Someone who is leaving or has left is picked instead, or "One more person" is chosen.
  REPLACED_NOT_LEAVING: 'form',
  // The Recruitment team section: someone to pick, remove or replace, a client's contact to
  // type again, or (ROUNDS_CHANGED) the budget's rounds, read again into the section.
  TEAM_INCOMPLETE: 'form',
  TEAM_PERSON_NOT_ALLOWED: 'form',
  ROUNDS_CHANGED: 'form',
  CLIENT_CONTACT_INVALID: 'form',
  CITY_NOT_MATCHED: 'form',
  CITY_MATCHED_SEVERAL: 'form',
  CITY_NOT_FOUND: 'form',
  CITY_HAS_NO_MAP_LOCATION: 'form',
  STORE_FAR_FROM_CITY: 'form',
  ROLE_ADDRESS_MISSING: 'form',

  // The review no longer holds.
  PREVIEW_STALE: 'review_again',
  REQUEST_KEY_REUSED: 'review_again',

  // A request is already out for this store or role, this person, or this budget.
  RAISE_IN_PROGRESS: 'wait',
  RAISE_IN_PROGRESS_FOR_BUDGET: 'wait',
  REPLACEMENT_ALREADY_HAS_HIRING: 'wait',

  // The budget, Recruitment, or the person's own access.
  STORE_NOT_IN_PROJECT: 'same_request',
  ROLE_NOT_IN_PROJECT: 'same_request',
  BUDGET_NOT_IN_PROJECT: 'same_request',
  BUDGET_NOT_ON_PROJECT_PAGE: 'same_request',
  BUDGET_NOT_STORE_LEVEL: 'same_request',
  BUDGET_IS_STORE_LEVEL: 'same_request',
  BUDGET_NOT_ACTIVE: 'same_request',
  NO_FREE_POSITION: 'same_request',
  // The budget's interview grades must be corrected in Recruitment.
  NO_INTERVIEWERS_FOR_BUDGET: 'same_request',
  // A store outside the person's reach: nothing on the form makes it theirs.
  STORE_NOT_YOURS: 'same_request',
  // No longer answered (14 Oct 2026); kept so an older answer still has its step.
  BUDGET_HAS_CLIENT_ROUND: 'same_request',
  NO_DEFAULT_TEAM: 'same_request',
  DEFAULT_TEAM_INCOMPLETE: 'same_request',
  EXIT_NOT_PROCESSED: 'same_request',
  NO_RECRUITMENT_IDENTITY: 'same_request',
  HIRING_UNVERIFIED: 'same_request',
  HIRING_CHECK_FAILED: 'same_request',
  HIRING_NO_ACCESS: 'same_request',
  RECRUITMENT_UNAVAILABLE: 'same_request',
  RECRUITMENT_SIGN_IN_FAILED: 'same_request',
  RECRUITMENT_FORBIDDEN: 'same_request',
  RECRUITMENT_BAD_ANSWER: 'same_request',
  RECRUITMENT_REFUSED: 'same_request',
  HIRING_ERROR: 'same_request',

  // Not answers of a review or a confirm (the hand link, the switches, check and settle). Listed so the list is whole.
  OPENING_NOT_IN_BUDGET: 'same_request',
  OPENING_HAS_SEVERAL_POSITIONS: 'same_request',
  SHARED_NOT_ALLOWED: 'same_request',
  OPENING_HAS_NO_OPEN_POSITION: 'same_request',
  OPENING_ALREADY_LINKED: 'same_request',
  SHARED_OPENING_FULL: 'same_request',
  OPENING_BEING_RAISED: 'same_request',
  LINK_NOT_FOUND: 'same_request',
  LINK_NOT_LIVE: 'same_request',
  // Answers to a question a message from Recruitment opened on a link: never sent by a review or a confirm.
  ATTENTION_NOT_OPEN: 'same_request',
  ATTENTION_CHANGED: 'same_request',
  NO_POSITION_LEFT: 'same_request',
  LINK_NOT_SENDING: 'same_request',
  LINK_NOT_RAISED: 'same_request',
  NOT_A_CANDIDATE: 'same_request',
  TOO_EARLY: 'same_request',
  CANDIDATE_EXISTS: 'same_request',
  RAISE_OFF_ON_SERVER: 'same_request',

  // A cancel's own refusals (ChannelPlex asks Recruitment to delete an opening). None is an
  // answer of a raise's review or confirm - a raise that finds a cancel open under its
  // budget is told so as RAISE_IN_PROGRESS_FOR_BUDGET. Listed so the list is whole; what
  // the cancel step itself does with each is CANCEL_REFUSAL_STEPS.
  CANCEL_NOT_ENABLED: 'same_request',
  CHANGE_OFF_ON_SERVER: 'same_request',
  CANCEL_SHARED_OPENING: 'same_request',
  CANCEL_IN_PROGRESS: 'same_request',
  CANCEL_HAS_OFFER: 'same_request',
  CANCEL_HAS_JOINER: 'same_request',
  LINK_NOT_CANCELLING: 'same_request',

  // A hiring request (one person asks, another confirms). One is already waiting for this
  // store or person (several with no store may wait under one budget since 15 Oct 2026);
  // or the one being acted on is no longer waiting: nothing to
  // change here, and the page's lists are read again so it shows that.
  REQUEST_ALREADY_WAITING: 'wait',
  REQUEST_NOT_WAITING: 'wait',
  // It is not there, not this person's to withdraw, or can no longer be read as it was sent.
  REQUEST_NOT_FOUND: 'same_request',
  REQUEST_NOT_YOURS: 'same_request',
  REQUEST_UNREADABLE: 'same_request',

  // The Hiring dashboard's Download (28 Oct 2026): another download is being prepared, for this
  // person or three on the server (HTTP 429). Never an answer of a review or a confirm; listed so
  // the list is whole. The dashboard shows its sentence under the toolbar.
  EXPORT_BUSY: 'same_request',
};

/** Every refusal code the Raise hiring window has a step for. */
export const RAISE_REFUSAL_CODES = Object.keys(RAISE_REFUSAL_STEPS);

/** The step for a refusal; null when the call was not refused with a code at all (no answer, a broken answer). */
export const raiseRefusalStep = (err: unknown): RaiseRefusalStep | null => {
  const code = hiringCodeOf(err);
  return code ? RAISE_REFUSAL_STEPS[code] ?? 'same_request' : null;
};

/** Refusals of the confirm that end this review: what was reviewed can no longer be sent as it is. */
export const mustReviewAgain = (err: unknown): boolean => raiseRefusalStep(err) === 'review_again';

/** Refusals that are answered on the form (a tick, a city, another person or address), or by correcting the store first. */
export const sendsBackToForm = (err: unknown): boolean => raiseRefusalStep(err) === 'form';

/**
 * Refusals that say the owner's list is behind: a request, or an opening, that the page
 * does not show yet is in the way. The list is read again, so the row says so too.
 */
export const listIsBehindAfter = (err: unknown): boolean => {
  const code = hiringCodeOf(err);
  return raiseRefusalStep(err) === 'wait' || code === RAISE_CODES.storeHasHiring || code === RAISE_CODES.roleHasHiring;
};

/**
 * The opening a refusal says is already there for this store or role, unlinked: the one
 * "Link it instead" is about. Null for any other refusal.
 */
export const openingToLinkInstead = (err: unknown): number | null => {
  if (hiringCodeOf(err) !== RAISE_CODES.openingExists) return null;
  const openingId = hiringDetailOf(err).openingId;
  return typeof openingId === 'number' && Number.isInteger(openingId) && openingId > 0 ? openingId : null;
};

/** The request that is in the way under a budget, when the refusal names it. */
export const blockingRequestOf = (err: unknown): HiringRaiseBlocking | null => {
  if (hiringCodeOf(err) !== RAISE_CODES.budgetBusy) return null;
  const raw = hiringDetailOf(err).blocking as Partial<HiringRaiseBlocking> | undefined;
  if (!raw || typeof raw !== 'object' || typeof raw.linkId !== 'string' || !raw.linkId) return null;
  return {
    linkId: raw.linkId,
    label: typeof raw.label === 'string' ? raw.label : null,
    subjectKind: typeof raw.subjectKind === 'string' ? raw.subjectKind : 'store',
    sentAt: typeof raw.sentAt === 'string' ? raw.sentAt : null,
    settleAfter: typeof raw.settleAfter === 'string' ? raw.settleAfter : null,
    // A cancel of one of the budget's openings, not a request to raise one: the button says "opening".
    ...(raw.what === 'cancel' ? { what: 'cancel' as const } : {}),
  };
};

/** The words on the button that opens what is in the way under a budget. */
export const openBlockingWords = (blocking: Pick<HiringRaiseBlocking, 'what'> | null | undefined): string =>
  (blocking?.what === 'cancel' ? RAISE_WORDS.openThatOpening : RAISE_WORDS.openThatRequest);

/**
 * A refusal that came as a server error (5xx) and still says, by its code, that NOTHING
 * was sent: it is made before anything is claimed. Recruitment did not answer a check.
 * Such an answer is not "no answer": the server's own sentence stands alone, and the same
 * request can be asked again.
 */
export const nothingWasSentBy = (err: unknown): boolean => {
  const status = hiringStatusOf(err);
  const code = hiringCodeOf(err);
  return status !== undefined && status >= 500 && code === HIRING_CODES.recruitmentUnavailable;
};

export interface RaiseFormState {
  /**
   * A store, or no store (`role`). Since 15 Oct 2026 a hiring with no store sends no name: the
   * server names it after its budget. A typed `roleLabel` is still sent where one is given.
   */
  subject: { kind: 'store'; storeId: string } | { kind: 'role'; roleLabel?: string };
  budgetId: number;
  reason: HiringRaiseReason;
  replacesEmpCode: string;
  /** The Recruitment city picked for a store. */
  storeCityId: number | null;
  /**
   * `point`: the map point of an address picked from Google's suggestions (15 Oct 2026), so the
   * opening sits at the real address on Recruitment's map, not at the city's point. Null when
   * the address was typed and not picked.
   */
  roleAddress: { source: HiringRoleAddressSource; cityId: number | null; address: string; pincode: string; point?: HiringMapPoint | null };
  additional: boolean;
  confirmStatus: boolean;
  confirmNoRecord: boolean;
  /** The recruitment team picked in the section. Null in ask mode: the person who confirms picks it. */
  team: HiringRaiseTeam | null;
}

/**
 * The body of the review, from what was filled in. The confirm sends this same body
 * again with its key and the review's hash, so only what the form really shows goes in:
 * a code for a replacement only, a city only where one was picked, an address only for
 * a role, a tick only when it is ticked, and the team only where it was picked. There is
 * no note (14 Oct 2026).
 */
export const buildRaiseBody = (form: RaiseFormState): HiringRaiseInput => {
  const subject: HiringRaiseInput['subject'] = form.subject.kind === 'role' && !String(form.subject.roleLabel ?? '').trim()
    ? { kind: 'role' }
    : form.subject;
  const body: HiringRaiseInput = { subject, budgetId: form.budgetId, reason: form.reason };
  if (form.reason === 'replacement' && form.replacesEmpCode.trim()) body.replacesEmpCode = form.replacesEmpCode.trim();
  if (form.subject.kind === 'store') {
    if (form.storeCityId != null) body.cityId = form.storeCityId;
  } else if (form.roleAddress.source === 'typed') {
    if (form.roleAddress.cityId != null) body.cityId = form.roleAddress.cityId;
    const point = mapPointOf(form.roleAddress.point);
    body.roleAddress = {
      source: 'typed',
      address: form.roleAddress.address.trim().replace(/\s+/g, ' '),
      pincode: form.roleAddress.pincode.trim(),
      // Only an address picked from Google's suggestions has one: a typed one goes to the city's point.
      ...(point ? { lat: point.lat, lng: point.lng } : {}),
    };
  } else {
    body.roleAddress = { source: form.roleAddress.source };
  }
  if (form.additional) body.confirmAdditional = true;
  if (form.confirmStatus) body.confirmStatus = true;
  if (form.confirmNoRecord) body.confirmNoRecord = true;
  if (form.team) body.team = form.team;
  return body;
};

/**
 * The key of one confirm. Made once, when the review step opens, and sent with every
 * repeat of that confirm: the server answers a repeat from the first request, so one
 * review can never create two openings. 32 letters and digits, which is what the
 * server's own check takes (16 to 64 of A-Z a-z 0-9 -).
 */
export const makeRaiseRequestKey = (): string => {
  const source = (globalThis as { crypto?: Crypto }).crypto;
  if (source?.randomUUID) return source.randomUUID();
  const bytes = new Uint8Array(16);
  if (source?.getRandomValues) source.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
};

/* ── the words of the window ── */

export const RAISE_WORDS = {
  /** The control in a store's row, and the buttons that open the window. */
  rowControl: (storeName: string): string => `Raise hiring for ${storeName}`,
  /** Said on the row control of a store whose request has not ended: it cannot be pressed until it has. */
  rowControlBusy: 'Hiring is already being raised for this store',
  roleButton: 'Raise hiring for a role',
  panelButton: 'Raise hiring',
  titleStore: (storeName: string, storeCode: string): string => `Raise hiring for ${storeName}${storeCode ? ` (${storeCode})` : ''}`,
  titleRole: 'Raise hiring for a role',

  storeHeading: 'Store',
  /** What the window shows of a store, read-only, in this order. */
  storeFacts: {
    address: 'Address', cityAndState: 'City and state', pincode: 'PIN code', mapLocation: 'Map location',
    status: 'Status', staffingStatus: 'Staffing status',
  },
  /** The link on the store card: the store's own Edit Store window, over this one. Not drawn where there is none to open. */
  editStore: 'Edit store',
  editStoreHover: 'Opens this store’s Edit Store window. Saving there refreshes this card.',
  mapOnRecord: 'On record',
  mapNotOnRecord: 'Not on record',
  notSet: 'Not set',
  roleName: 'Role name',
  roleNamePlaceholder: 'For example, Area Manager',
  reasonLegend: 'Why is hiring needed?',
  budget: 'Budget',
  budgetBeforeReason: 'Choose why hiring is needed to see the budgets.',
  budgetsLoading: 'Loading budgets from Recruitment…',
  budgetsFailed: 'Could not load the budgets. Try again.',
  /** A budget card: "3" above "free of 10". */
  budgetFreeOf: (total: number): string => `free of ${total}`,
  budgetFull: 'Full',
  budgetFullHover:
    'No free position. A leaver’s position is freed when HR processes their exit in 1Office, or the project manager can add one in 1Office Project-Management.',
  /** Under the budget cards, when the person being replaced has their own budget among them (it is the first card). */
  ownBudget: (who: string): string => `${who}’s budget.`,
  budgetSearch: 'Search budgets',
  budgetSearchNone: 'No budget matches.',
  tryAgain: 'Try again',
  noBudgets: (subjectKind: 'store' | 'role', reason: HiringRaiseReason | ''): string => (
    reason === 'replacement'
      ? 'No budget can be used. A budget must be active in Recruitment and saved for this project on the ChannelPlex > Project Management page.'
      : subjectKind === 'store'
        ? 'No budget can be used. A store needs a budget that is marked Store-level on the ChannelPlex > Project Management page and is active in Recruitment.'
        : 'No budget can be used. A manager role needs one of the project’s budgets that is not marked Store-level and is active in Recruitment.'
  ),
  /** "Who is being replaced?": the cards of the people who may be replaced. */
  whoReplaced: 'Who is being replaced?',
  whoReplacedInfo: (subjectKind: 'store' | 'role'): string => (subjectKind === 'store'
    ? 'This store’s people, and anyone who left it in the last 30 days.'
    : 'This budget’s people who are leaving, or who left in the last 30 days.'),
  /** A manager role: the budget anchors the list, so it comes first. */
  whoReplacedBeforeBudget: 'Choose a budget to see who is leaving.',
  whoReplacedLoading: (subjectKind: 'store' | 'role'): string => (subjectKind === 'store' ? 'Reading this store’s people…' : 'Reading this budget’s people…'),
  whoReplacedFailed: 'Could not read who may be replaced. Try again.',
  whoReplacedNobody: (subjectKind: 'store' | 'role'): string => (subjectKind === 'store'
    ? 'Nobody works here, and nobody left in the last 30 days. Choose One more person.'
    : 'Nobody under this budget is leaving or left in the last 30 days. Choose One more person.'),
  addressLegend: 'Address on the opening',
  typedCity: 'City, from Recruitment’s list',
  typedAddress: 'Address',
  typedPincode: 'PIN code',
  typedMapNote: 'The opening is placed on the map at the city’s own point in Recruitment.',
  sourceNotComplete: 'Not complete in Recruitment',
  storeCity: 'Recruitment city for this store',
  citySearchPlaceholder: 'Type at least 2 letters of the city',
  citiesLoading: 'Searching Recruitment’s cities…',
  citiesFailed: 'Could not search Recruitment’s cities. Try again.',
  picked: (cityLabel: string): string => `Picked: ${cityLabel}`,
  noCities: 'No city in Recruitment’s list matches.',
  cityNoMap: 'No map location in Recruitment',
  cancel: 'Cancel',
  review: 'Review',
  reviewing: 'Checking with Recruitment…',
  nothingSentYet: 'Nothing is sent until you review and confirm.',
  /** The review could not be made, or could not be read. Either way nothing went to Recruitment. */
  reviewFailed: 'Could not check this with Recruitment. Nothing was sent. Try again.',
  reviewUnreadable: 'ChannelPlex could not read what Recruitment would do. Nothing was sent. Try again.',

  /** The review step. The lines themselves are the server's. */
  reviewHeading: 'What Recruitment will do',
  yes: 'Yes, raise hiring',
  no: 'No, go back',
  footnote: 'Sent to Recruitment in your name.',
  sending: 'Sending to Recruitment…',
  /** A refusal of the checks that came with no sentence of its own. */
  refusedPlain: 'This request was refused before anything was sent to Recruitment.',
  sendingNote:
    'This can take a few minutes. You can close this window: the request goes on either way, and the Hiring column shows how it ended.',
  /** No answer came back: the one thing that must not happen now is a second request. */
  lostAnswer:
    'ChannelPlex did not get an answer, so it does not know yet whether this request reached Recruitment. Do not start a new request.',
  lostAnswerNext:
    'Ask again repeats this same request: if it already went through you are shown what happened, and if it never arrived it is sent now. It is never sent to Recruitment twice.',
  /** A repeat was refused after an answer had been lost: the form still stays out of reach here. */
  lockedNote:
    'An earlier answer for this request did not arrive, so this window does not offer the form again. To start over, close it: the Hiring column shows the request if it did go through.',
  askAgain: 'Ask again',
  openThatRequest: 'Open that request',
  /** The same button when what is in the way is a cancel of an opening, not a request to raise one. */
  openThatOpening: 'Open that opening',
  /**
   * Offered when Recruitment already has an unlinked opening with this store's or role's
   * name on it. The server's sentence, or the line beside the review, names that opening.
   */
  linkInstead: 'Link it instead',

  /** The result step. */
  resultCreated: 'Hiring raised',
  resultWaiting: 'Waiting for Recruitment',
  resultNothing: 'Nothing was created',
  resultAlreadyLinked: 'Already linked',
  raiseAgain: 'Raise again',
  close: 'Close',
  alreadyRaising: (subjectKind: 'store' | 'role'): string =>
    `Hiring is already being raised for this ${subjectKind}. A new request can be made once this one has ended.`,

  /** A request Recruitment has not confirmed, wherever it is shown. */
  sentOn: (moment: string): string => `Sent to Recruitment on ${moment}.`,
  checksByItself: 'ChannelPlex checks again by itself for the next minute.',
  checkAgain: 'Check again',
  checking: 'Checking Recruitment…',
  checkFailed: 'Could not check just now. Try again in a moment.',
  newOpenings: 'New openings under this budget',
  thisOne: 'This one',
  thisOneLabel: (openingId: number): string => `Opening ${openingId} is this request’s opening`,
  sameAsSent: 'Same as was sent',
  alreadyLinked: 'Already linked',
  pickFailed: 'Could not save your choice. Try again.',
  closeRequest: 'Nothing was created, close this request',
  closeGroupLabel: 'Confirm closing this request',
  closeQuestion: 'Close this request as not created?',
  closeWarning:
    'Do this only if Recruitment has no opening for it. The store or role, and the budget, are then free for another request.',
  closeNo: 'No, keep it open',
  closeYes: 'Yes, close it',
  closeFailed: 'Could not close this request. Try again.',

  /** The Hiring tab: only the requests Recruitment has not confirmed. */
  waitingFilter: 'Waiting for Recruitment',

  /** The hiring panel. */
  requestHeading: 'Request to raise hiring',
  standingLabel: 'Where this request stands',
  panelEmptyHint: 'Use Raise hiring to ask Recruitment to create a new opening.',

  /** A raised opening that is live, with an open question about it. */
  incompleteTitle: 'Recruitment did not finish setting up this opening',
  notInListTitle: 'Not in its budget’s list of openings',
} as const;

/**
 * The Recruitment team section, in the Raise hiring window and in the hiring request window
 * of the person who confirms. Staff are named "Name (code)", as 1Office names them; names
 * are read fresh, shown, and never kept.
 */
export const TEAM_WORDS = {
  heading: 'Recruitment team',
  fromDefaults: 'From the budget’s defaults.',
  noDefaults: 'No defaults saved for this budget.',
  loading: 'Loading the team from Recruitment…',
  failed: 'Could not load the team from Recruitment. Try again.',
  unreadable: 'ChannelPlex could not read the team from Recruitment. Try again.',
  /** Ask mode, in place of the section. */
  askLine: 'The person who confirms picks the recruiters and interviewers.',

  recruiters: 'Recruiters',
  recruitersInfo: 'Also sent as schedulers, as 1Office does. Anyone with the Recruiter role in Recruitment.',
  offerMakers: 'Offer makers',
  offerMakersInfo: 'Anyone with the Offer Maker role in Recruitment.',
  interviewers: 'Interviewers',
  interviewersInfo: 'One per interview round, from the people whose grade is set for that round in Recruitment.',

  add: '+ Add',
  addHover: 'Search by name or code',
  addLabel: (what: 'recruiter' | 'offer maker'): string => `Add ${what === 'offer maker' ? 'an offer maker' : 'a recruiter'}`,
  searchLabel: (what: string): string => `Search ${what} by name or code`,
  searchPlaceholder: 'Search by name or code',
  noMatch: 'Nobody matches.',
  /** The closed chips field while nobody is picked, as the other pickers' "Choose …". */
  choose: (what: 'recruiter' | 'offer maker'): string => (what === 'offer maker' ? 'Choose offer makers' : 'Choose recruiters'),
  /**
   * The open list of the role 1Office answered EMPTY (the server's 503 names it); "Try again"
   * follows it. The lists are company-wide, not the project's.
   */
  listedNone: (what: 'recruiter' | 'offer maker'): string => `1Office listed no ${what === 'offer maker' ? 'offer makers' : 'recruiters'} just now.`,
  /** The closed field of that role. */
  listedNoneShort: (what: 'recruiter' | 'offer maker'): string => `1Office listed no ${what === 'offer maker' ? 'offer makers' : 'recruiters'}`,
  /**
   * The open list of a row when the team could not be read and nothing says it was this row's
   * list: one error comes for the whole team. "Try again" follows it.
   */
  teamUnread: 'Could not read the team from 1Office.',
  /** The closed field then. */
  teamUnreadShort: 'Could not read from 1Office',
  /** Nothing typed, and everyone 1Office lists is already picked. */
  allPicked: 'Everyone listed is picked.',
  closeSearch: 'Cancel',
  pickLabel: (person: string): string => `Pick ${person}`,
  remove: (person: string): string => `Remove ${person}`,
  notAllowed: 'No longer holds this role in Recruitment.',

  round: (n: number): string => `Round ${n}`,
  roundClient: (n: number): string => `Round ${n} · client`,
  change: 'Change',
  changeLabel: (n: number): string => `Change the round ${n} interviewer`,
  pick: 'Pick',
  pickRoundLabel: (n: number): string => `Pick the round ${n} interviewer`,
  nobodyPicked: 'Nobody picked',
  roundSearchLabel: (n: number): string => `round ${n} interviewers`,
  clientName: 'Client name',
  clientEmail: 'Email',
  clientMobile: 'Mobile (optional)',
  clientFieldLabel: (n: number, field: 'name' | 'email' | 'mobile'): string => `Round ${n} client ${field}`,
} as const;

/**
 * A store was just added (Stores › Add New Store made a new store, or Store Requests ›
 * Approve & create store did): the step that offers to raise hiring for it, with the
 * reason New store. Offered only where the person may raise or ask, and the store is
 * Active with no staffing hold (newStoreRaise.ts).
 */
export const STORE_ADDED_WORDS = {
  title: 'Store added',
  line: (storeName: string, storeCode: string): string => `${storeName}${storeCode ? ` (${storeCode})` : ''} is on the Stores list.`,
  done: 'Done',
  raise: 'Raise hiring for it',
  ask: 'Ask for hiring for it',
} as const;

export const RAISE_NOT_FULLY_SET_UP = 'not fully set up in Recruitment';

/* ── where a request stands ── */

export const RAISE_SENDING = 'Sending to Recruitment…';
/** Nothing checks a request on a timer: the line sends the reader to the request, where the check is. */
export const RAISE_NOT_CONFIRMED = 'Recruitment did not confirm. Open it to check.';

/** The short line under "Raising hiring": where the request stands. Null when the list did not say. */
export const raiseShortLine = (link: HiringLink): string | null => {
  if (link.state !== 'sending') return null;
  if (link.raise?.sendStatus === 'unknown') return RAISE_NOT_CONFIRMED;
  if (link.raise?.sendStatus === 'claimed') return RAISE_SENDING;
  return null;
};

/**
 * Whether a request moved on between two answers: it ended, Recruitment's answer about
 * it changed, or an opening number arrived. A check that found nothing new is not worth
 * re-reading a whole list for.
 */
export const raiseLinkMoved = (before: HiringLink, after: HiringLink): boolean =>
  before.state !== after.state
  || before.openingId !== after.openingId
  || (before.raise?.sendStatus ?? null) !== (after.raise?.sendStatus ?? null)
  || (before.raise?.answeredOpeningId ?? null) !== (after.raise?.answeredOpeningId ?? null)
  || (before.attention ?? []).join() !== (after.attention ?? []).join();

/** Whether a store or role has a request that has not ended. While it has, no second one is offered for it. */
export const hasRaiseInFlight = (links: readonly HiringLink[] | null | undefined): boolean =>
  (links ?? []).some((link) => link.state === 'sending');

/** The sentence for a request still being raised: the server's, or the short line when it sent none. */
export const raiseStandingSentence = (link: HiringLink): string =>
  localiseRaiseMessage(link.raise?.message) || raiseShortLine(link) || 'ChannelPlex is raising hiring. Recruitment has not confirmed an opening yet.';

/**
 * How an outcome is shown, and what may be offered after it.
 *   created         the opening exists and is linked.
 *   waiting         Recruitment has not confirmed. NEVER offers "Raise again": the request
 *                   may have created an opening, and a second one would be a duplicate.
 *   nothing         closed with nothing made; raising again is safe.
 *   already_linked  the opening exists and is followed on a link made by hand.
 * An outcome this screen does not know is "waiting": the safe reading.
 */
export type RaiseOutcomeKind = 'created' | 'waiting' | 'nothing' | 'already_linked';

const OUTCOME_KIND: Record<string, RaiseOutcomeKind> = {
  created: 'created',
  not_confirmed: 'waiting',
  needs_choice: 'waiting',
  in_progress: 'waiting',
  held_elsewhere: 'waiting',
  refused: 'nothing',
  not_sent: 'nothing',
  closed_not_created: 'nothing',
  already_linked: 'already_linked',
};

export const RAISE_OUTCOMES = Object.keys(OUTCOME_KIND);

export const raiseOutcomeKind = (outcome: string | null | undefined): RaiseOutcomeKind =>
  (outcome && OUTCOME_KIND[outcome]) || 'waiting';

/** The heading over an outcome. */
export const raiseOutcomeHeading = (outcome: string | null | undefined): string => {
  switch (raiseOutcomeKind(outcome)) {
    case 'created': return RAISE_WORDS.resultCreated;
    case 'nothing': return RAISE_WORDS.resultNothing;
    case 'already_linked': return RAISE_WORDS.resultAlreadyLinked;
    default: return RAISE_WORDS.resultWaiting;
  }
};

/** An outcome that waits for a person, not for Recruitment: checking by itself would change nothing. */
export const raiseNeedsAPerson = (outcome: string | null | undefined): boolean =>
  outcome === 'needs_choice' || outcome === 'held_elsewhere';

/** When ChannelPlex checks a request again by itself, counted from when it is shown. Then "Check again". */
export const RAISE_CHECK_AFTER_MS: readonly number[] = [5_000, 15_000, 45_000];

/**
 * Whether "Nothing was created, close this request" is offered: only once the time the
 * request stays open has passed. While Recruitment's own answer named an opening, that
 * number stands, and closing is offered only once the server itself has said a person
 * must decide (`lastOutcome` is the outcome of the latest check, when there was one).
 * The server still has the last word: it refuses a close it cannot stand behind.
 */
export const canCloseAsNotCreated = (link: HiringLink, nowMs: number, lastOutcome?: string | null): boolean => {
  if (link.state !== 'sending' || link.raise?.sendStatus !== 'unknown') return false;
  if (link.raise.answeredOpeningId != null && lastOutcome !== 'needs_choice') return false;
  const until = link.raise.settleAfter ? Date.parse(link.raise.settleAfter) : NaN;
  return Number.isFinite(until) && nowMs >= until;
};

export interface RaiseCandidateRow { title: string; line: string; tag: string | null; pickable: boolean }

/** One new opening a person may pick as the one their request created. An opening, never a person. */
export const raiseCandidateRow = (candidate: HiringRaiseCandidate): RaiseCandidateRow => {
  const where = [candidate.cityName, candidate.location].map((part) => String(part ?? '').trim()).filter(Boolean);
  return {
    title: `Opening ${candidate.openingId}`,
    line: [...where, counted(candidate.positions, 'position')].join(' · '),
    tag: candidate.linked ? RAISE_WORDS.alreadyLinked : candidate.matches ? RAISE_WORDS.sameAsSent : null,
    pickable: !candidate.linked,
  };
};

/** The new openings a refusal lists (it lists them when it says "pick one" or "none of these"). */
export const candidatesOf = (err: unknown): HiringRaiseCandidate[] => {
  const raw = hiringDetailOf(err).candidates;
  if (!Array.isArray(raw)) return [];
  return raw.filter((c): c is HiringRaiseCandidate =>
    !!c && typeof c === 'object' && typeof (c as HiringRaiseCandidate).openingId === 'number');
};

/** The open question about an opening ChannelPlex raised and that is live: a title and the server's sentence. */
export const raisedOpeningFlag = (link: HiringLink): { title: string; sentence: string } | null => {
  if (link.state !== 'live') return null;
  const attention = link.attention ?? [];
  // Not for `team_unconfirmed` alone: the people on a just-raised opening are only not read
  // back yet - nobody needs to do anything, and the opening reads Hiring (15 Oct 2026).
  const title = attention.includes('created_incomplete') ? RAISE_WORDS.incompleteTitle
    : attention.includes('not_listed') ? RAISE_WORDS.notInListTitle
    : null;
  const sentence = localiseRaiseMessage(link.raise?.message);
  return title && sentence ? { title, sentence } : null;
};

/* ── the timeline of a raise ── */

const MISSING_WORDS: Record<string, string> = {
  recruiters: 'its recruiters',
  schedulers: 'its schedulers',
  offer_makers: 'its offer makers',
  interviewer_1: 'its interviewer for round 1',
  interviewer_2: 'its interviewer for round 2',
  interviewer_3: 'its interviewer for round 3',
  interviewer_4: 'its interviewer for round 4',
  mail: 'the new-position mail',
};
export const RAISE_MISSING_PARTS = Object.keys(MISSING_WORDS);

const listOf = (parts: readonly string[]): string =>
  (parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`);

/** "Recruitment did not finish setting it up: its recruiters and the new-position mail." Null when nothing is missing. */
const missingLine = (missing: unknown): string | null => {
  const parts = (Array.isArray(missing) ? missing : []).map((part) => MISSING_WORDS[String(part)]).filter(Boolean);
  return parts.length > 0 ? `Recruitment did not finish setting it up: ${listOf(parts)}.` : null;
};

const REFUSED_WORDS: Record<string, string> = {
  budget_limit: 'The budget has no free position.',
  sign_in: 'Recruitment did not accept the 1Office sign-in.',
  forbidden: 'Recruitment did not allow this for the 1Office account.',
};
export const RAISE_REFUSED_KINDS = [...Object.keys(REFUSED_WORDS), 'rejected', 'recruitment'];

const NOT_SENT_WORDS: Record<string, string> = {
  no_identity: 'The 1Office sign-in could not be used.',
  switched_off: 'Raising hiring was switched off just before it was sent.',
  restart: 'ChannelPlex stopped before it was sent.',
};
const NOT_SENT_OTHER = 'ChannelPlex could not start the request.';
export const RAISE_NOT_SENT_WHY = [...Object.keys(NOT_SENT_WORDS), 'not_stamped', 'stamp_failed'];

const answeredNumber = (detail: Record<string, unknown>): string =>
  (typeof detail.answeredOpeningId === 'number' ? `opening ${detail.answeredOpeningId}` : 'an opening');

const UNKNOWN_WORDS: Record<string, (detail: Record<string, unknown>) => string> = {
  no_answer: () => 'No answer came back in time.',
  stakeholders: () => 'Recruitment answered with an error after it may have saved the opening.',
  server_error: () => 'Recruitment answered with an error after it may have saved the opening.',
  unreadable_success: () => 'Recruitment’s answer could not be read as created or as refused.',
  other_budget: () => 'Recruitment’s answer could not be read as created or as refused.',
  unexpected_status: () => 'Recruitment’s answer could not be read as created or as refused.',
  not_listed_yet: (d) => `Recruitment answered that ${answeredNumber(d)} was created. Its list did not show it yet.`,
  listed_differently: (d) =>
    `Recruitment answered that ${answeredNumber(d)} was created. Its list shows it with a different address or number of positions.`,
  held_elsewhere: (d) => `Recruitment created ${answeredNumber(d)}, and it had just been linked by hand elsewhere.`,
  restart: () => 'ChannelPlex stopped before the answer was recorded.',
};
export const RAISE_UNKNOWN_WHY = Object.keys(UNKNOWN_WORDS);

/** What was ticked on the form before the request, in words. */
const tickedLine = (confirmed: unknown): string | null => {
  const c = (confirmed && typeof confirmed === 'object' ? confirmed : {}) as Record<string, unknown>;
  const said: string[] = [];
  if (c.additional === true) said.push('one more person is needed');
  if (c.status === true) said.push('raise although the store’s staffing status says not to');
  if (c.noRecord === true) said.push('the person worked at this store');
  return said.length > 0 ? `Confirmed by the person who raised it: ${listOf(said)}.` : null;
};

/** The kinds of timeline entry a raise writes. */
export const RAISE_EVENT_TYPES: readonly string[] = [
  'send_claimed', 'send_not_sent', 'send_confirmed', 'send_refused', 'send_unknown', 'send_settled', 'attention_answered',
];

/** One entry of a raise's timeline in words; null for any other kind of entry. Counts and codes only. */
export const raiseTimelineLine = (event: HiringTimelineEvent): TimelineLine | null => {
  const detail = event.detail ?? {};
  const opening = event.openingId ?? (typeof detail.openingId === 'number' ? detail.openingId : null);
  const openingText = opening != null ? `opening ${opening}` : 'an opening';
  const note = textOf(detail.note);
  const lines = (...parts: (string | null)[]): string[] => parts.filter((part): part is string => !!part);
  switch (event.type) {
    case 'send_claimed': {
      const budget = textOf(detail.budgetName);
      const reason = textOf(detail.reason);
      const replaces = textOf(detail.replacesEmpCode);
      return {
        title: `Asked Recruitment to create an opening${budget ? ` · ${budget}` : ''}`,
        details: lines(
          reason ? `Reason: ${raiseReasonLabel(reason)}${replaces ? ` · Replacing ${replaces}` : ''}` : null,
          tickedLine(detail.confirmed),
        ),
        note,
      };
    }
    case 'send_not_sent':
      return {
        title: 'Nothing was sent to Recruitment',
        details: [NOT_SENT_WORDS[String(detail.why)] ?? NOT_SENT_OTHER],
        note: null,
      };
    case 'send_confirmed':
      return {
        title: `Recruitment created ${openingText}`,
        details: lines(missingLine(detail.missing), detail.listed === false ? 'It is not in its budget’s list of openings.' : null),
        note: null,
      };
    case 'send_refused': {
      const said = textOf(detail.text);
      const kind = String(detail.kind);
      const why = REFUSED_WORDS[kind]
        ?? (kind === 'rejected'
          ? `Recruitment rejected the request before creating anything${typeof detail.httpStatus === 'number' ? ` (HTTP ${detail.httpStatus})` : ''}.`
          : said ? `Recruitment said: “${said}”` : null);
      return { title: 'Recruitment refused. Nothing was created', details: lines(why), note: null };
    }
    case 'send_unknown': {
      const why = UNKNOWN_WORDS[String(detail.why)];
      return { title: 'Recruitment did not confirm', details: lines(why ? why(detail) : null), note: null };
    }
    case 'send_settled': {
      const byPerson = detail.by === 'person';
      if (detail.result === 'not_applied') {
        return {
          title: byPerson ? 'Closed as not created' : 'Closed: Recruitment’s list showed no new opening under the budget',
          details: [],
          note,
        };
      }
      if (typeof detail.supersededBy === 'string' && detail.supersededBy) {
        return { title: `Recruitment created ${openingText}, which was already linked here by hand`, details: [], note };
      }
      return {
        title: byPerson ? `Picked as this request’s opening: ${openingText}` : `Found in Recruitment’s list: ${openingText}`,
        details: lines(missingLine(detail.missing), detail.listed === false ? 'It is not in its budget’s list of openings.' : null),
        note,
      };
    }
    case 'attention_answered':
      return { title: 'Recruitment now shows everyone who was put on this opening', details: [], note: null };
    default:
      return null;
  }
};

/* ── the Raise switch (super admins) ── */

export const RAISE_SWITCH = {
  label: 'Raise hiring from ChannelPlex',
  about:
    'Lets people with "Raise & cancel hiring" raise a new opening for a store or a manager role from ChannelPlex. '
    + 'Recruitment creates a real opening at once, normally mails its recruiters, and can alert candidates. A person reviews and confirms each one. '
    + 'Everyone else with Stores ticked can fill the same form and send it for confirmation. Nothing goes to Recruitment until a person with "Raise & cancel hiring" confirms it.',
  needsSee: 'Turn on Show hiring for this project first.',
  /**
   * The second step before Raise is turned ON for a project. Turning it on is what lets
   * real openings be created, so it is never one stray click. Turning it off asks nothing.
   */
  confirmGroupLabel: 'Confirm turning on Raise',
  confirmLines: [
    // "Normally": Recruitment skips the mail silently in some cases, and ChannelPlex cannot see whether it was sent.
    'Raise creates real openings in Recruitment. Recruiters are normally mailed, and for a published budget nearby candidates get an alert.',
    'Turn it on only for the supervised test with a test budget, or after that test has passed.',
    // Who it reaches. Raising is "Raise & cancel hiring", a row of its own in Access
    // Management (since 14 Oct 2026); asking for it is the Stores tick.
    'Only people whose role has "Raise & cancel hiring" ticked in Access Management will be able to raise.',
    'Everyone else with Stores ticked will be able to send hiring requests for confirmation.',
  ],
  confirmNo: 'No, keep it off',
  confirmYes: 'Yes, turn on Raise',
  /** The server's emergency off of Raise is set (the settings answer says Raise is not available). */
  stoppedOnServer:
    'Raise is stopped on the server for every project (the server setting HIRING_LINK_RAISE_ENABLED is set to off). '
    + 'Nothing can be raised or sent to Recruitment, and this switch cannot be turned on, until that setting is removed. Seeing hiring is not affected.',
  seeHeldOn: 'To hide hiring, turn off Raise hiring from ChannelPlex first.',
  // Who, as the second step said it: "Raise & cancel hiring" raises, the Stores tick asks.
  turnedOn: 'People with "Raise & cancel hiring" can now raise openings for this project. Others with Stores ticked can send hiring requests for confirmation.',
  turnedOff:
    'Raising hiring is now off for this project. Openings already raised are kept, and a request still waiting for Recruitment can still be checked. '
    + 'Hiring requests waiting for confirmation are kept, and come back when Raise is turned on again.',
  saveFailed: 'Could not save this switch. Try again.',
} as const;

/* ═══════════════════════════════ Hiring requests: one person asks, another confirms ═══════════════════════════════ */
//
// Only a person with "Raise & cancel hiring" (a row of its own in Access Management) raises.
// So a person with the Stores tick alone fills the Raise hiring form and sends it for
// confirmation: NOTHING is sent to Recruitment. A person with "Raise & cancel hiring" opens
// the waiting hiring request, gets a fresh review in their own 1Office sign-in, and
// confirms it or declines it with a reason.
//
// On screen it is always a "hiring request". The screens already call a create that
// Recruitment has not confirmed a "request to raise hiring"; those words stay as they are,
// and the two are never called by the same name.
//
// Where it stands (`line`), what is in its way (`notes`), every refusal and the review's
// lines are the server's own sentences. What follows are the screens' words around them.

/** The refusal codes of a hiring request that the screens act on. Every other code only shows its sentence. */
export const REQUEST_CODES = {
  notFound: 'REQUEST_NOT_FOUND',
  notWaiting: 'REQUEST_NOT_WAITING',
  alreadyWaiting: 'REQUEST_ALREADY_WAITING',
  notYours: 'REQUEST_NOT_YOURS',
  unreadable: 'REQUEST_UNREADABLE',
} as const;

/** A decline needs this many characters before it can be sent; the server asks the same. */
export const DECLINE_REASON_MIN = 3;
export const DECLINE_REASON_MAX = 500;

/** Why hiring was asked for, in a line beside the budget. A replacement names the person next to it. */
const REQUEST_REASON_WORDS: Record<string, string> = {
  replacement: 'Replacement',
  new_store: 'New store',
  extra_headcount: 'One more person',
};

export const requestReasonLabel = (reason: string | null | undefined): string =>
  (reason && REQUEST_REASON_WORDS[reason]) || 'Other';

/** "Replacement · Replacing 910001": why, and for whom. */
export const requestReasonLine = (request: Pick<HiringRequest, 'reason' | 'replacesEmpCode'>): string =>
  [requestReasonLabel(request.reason), request.replacesEmpCode ? `Replacing ${request.replacesEmpCode}` : null].filter(Boolean).join(' · ');

const plainSubjectName = (request: Pick<HiringRequest, 'subject'>): string =>
  (request.subject.kind === 'store'
    ? request.subject.storeName || request.subject.storeCode || 'a store'
    : request.subject.roleLabel || 'a hiring with no store');

/** What a hiring request is for, in a control's name: the store's name, or the role's. */
export const requestSubjectName = plainSubjectName;

export const REQUEST_WORDS = {
  /* ── asking: the Stores tab's row control, the Hiring tab's button, the panel's button ── */
  rowControl: (storeName: string): string => `Ask for hiring for ${storeName}`,
  /** Said on the row control of a store whose hiring request has not been decided: no second one is offered. */
  rowControlWaiting: 'A hiring request for this store is already waiting for confirmation',
  roleButton: 'Ask for hiring for a role',
  panelButton: 'Ask for hiring',
  panelEmptyHint: 'Use Ask for hiring to send a hiring request for confirmation.',

  /* ── the Raise hiring window in ask mode. The form, its pickers and its checks are the same. ── */
  askTitleStore: (storeName: string, storeCode: string): string => `Ask for hiring for ${storeName}${storeCode ? ` (${storeCode})` : ''}`,
  askTitleRole: 'Ask for hiring for a role',
  askFootnote: 'Nothing is sent to Recruitment from this window.',
  askReviewHeading: 'What Recruitment will do when this is confirmed',
  /** Used only when the review came without its own buttons: the server writes these. */
  askYes: 'Send for confirmation',
  askNo: 'No, go back',
  askReviewFootnote: 'Nothing is sent to Recruitment now. A person who may raise hiring for this project confirms it first.',
  sending: 'Sending…',
  sentHeading: 'Sent for confirmation',
  /** No answer came back: the one thing that must not happen now is a second hiring request. */
  lostAnswer:
    'ChannelPlex did not get an answer, so it does not know yet whether your hiring request was saved. Do not start a new one.',
  lostAnswerNext: 'Ask again repeats this same request. It is never saved twice.',
  askAgain: 'Ask again',
  /** A refusal that came with no sentence of its own. Nothing was saved, and nothing was sent. */
  askRefusedPlain: 'This hiring request was not saved. Nothing was sent to Recruitment.',

  /* ── a hiring request, wherever it is shown ── */
  rowLine: 'Hiring request waiting for confirmation',
  windowTitle: (request: Pick<HiringRequest, 'subject'>): string =>
    (request.subject.kind === 'store'
      ? `Hiring request for ${plainSubjectName(request)}${request.subject.storeName && request.subject.storeCode ? ` (${request.subject.storeCode})` : ''}`
      : `Hiring request for ${plainSubjectName(request)} (no store)`),
  factsLabel: 'What was asked',
  askedBy: (request: Pick<HiringRequest, 'requestedBy' | 'requestedAt' | 'mine'>): string =>
    `Asked by ${request.mine ? 'you' : request.requestedBy} on ${formatHiringMoment(request.requestedAt)}`,
  budget: (name: string): string => `Budget: ${name}`,
  reason: (request: Pick<HiringRequest, 'reason' | 'replacesEmpCode'>): string => `Reason: ${requestReasonLine(request)}`,
  standingLabel: 'Where this hiring request stands',
  notesLabel: 'What stands in its way',
  open: 'Open hiring request',
  openFor: (name: string): string => `Open hiring request for ${name}`,
  close: 'Close',

  /* ── the hiring request window, for a person who may raise hiring ── */
  reviewHeading: 'What Recruitment will do',
  yes: 'Yes, raise hiring',
  /** Under the confirm: whose name the create goes out in, and who stays on record as having asked. */
  confirmFootnote: (requestedBy: string): string =>
    `Sent to Recruitment in your name. ${requestedBy} is recorded as the person who asked.`,
  /**
   * After the server's own sentence, when the fresh review refuses for something a form
   * would have answered (a tick, a city, an address): there is no form here to change.
   * Said ONLY for those refusals. One that says to wait, or that the budget or Recruitment
   * is in the way, would refuse a raise made by hand as well, so nothing is added to it.
   * A store's hiring is raised from the Stores tab; a manager role's, on the Hiring tab.
   */
  cannotChange: (subjectKind: 'store' | 'role'): string =>
    (subjectKind === 'role'
      ? `A hiring request cannot be changed here. Decline it with a reason, or raise this hiring yourself with "${RAISE_WORDS.panelButton}" on Dashboard > Hiring.`
      : 'A hiring request cannot be changed here. Decline it with a reason, or raise this hiring yourself from the Stores tab.'),
  reviewFailed: 'Could not check this with Recruitment. Nothing was sent. Try again.',
  reviewUnreadable: 'ChannelPlex could not read what Recruitment would do. Nothing was sent. Try again.',
  reviewAgain: 'Review again',
  /** A refusal that says the request is gone, when it came with no sentence of its own. */
  noLongerWaiting: 'This hiring request is no longer waiting for confirmation.',

  decline: 'Decline',
  declineGroupLabel: 'Confirm declining this hiring request',
  declineQuestion: 'Why are you declining? The person who asked will see this.',
  declineHint: 'Do not write candidate names or phone numbers.',
  declineNo: 'No, keep it',
  declineYes: 'Yes, decline',
  declined: 'Declined. The person who asked will see your reason.',
  declineFailed: 'Could not decline this hiring request. Try again.',

  /* ── for the person who asked ── */
  withdraw: 'Withdraw hiring request',
  withdrawGroupLabel: 'Confirm withdrawing this hiring request',
  withdrawQuestion: 'Withdraw this hiring request? It will not be raised.',
  withdrawNo: 'No, keep it',
  withdrawYes: 'Yes, withdraw',
  withdrawn: 'Withdrawn.',
  withdrawFailed: 'Could not withdraw this hiring request. Try again.',

  /* ── the Hiring tab ── */
  waitingFilter: (waiting: number): string => (waiting > 0 ? `Waiting for confirmation (${waiting})` : 'Waiting for confirmation'),
  pastFilter: 'Past hiring requests',
  /** Said above the table to a person who may raise hiring, while another filter is chosen. */
  waitingBanner: (waiting: number): string =>
    (waiting === 1 ? '1 hiring request is waiting for confirmation.' : `${waiting} hiring requests are waiting for confirmation.`),
  showThem: 'Show them',
  noneWaiting: 'No hiring request is waiting for confirmation.',
  nonePast: 'No past hiring requests.',
  listFailed: 'Could not load the hiring requests.',

  /* ── the timeline of an opening that was raised from one ── */
  timelineTitle: 'Raised from a hiring request',
} as const;

/** How long a request has waited, once the server says it is old: "Waiting 3 days". Null before that. */
export const requestWaitingTag = (request: Pick<HiringRequest, 'old' | 'waitingDays' | 'standing'>): string | null =>
  (request.standing === 'waiting' && request.old && typeof request.waitingDays === 'number'
    ? `Waiting ${counted(request.waitingDays, 'day')}`
    : null);

/**
 * The line under a store's Hiring pill while a hiring request for it waits: "Hiring request
 * waiting for confirmation", and "· 3 days" once it is old. Null for a request that no
 * longer waits.
 */
export const requestRowLine = (request: Pick<HiringRequest, 'old' | 'waitingDays' | 'standing'> | null | undefined): string | null => {
  if (!request || request.standing !== 'waiting') return null;
  return request.old && typeof request.waitingDays === 'number'
    ? `${REQUEST_WORDS.rowLine} · ${counted(request.waitingDays, 'day')}`
    : REQUEST_WORDS.rowLine;
};

const REQUEST_PILLS: Record<HiringRequestStanding, HiringPill> = {
  waiting: { text: 'Waiting for confirmation', className: 'border-amber-200 bg-amber-100 text-amber-800' },
  sent: { text: 'Sent to Recruitment', className: 'border-violet-200 bg-violet-100 text-violet-800' },
  raised: { text: 'Raised', className: 'border-green-200 bg-green-100 text-green-800' },
  not_created: { text: NOT_CREATED, className: 'border-gray-200 bg-gray-100 text-gray-500' },
  declined: { text: 'Declined', className: 'border-rose-200 bg-rose-100 text-rose-800' },
  withdrawn: { text: 'Withdrawn', className: 'border-gray-200 bg-gray-100 text-gray-500' },
};

/** Every standing a hiring request can be in. Held to the API's own list by the contract test. */
export const REQUEST_STANDINGS = Object.keys(REQUEST_PILLS) as HiringRequestStanding[];

/** A hiring request's pill. A standing this screen does not know reads as waiting: the safe reading. */
export const requestPill = (request: Pick<HiringRequest, 'standing'>): HiringPill =>
  REQUEST_PILLS[request.standing] ?? REQUEST_PILLS.waiting;

/** The server's sentence for where a request stands, with its moments in India time. */
export const requestLine = (request: Pick<HiringRequest, 'line'>): string => localiseRaiseMessage(request.line);

/** What the person who asked ticked on the form, in words; null when they ticked nothing. */
export const requestTicksLine = (ticks: HiringRequest['ticks'] | null | undefined): string | null => {
  const said: string[] = [];
  if (ticks?.additional === true) said.push('one more person is needed');
  if (ticks?.status === true) said.push('raise although the store’s staffing status says not to');
  if (ticks?.noRecord === true) said.push('the person worked at this store');
  return said.length > 0 ? `Ticked by the person who asked: ${said.join(', ')}.` : null;
};

/** "910001 · 13 Oct 2026": who asked, and the day. For a row of the Hiring tab. */
export const requestAskedByShort = (request: Pick<HiringRequest, 'requestedBy' | 'requestedAt'>): string =>
  `${request.requestedBy} · ${formatHiringDay(request.requestedAt)}`;

/** Whether a store has a hiring request still waiting. While it has, no second one is offered for that store (since 15 Oct 2026 several requests with no store may wait under one budget). */
export const hasRequestWaiting = (requests: readonly Pick<HiringRequest, 'standing'>[] | null | undefined): boolean =>
  (requests ?? []).some((request) => request.standing === 'waiting');

/**
 * The standing a refusal says a request is in now ("already confirmed", "declined",
 * "withdrawn"): the server sends it with REQUEST_NOT_WAITING. Null for any other refusal.
 */
export const standingAfterRefusal = (err: unknown): HiringRequestStanding | null => {
  if (hiringCodeOf(err) !== REQUEST_CODES.notWaiting) return null;
  const standing = hiringDetailOf(err).standing;
  return typeof standing === 'string' && (REQUEST_STANDINGS as string[]).includes(standing) ? (standing as HiringRequestStanding) : null;
};

/** The kinds of timeline entry a hiring request writes. Only the last is on an opening's own timeline. */
export const REQUEST_EVENT_TYPES: readonly string[] = ['request_made', 'request_confirmed', 'request_declined', 'request_withdrawn'];

/**
 * One entry of a hiring request on a timeline, in words; null for any other kind of entry.
 * An opening's timeline holds `request_confirmed`: it was raised from a hiring request,
 * and the line under it (the entry's own actor) is the person who confirmed it.
 */
export const requestTimelineLine = (event: HiringTimelineEvent): TimelineLine | null => {
  const detail = event.detail ?? {};
  const by = textOf(detail.requestedBy);
  switch (event.type) {
    case 'request_confirmed': {
      const when = textOf(detail.requestedAt);
      return {
        title: REQUEST_WORDS.timelineTitle,
        details: by ? [`Asked by ${by}${when ? ` on ${formatHiringMoment(when)}` : ''}`] : [],
        note: null,
      };
    }
    case 'request_made': {
      const budget = textOf(detail.budgetName);
      const reason = textOf(detail.reason);
      const replaces = textOf(detail.replacesEmpCode);
      const ticked = requestTicksLine(
        detail.confirmed && typeof detail.confirmed === 'object' ? (detail.confirmed as HiringRequest['ticks']) : null,
      );
      return {
        title: `Hiring request sent for confirmation${budget ? ` · ${budget}` : ''}`,
        details: [reason ? `Reason: ${requestReasonLine({ reason, replacesEmpCode: replaces })}` : null, ticked].filter((line): line is string => !!line),
        note: textOf(detail.note),
      };
    }
    case 'request_declined':
      return { title: 'Hiring request declined', details: by ? [`Asked by ${by}`] : [], note: textOf(detail.reason) };
    case 'request_withdrawn':
      return { title: 'Hiring request withdrawn', details: [], note: null };
    default:
      return null;
  }
};

/* ─────────────────────────────── messages from Recruitment ─────────────────────────────── */

// Recruitment tells ChannelPlex in real time when an opening or its candidates change. A
// change only a person can judge - the address changed, the positions changed, the opening
// was deleted or closed - is left on the link as a question: it shows under "Needs
// attention" on the Hiring tab, and the hiring panel asks it, with what changed and what
// can be done. Every word of that is here. The person named on a change is the employee
// code Recruitment recorded: never a name, and it can be missing or out of date.

/** The refusal codes of an answer that the screens act on. */
export const ATTENTION_CODES = {
  notOpen: 'ATTENTION_NOT_OPEN',
  changed: 'ATTENTION_CHANGED',
  noPositionLeft: 'NO_POSITION_LEFT',
} as const;

export const ATTENTION_WORDS = {
  /** The Hiring tab's filter choice, with how many there are once the list has said. */
  filter: (count: number | null | undefined): string => (typeof count === 'number' && count > 0 ? `Needs attention (${count})` : 'Needs attention'),
  /** The line above the list while another filter is on. */
  banner: (count: number): string => (count === 1 ? '1 opening needs attention.' : `${count} openings need attention.`),
  showThem: 'Show them',
  none: 'Nothing needs attention.',
  /**
   * The same, while the list is narrowed to stores or to manager roles: "nothing" would be
   * untrue of the project, and would sit beside a count that says otherwise.
   */
  noneOf: (subject: 'store' | 'role'): string => (subject === 'store' ? 'No store needs attention.' : 'No manager role needs attention.'),
  footnote: 'The person named on a change is the one Recruitment recorded. It can be missing or out of date.',
  /** Added to a store's line on the Stores tab. */
  storeLine: 'needs attention',
  cannotAnswer: 'Someone with Hiring (manage) can answer this.',
  stopFollowing: 'Stop following',
  /**
   * What the box's "Close" is called to a screen reader: the hiring panel has a "Close" of
   * its own in its footer, and the two must not be one name. It still begins with the word on the button.
   */
  closeName: 'Close this hiring link',
  answerFailed: 'Could not save your answer. Try again.',
  closedNote: 'Nobody is being hired on this opening now. ChannelPlex does not raise hiring again by itself.',
} as const;

/** The three questions, by the word the server sends. Held to the API's list by the contract test. */
const ATTENTION_TITLES: Record<HiringAttentionItem['reason'], string> = {
  address_changed: 'Address changed in Recruitment',
  positions_changed: 'Positions changed in Recruitment',
  closed_in_recruitment: 'Opening closed in Recruitment',
};
export const ATTENTION_REASONS = Object.keys(ATTENTION_TITLES) as HiringAttentionItem['reason'][];

/** A calendar day ("2031-03-20") as "20 Mar 2031". */
const dayWords = (day: string | null | undefined): string | null => (day ? formatHiringDay(day) : null);

/** "12 → 2", or the new number alone when the old one is not known. */
const fromTo = (from: unknown, to: unknown): string | null => {
  if (typeof to !== 'number') return null;
  return typeof from === 'number' ? `${from} → ${to}` : String(to);
};

/** A link's subject, as a sentence names it: "Sample Store 01", "the Area Manager role". */
export const attentionSubjectWords = (link: Pick<HiringLink, 'subject'>): string =>
  (link.subject.kind === 'store'
    ? link.subject.storeName || link.subject.storeCode || 'this store'
    : `${link.subject.roleLabel || 'this budget'} with no store`);

/**
 * When a change happened, as the server says it: a moment - or a calendar day alone
 * ("2031-03-14") when Recruitment recorded only the day an opening was closed. A day is
 * printed as a day: no time of day is made up for it.
 */
const whenWords = (at: string): string => (/^\d{4}-\d{2}-\d{2}$/.test(at) ? formatHiringDay(at) : formatHiringMoment(at));

/** The title of a question: a delete says so. */
export const attentionTitle = (item: Pick<HiringAttentionItem, 'reason' | 'how'>): string =>
  (item.reason === 'closed_in_recruitment' && item.how === 'deleted' ? 'Opening deleted in Recruitment' : ATTENTION_TITLES[item.reason] ?? 'Changed in Recruitment');

/**
 * One open question on a row of the Hiring tab: what changed, then who and when.
 * "Address changed in Recruitment · by 910020 on 12 Oct 2026, 10:15 am".
 */
export const attentionRowLine = (item: HiringAttentionItem): string => {
  let what = attentionTitle(item);
  if (item.reason === 'positions_changed') {
    const p = item.positions;
    // A shared link accounts for one position whatever the opening has: "1 → 2" would set
    // unlike things side by side (and "1 → 1" says nothing). It says what is short instead.
    const numbers = p && typeof p.linkedStores === 'number' && typeof p.now === 'number'
      ? `${counted(p.now, 'position')} for ${counted(p.linkedStores, 'store')}`
      : fromTo(p?.was, p?.now);
    if (numbers) what = `${what}: ${numbers}`;
  }
  if (item.reason === 'closed_in_recruitment' && item.how === 'closed_on_exit') what = `${what}: the person who joined has left`;
  if (!item.at) return item.byEmpCode ? `${what} · by ${item.byEmpCode}` : what;
  const when = whenWords(item.at);
  return `${what} · ${item.byEmpCode ? `by ${item.byEmpCode}` : 'in Recruitment'} on ${when}`;
};

/** Every open question of a link, one line each, for its row on the Hiring tab. */
export const attentionRowLines = (link: Pick<HiringLink, 'state' | 'attentionItems'>): string[] =>
  (link.state === 'live' ? (link.attentionItems ?? []).map(attentionRowLine) : []);

/** "Changed by 910020 on 12 Oct 2026, 10:15 am" / "Changed in Recruitment on …"; null when neither is known. */
const whoWhenLine = (verb: string, item: Pick<HiringAttentionItem, 'at' | 'byEmpCode'>): string | null => {
  const when = item.at ? ` on ${whenWords(item.at)}` : '';
  if (item.byEmpCode) return `${verb} by ${item.byEmpCode}${when}`;
  return item.at ? `${verb} in Recruitment${when}` : null;
};

export interface AttentionBox {
  title: string;
  /** What changed, then who and when. */
  lines: string[];
  /** What the person is asked; null where the box only states. */
  question: string | null;
  /** The answers offered, in order. Empty for someone who may not answer, and when nothing is left to keep. */
  buttons: { choice: HiringAttentionItem['choices'][number]; label: string; primary: boolean }[];
  /** Whether "Stop following" is offered beside them (the link's own stop, unchanged). */
  stop: boolean;
  /** What each answer does, under the buttons. Null when there are none. */
  footnote: string | null;
}

/**
 * One open question, as the hiring panel asks it. `canManage`: this person may answer;
 * `canRaiseAgain`: the screen has a Raise hiring window to open for this store or role
 * (the server's `choices` already say whether this person may raise).
 */
export const attentionBox = (
  item: HiringAttentionItem,
  link: Pick<HiringLink, 'subject' | 'shared'>,
  opts: { canManage: boolean; canRaiseAgain: boolean },
): AttentionBox => {
  const subject = attentionSubjectWords(link);
  const role = link.subject.kind === 'role';
  const may = (choice: HiringAttentionItem['choices'][number]): boolean => opts.canManage && item.choices.includes(choice);
  const lines: string[] = [];
  const add = (line: string | null): void => { if (line) lines.push(line); };

  if (item.reason === 'address_changed') {
    const was = addressText(item.address?.was);
    const now = addressText(item.address?.now);
    if (item.address?.onlyMapMoved) add('The map location was changed. The address text is the same.');
    else {
      add(was ? `When linked: ${was}` : null);
      add(now ? `Now: ${now}` : null);
    }
    add(whoWhenLine('Changed', item));
    const keep = role ? 'Still this budget' : 'Still this store';
    const buttons = may('keep') ? [{ choice: 'keep' as const, label: keep, primary: true }] : [];
    return {
      title: attentionTitle(item),
      lines,
      question: `Is this opening still for ${subject}?`,
      buttons,
      stop: opts.canManage,
      footnote: buttons.length > 0
        ? `${keep} keeps the hiring here and accepts the new address. Stop following sends nothing to Recruitment; the hiring carries on there.`
        : null,
    };
  }

  if (item.reason === 'positions_changed') {
    const now = item.positions?.now;
    const none = typeof now !== 'number' || now < 1;
    if (link.shared && typeof now === 'number' && typeof item.positions?.linkedStores === 'number') {
      add(`This shared opening now has ${counted(now, 'position')} and ${counted(item.positions.linkedStores, 'store')} ${item.positions.linkedStores === 1 ? 'is' : 'are'} linked to it.`);
    } else if (none) {
      add('This opening has no position left in Recruitment.');
    } else if (typeof item.positions?.was === 'number') {
      add(`When linked: ${item.positions.was} · Now: ${now}`);
    }
    add(whoWhenLine('Changed', item));
    const keep = link.shared ? 'Keep this store on it' : 'Keep following';
    const buttons = may('keep') ? [{ choice: 'keep' as const, label: keep, primary: true }] : [];
    return {
      title: attentionTitle(item),
      lines,
      question: null,
      buttons,
      stop: opts.canManage,
      footnote: buttons.length > 0 ? (link.shared ? 'Keep this store on it leaves this store linked to the shared opening.' : 'Keep following accepts the new number.') : null,
    };
  }

  // Deleted, or closed there.
  if (item.how === 'deleted') add(whoWhenLine('Deleted', item));
  else if (item.how === 'closed_on_exit') add(item.at ? `Closed in Recruitment on ${formatHiringDay(item.at)}, because the person who joined on it has left.` : 'Closed in Recruitment, because the person who joined on it has left.');
  else add(item.at ? `Closed in Recruitment on ${formatHiringDay(item.at)}.` : null);
  add(ATTENTION_WORDS.closedNote);
  const again = may('raise_again') && opts.canRaiseAgain;
  const buttons = [
    ...(again ? [{ choice: 'raise_again' as const, label: 'Raise again', primary: true }] : []),
    ...(may('close') ? [{ choice: 'close' as const, label: 'Close', primary: !again }] : []),
  ];
  // What is certain of a close: this opening is no longer followed. Whether the store or
  // role then shows "No hiring" is not: it may have another opening being followed.
  const ends = 'this opening is then no longer followed';
  return {
    title: attentionTitle(item),
    lines,
    question: null,
    buttons,
    stop: false,
    footnote: buttons.length === 0 ? null
      : again ? `Raise again closes this hiring link and opens Raise hiring for ${subject}. Close ends it; ${ends}.`
      // This person may raise, and this screen has no Raise hiring window for a store: it says where one is.
      : may('raise_again') && !role ? `Close ends this hiring link; ${ends}. To raise hiring again for it, use Raise hiring on the Stores tab.`
      : `Close ends this hiring link; ${ends}.`,
  };
};

/** What is said once an answer went through. */
export const attentionAnswered = (
  reason: HiringAttentionItem['reason'],
  result: 'kept' | 'closed',
  link: Pick<HiringLink, 'subject' | 'shared' | 'positions'>,
): string => {
  // Said of the opening, not of the store or role: another opening may still be followed for it.
  if (result === 'closed') return 'Closed. This opening is no longer followed.';
  if (reason === 'address_changed') return 'Kept. This opening is now checked against the new address.';
  if (link.shared) return 'Kept. This store stays on the shared opening.';
  return `Kept. This opening is now followed with ${counted(link.positions, 'position')}.`;
};

/** The questions of a link that the phase 1 boxes ("changed in Recruitment", from the live read alone) would repeat. */
export const attentionAsks = (link: Pick<HiringLink, 'state' | 'attentionItems'>, reason: HiringAttentionItem['reason']): boolean =>
  link.state === 'live' && (link.attentionItems ?? []).some((item) => item.reason === reason);

/** "offered · joining 20 Mar 2031": an offer is out and Recruitment said when the person is expected. Null otherwise. */
export const offerJoiningWords = (link: Pick<HiringLink, 'state' | 'status' | 'numbers' | 'offerJoiningDate'>): string | null => {
  const day = dayWords(link.offerJoiningDate);
  return link.state === 'live' && link.status === 'shortlisted_offered' && offerIsOut(link.numbers) && day ? `offered · joining ${day}` : null;
};

/**
 * What a card says under its pill when a message from Recruitment told more than the
 * numbers do: the expected joining date of an offer, or who joined. Null: the card says
 * what it always said (statusSentence).
 */
export const messageStatusSentence = (link: HiringLink): string | null => {
  if (link.state !== 'live') return null;
  if (link.status === 'shortlisted_offered' && offerIsOut(link.numbers) && link.offerJoiningDate) {
    return `An offer has been made on this opening. Expected joining: ${dayWords(link.offerJoiningDate)}.`;
  }
  if (link.status === 'joined_not_placed' && link.joined?.empCode) {
    const on = dayWords(link.joined.on);
    return on ? `${link.joined.empCode} joined on this opening on ${on}.` : `${link.joined.empCode} joined on this opening.`;
  }
  return null;
};

/** The six counts a card shows, from a timeline entry's counts; the stages with nobody are left out. */
const countsWords = (stages: unknown): string | null => {
  const s = (stages && typeof stages === 'object' ? stages : {}) as Record<string, unknown>;
  const said = CANDIDATE_COUNTS.filter(({ key }) => typeof s[key] === 'number' && (s[key] as number) > 0).map(({ key, label }) => `${label} ${s[key]}`);
  return said.length > 0 ? said.join(' · ') : null;
};

/** The kinds of timeline entry a message from Recruitment writes. Held to the API's list by the contract test. */
export const RECRUITMENT_EVENT_TYPES: readonly string[] = [
  'recruitment_not_listed', 'recruitment_deleted', 'recruitment_closed_on_exit', 'recruitment_reopened', 'recruitment_positions_changed',
  'recruitment_address_changed', 'recruitment_counts_changed', 'recruitment_offer_made', 'recruitment_offer_withdrawn',
  'recruitment_not_joined', 'recruitment_joined',
];

/** How a person, or a later message, ended a question. Held to the API's list by the contract test. */
export const ATTENTION_ANSWER_RESULTS: readonly string[] = ['kept', 'closed', 'resolved_in_recruitment'];

const SEEN_LATER = 'ChannelPlex saw this in a later message from Recruitment.';

/**
 * One entry a message from Recruitment wrote on a timeline, or the answer to a question
 * one of them opened, in words. Null for any other entry. Counts, numbers, addresses and
 * employee codes only.
 */
export const recruitmentTimelineLine = (event: HiringTimelineEvent, subjectKind: 'store' | 'role' = 'store'): TimelineLine | null => {
  const detail = event.detail ?? {};
  const by = textOf(detail.byEmpCode);
  const byWords = by ? ` by ${by}` : '';
  const details = detail.inferred === true ? [SEEN_LATER] : [];
  const line = (title: string): TimelineLine => ({ title, details, note: null });
  // The day Recruitment recorded an opening as closed, when it gave a day and no time of day.
  const closedOn = dayWords(textOf(detail.on));
  const onWords = closedOn ? ` on ${closedOn}` : '';
  switch (event.type) {
    case 'recruitment_counts_changed': {
      const counts = countsWords(detail.stages);
      return line(counts ? `Candidates now: ${counts}` : 'Candidates now: none in process');
    }
    case 'recruitment_offer_made': {
      const day = dayWords(textOf(detail.joiningDate));
      return line(day ? `Offer made in Recruitment. Expected joining: ${day}.` : 'Offer made in Recruitment.');
    }
    case 'recruitment_offer_withdrawn': return line('Offer withdrawn in Recruitment.');
    case 'recruitment_not_joined': return line('The person offered did not join.');
    case 'recruitment_joined': {
      const who = textOf(detail.empCode);
      const day = dayWords(textOf(detail.on));
      if (!who) return line(day ? `Someone joined on this opening on ${day}.` : 'Someone joined on this opening.');
      return line(day ? `${who} joined on ${day}.` : `${who} joined on this opening.`);
    }
    case 'recruitment_deleted': return line(`Opening deleted in Recruitment${byWords}${onWords}.`);
    case 'recruitment_closed_on_exit': return line(`Recruitment closed this opening${onWords} because the person who joined on it left.`);
    case 'recruitment_not_listed': return line(`Opening closed in Recruitment${onWords}.`);
    case 'recruitment_reopened': return line(`Opening reopened in Recruitment${byWords}.`);
    case 'recruitment_positions_changed': {
      const numbers = fromTo(detail.from, detail.to);
      return line(numbers ? `Positions changed in Recruitment${byWords}: ${numbers}.` : `Positions changed in Recruitment${byWords}.`);
    }
    case 'recruitment_address_changed': {
      const from = addressText(detail.from as HiringAddressText | null);
      const to = addressText(detail.to as HiringAddressText | null);
      const moved = from && to ? `: ${from} → ${to}` : to ? `: now ${to}` : '';
      return line(`Address changed in Recruitment${byWords}${moved}.`);
    }
    case 'attention_answered': {
      // Only the answers to a question a message opened: a raise's own ("everyone is on the opening") is not one of these.
      const result = textOf(detail.result);
      if (!result || !ATTENTION_ANSWER_RESULTS.includes(result)) return null;
      const who = event.actor ?? 'Someone';
      const reasons = Array.isArray(detail.reasons) ? detail.reasons : [];
      if (result === 'resolved_in_recruitment') return { title: 'No longer needs an answer: it was changed back in Recruitment.', details: [], note: null };
      if (result === 'closed') {
        return { title: detail.raiseAgain === true ? `${who} closed this hiring link to raise hiring again.` : `${who} closed this hiring link.`, details: [], note: null };
      }
      if (reasons.includes('address_changed')) {
        return { title: `${who}: still this ${subjectKind === 'role' ? 'budget' : 'store'}. The new address was accepted.`, details: [], note: null };
      }
      return {
        title: typeof detail.positions === 'number' ? `${who}: keep following with ${counted(detail.positions, 'position')}.` : `${who}: keep following.`,
        details: [],
        note: null,
      };
    }
    default: return null;
  }
};

/** The one line of the Hiring card (super admins) about Recruitment's real-time messages. */
export const messagesLine = (status: HiringMessagesStatus | null | undefined): string | null => {
  if (!status) return null;
  if (status.stopped) return 'Messages from Recruitment are stopped on this server.';
  if (!status.accepting) {
    return 'Real-time messages from Recruitment are not set up on this server. Changes made in Recruitment show when someone opens the Hiring list.';
  }
  return status.lastReceivedAt
    ? `Recruitment’s messages are arriving. Last one: ${formatHiringMoment(status.lastReceivedAt)}.`
    : 'Ready to receive Recruitment’s messages. None has arrived yet.';
};

/* ─────────────────────────────── a new joiner and the store; people leaving a store ─────────────────────────────── */

// Two boxes of the hiring panel that are about PEOPLE - employees, never candidates.
// "Who joined": when Recruitment's numbers show a joining on a store's opening, the people
// of the employee list who may be that joiner, each with how sure the match is, and the
// two clicks that place one of them at the store and close the hiring as filled.
// "People leaving this store": who has a resignation on record whose exit has not been
// started, and who is on notice. Every word of both is here; the sentence over a list of
// joiners, and every refusal, is the server's own.
//
// (The types are imported here, with the block, so the list at the top of the file stays as it was.)
import type { HiringJoiner, HiringJoinerMatch, HiringLeavingPerson, HiringStoreLeaving } from '../../services/api';

/** The refusal codes of a placing that the screens act on. Held to the API's list by the contract test. */
export const PEOPLE_CODES = {
  storeNotYours: 'STORE_NOT_YOURS',
  notAStoreOpening: 'NOT_A_STORE_OPENING',
  nobodyJoined: 'NOBODY_JOINED',
  notAMatch: 'NOT_A_MATCH',
  joinersChanged: 'JOINERS_CHANGED',
  rosterUnavailable: 'ROSTER_UNAVAILABLE',
  placeFailed: 'PLACE_FAILED',
} as const;

/** "910245 · Invented Name": an employee, as these two boxes name one. */
export const personWords = (person: { empCode: string; name?: string | null }): string =>
  (person.name && person.name.trim() ? `${person.empCode} · ${person.name.trim()}` : person.empCode);

/** "910245 Invented Name": the same person inside a sentence. */
const personInSentence = (person: { empCode: string; name?: string | null }): string =>
  (person.name && person.name.trim() ? `${person.empCode} ${person.name.trim()}` : person.empCode);

export const PEOPLE_WORDS = {
  // Who joined.
  joinerHeading: 'Who joined',
  joinerCannotPlace: 'Someone with Hiring (manage) who looks after this store can place the person who joined.',
  joinerLoading: 'Looking for the person who joined…',
  joinerFailed: 'Could not check who joined. Try again.',
  tryAgain: 'Try again',
  joinedOn: (day: string | null | undefined): string | null => (day ? `Joined on ${formatHiringDay(day)}` : null),
  notAtAnyStore: 'Not at any store yet',
  alreadyHere: 'Already at this store',
  place: 'Place at this store',
  closeAsFilled: 'Close as filled',
  placeQuestion: (person: { empCode: string; name?: string | null }, storeName: string): string =>
    `Place ${personInSentence(person)} at ${storeName}? They will show as deployed here from today, and this hiring will be closed as filled.`,
  closeQuestion: (person: { empCode: string; name?: string | null }): string =>
    `Close this hiring as filled by ${personInSentence(person)}? They are already at this store.`,
  goBack: 'No, go back',
  yesPlace: 'Yes, place at this store',
  yesClose: 'Yes, close as filled',
  nobodyHints: [
    'A new joiner can take up to an hour to show on the employee list.',
    'If the person joined on an opening with a different address, use Assign team on the Stores page, then stop following this opening.',
  ] as readonly string[],
  listFootnote: 'Only people who have joined are listed, from the employee list. Nothing is sent to Recruitment.',
  /** No answer at all came back from a placing. */
  placeNoAnswer: 'Could not place this person. Check the list again before you try once more.',
  // People leaving this store.
  leavingHeading: 'People leaving this store',
  leavingLoading: 'Checking for resignations…',
  leavingNone: 'No resignation is on record for the people at this store.',
  leavingNoDate: '1Office gives no date until HR starts the exit.',
  leavingNoIdentity: 'Your sign-in has no 1Office account, so only resignations filed from ChannelPlex are shown.',
  leavingUnavailable: '1Office could not be read just now, so only resignations filed from ChannelPlex are shown.',
  leavingPartial: 'Some of this store’s people could not be checked just now.',
  leavingFailed: 'Could not check for resignations. Try again.',
} as const;

/** How sure a match is, in the two places a person reads it: the tag beside the name and the sentence under it. */
const JOINER_MATCH_WORDS: Record<HiringJoinerMatch, { tag: string; sentence: string | null; timeline: string }> = {
  named: { tag: 'Named by Recruitment', sentence: null, timeline: 'Named by Recruitment' },
  store_tag: {
    tag: 'Raised for this store',
    sentence: 'The opening they joined on carries this store’s name and code.',
    timeline: 'Matched by the store’s name and code on the opening’s address',
  },
  same_address: {
    tag: 'Same address',
    sentence: 'They joined under the same budget, on an opening with this address. ChannelPlex cannot see the opening number, so check before placing.',
    timeline: 'Matched by the opening’s address',
  },
};
/** The kinds of match, by the word the server sends. Held to the API's list by the contract test. */
export const JOINER_MATCHES = Object.keys(JOINER_MATCH_WORDS) as HiringJoinerMatch[];

/** The tag and the sentence for how a person was matched. A kind this screen does not know says only that it is a match. */
export const joinerMatchWords = (match: string | null | undefined): { tag: string; sentence: string | null } => {
  const words = JOINER_MATCH_WORDS[match as HiringJoinerMatch];
  return words ? { tag: words.tag, sentence: words.sentence } : { tag: 'Matched', sentence: 'Check before placing.' };
};

const storeWords = (store: { storeName: string | null; storeCode: string | null }): string =>
  (store.storeName ? `${store.storeName}${store.storeCode ? ` (${store.storeCode})` : ''}` : store.storeCode ?? 'another store');

/** Where the person is deployed today, and what a placing would do about it. */
export const joinerWhereLine = (person: Pick<HiringJoiner, 'atThisStore' | 'otherStores' | 'keepsOtherStores'>): string => {
  if (person.atThisStore) return PEOPLE_WORDS.alreadyHere;
  const others = person.otherStores ?? [];
  if (others.length === 0) return PEOPLE_WORDS.notAtAnyStore;
  const names = others.map(storeWords).join(', ');
  if (person.keepsOtherStores) return `Now at ${names}. They keep ${others.length === 1 ? 'that store' : 'those stores'} too.`;
  return `Now at ${names}. Placing moves them here.`;
};

/** Where a person who is leaving stands, by the word the server sends. Held to the API's list by the contract test. */
export const LEAVING_STATES: readonly HiringLeavingPerson['state'][] = ['exit_not_started', 'on_notice', 'filed_here'];
/** How far 1Office was read for a list of people leaving. Held to the API's list by the contract test. */
export const LEAVING_READS: readonly HiringStoreLeaving['oneOffice'][] = ['read', 'partial', 'no_identity', 'unavailable'];

/**
 * What is said under the name of a person who is leaving. `oneOffice` is how far 1Office
 * was read for the list: a person with no 1Office account is not told it "could not be
 * read just now" - the box says, once, why only ChannelPlex's own filings are shown. Such
 * a filing always carries its caveat on the person's own line: ChannelPlex knows it was
 * filed, and not what 1Office made of it since (HR may have turned it down).
 */
export const leavingLines = (person: HiringLeavingPerson, oneOffice?: HiringStoreLeaving['oneOffice']): string[] => {
  const filed = person.filedFromChannelPlexOn ? formatHiringDay(person.filedFromChannelPlexOn) : null;
  if (person.state === 'exit_not_started') {
    return [
      'Leaving. The exit has not been started.',
      '1Office has a resignation, or an exit filed by HR, on record for this person.',
      ...(filed ? [`Filed from ChannelPlex (Start Exit) on ${filed}.`] : []),
    ];
  }
  if (person.state === 'on_notice') {
    return [
      person.lastWorkingDay ? `On notice. Last working day: ${formatHiringDay(person.lastWorkingDay)}.` : 'On notice.',
      ...(person.resignedOn ? [`Resigned on ${formatHiringDay(person.resignedOn)}.`] : []),
    ];
  }
  const filing = filed ? `A resignation was filed from ChannelPlex (Start Exit) on ${filed}.` : 'A resignation was filed from ChannelPlex (Start Exit).';
  return [oneOffice === 'no_identity'
    ? `${filing} ChannelPlex cannot see what 1Office made of it since.`
    : `${filing} 1Office could not be read just now to confirm it.`];
};

/** What the box says about how far 1Office was read, when that is not "all of it". Null: nothing to say. */
export const leavingReadLine = (answer: Pick<HiringStoreLeaving, 'oneOffice' | 'notChecked'>): string | null => {
  if (answer.oneOffice === 'no_identity') return PEOPLE_WORDS.leavingNoIdentity;
  if (answer.oneOffice === 'unavailable') return PEOPLE_WORDS.leavingUnavailable;
  if (answer.oneOffice === 'partial' || answer.notChecked > 0) return PEOPLE_WORDS.leavingPartial;
  return null;
};

/** Whether asking again could tell more: 1Office was not read in full for a reason that can pass. */
export const leavingCanRetry = (answer: Pick<HiringStoreLeaving, 'oneOffice' | 'notChecked'>): boolean =>
  answer.oneOffice === 'unavailable' || answer.oneOffice === 'partial' || (answer.oneOffice === 'read' && answer.notChecked > 0);

/** "Closed as filled by 910002 on 13 Oct 2026, 10:15 am": who closed a hiring as filled, and when. Null for any other link. */
export const filledByLine = (link: Pick<HiringLink, 'state' | 'closedAs' | 'closedBy' | 'closedAt'>): string | null => {
  if (link.state !== 'closed' || link.closedAs !== 'filled' || !link.closedAt) return null;
  const when = formatHiringMoment(link.closedAt);
  return link.closedBy ? `Closed as filled by ${link.closedBy} on ${when}` : `Closed as filled on ${when}`;
};

/** The two kinds of timeline entry a placing writes. Held to the API's list by the contract test. */
export const PEOPLE_EVENT_TYPES: readonly string[] = ['joiner_deployed', 'closed_filled'];

/**
 * One entry a placing wrote on a timeline, in words. Null for any other entry. Employee
 * codes, a date and fixed words only: a timeline keeps no name.
 */
export const peopleTimelineLine = (event: HiringTimelineEvent): TimelineLine | null => {
  const detail = event.detail ?? {};
  const who = textOf(detail.empCode) ?? 'The person who joined';
  if (event.type === 'closed_filled') return { title: `Hiring closed as filled by ${who}.`, details: [], note: null };
  if (event.type !== 'joiner_deployed') return null;
  const details: string[] = [];
  const joined = PEOPLE_WORDS.joinedOn(textOf(detail.dateOfJoining));
  if (joined) details.push(joined);
  const match = JOINER_MATCH_WORDS[textOf(detail.match) as HiringJoinerMatch];
  if (match) details.push(match.timeline);
  if (detail.alreadyAtStore === true) return { title: `${who} was already at this store.`, details, note: null };
  const moved = typeof detail.movedFromStores === 'number' ? detail.movedFromStores : 0;
  if (moved === 1) details.push('Moved from another store');
  if (moved > 1) details.push(`Moved from ${moved} other stores`);
  if (detail.keptOtherStores === true) details.push('They keep their other stores too');
  return { title: `${event.actor ?? 'Someone'} placed ${who} at this store.`, details, note: null };
};

/**
 * Does this card ask who joined? A live link of a store's own whose opening shows a
 * joining - now, or at the last good read when Recruitment cannot be read. Never a
 * manager role's, and never a shared opening's: the numbers of a shared opening say
 * nothing about one store among several.
 */
export const asksWhoJoined = (link: Pick<HiringLink, 'state' | 'shared' | 'subject' | 'status' | 'lastKnownStatus'>): boolean =>
  link.state === 'live' && !link.shared && link.subject.kind === 'store'
  && (link.status === 'joined_not_placed' || (link.status === 'numbers_unavailable' && link.lastKnownStatus === 'joined_not_placed'));

/* ═══════════════════════════════ Cancel: ChannelPlex asks Recruitment to delete an opening ═══════════════════════════════ */
//
// A cancel asks Recruitment to DELETE an opening ChannelPlex follows. Recruitment deletes
// it at once, in the name of the person who confirms, and then tells every candidate
// still in process that the position is closed. It cannot be undone. So what a person
// reads before they confirm, and after, is the server's own sentence wherever it wrote
// one: the review's title and lines, every refusal, every outcome, and the one line a
// list shows. What follows are the screens' own words around those, and the small rules
// that decide what is offered next.
//
// Two things are never offered. "Cancel again" while a cancel is open: it may already have
// deleted the opening, and only Recruitment's own list can say. And any way for a person
// who may not cancel to ask someone else to: there is no "ask to cancel".

/** The refusal codes a cancel adds. The screens act on a few; every other one only shows its sentence. */
export const CANCEL_CODES = {
  cancelNotEnabled: 'CANCEL_NOT_ENABLED',
  changeOffOnServer: 'CHANGE_OFF_ON_SERVER',
  sharedOpening: 'CANCEL_SHARED_OPENING',
  cancelInProgress: 'CANCEL_IN_PROGRESS',
  hasOffer: 'CANCEL_HAS_OFFER',
  hasJoiner: 'CANCEL_HAS_JOINER',
  linkNotCancelling: 'LINK_NOT_CANCELLING',
} as const;

/** Cancel was switched off, for the project or on the server, since the page asked. */
export const isCancelOff = (err: unknown): boolean =>
  hiringStatusOf(err) === 404 && hiringCodeOf(err) === CANCEL_CODES.cancelNotEnabled;

/** How short and how long a reason may be. The review's own answer says the same (`reason.min`, `reason.max`). */
export const CANCEL_REASON_MIN = 3;
export const CANCEL_REASON_MAX = 500;

export const CANCEL_WORDS = {
  /** The control on a card, beside "Stop following this opening". */
  button: 'Cancel opening',
  /** The two short lines under the two controls: what each one does, so neither is pressed for the other. */
  stopExplained: 'Stop following: nothing changes in Recruitment.',
  cancelExplained: 'Cancel opening: Recruitment deletes it and tells candidates in process.',
  /** A shared link, for a person who may cancel: why there is no Cancel control on it. */
  sharedLine: 'A shared opening serves several stores. It cannot be cancelled from here.',
  reading: 'Reading Recruitment…',
  /** The confirmation. Its title, its lines, the buttons and the line under them are the server's. */
  confirmGroupLabel: (openingId: number | null | undefined): string => `Confirm cancel of opening ${openingId ?? ''}`.trim(),
  reasonLabel: 'Why is this opening being cancelled?',
  reasonHelp: 'Required, 3 to 500 characters. It is kept on the timeline. Do not name candidates; long numbers are masked.',
  yes: 'Yes, cancel this opening',
  no: 'No, keep the opening',
  footnote: 'Sent to Recruitment in your name.',
  /** A review, or a confirm, the checks refused: the server's sentence, and this. */
  close: 'Close',
  reviewFailed: 'Could not check this with Recruitment. Nothing was sent. Try again.',
  /** Said, with a fresh review, when what was reviewed is no longer so. */
  reviewedAgain: 'Something changed since you reviewed this. Read it again before you confirm.',
  sending: 'Cancelling in Recruitment…',
  /** No answer came back from ChannelPlex's own server. The cancel may have gone out. */
  noAnswer: 'No answer came back. Do not cancel it again.',
  askAgain: 'Ask again',
  /** The box of a cancel Recruitment has not confirmed. */
  standingLabel: 'Where this cancel stands',
  sentOn: (moment: string): string => `Sent to Recruitment on ${moment}.`,
  checksByItself: 'ChannelPlex checks again by itself for the next minute.',
  checkAgain: 'Check again',
  checking: 'Checking Recruitment…',
  checkFailed: 'Could not check just now. Try again in a moment.',
  /** When the list said a cancel is open and sent no sentence of its own. */
  standing: 'A cancel of this opening was sent, and Recruitment has not confirmed it.',
  /**
   * "Stop following" came back with the link closed as CANCELLED: a cancel had been sent
   * for this opening, and when the server read Recruitment's list before letting go, the
   * opening was gone. Said in the card, so "Stop following" turning into "Cancelled" is explained.
   */
  stopFoundCancelled: (openingId: number | null | undefined): string =>
    `A cancel had been sent for ${openingId != null ? `Opening ${openingId}` : 'this opening'}, and Recruitment's list no longer shows it: it was deleted in Recruitment. Recruitment then marks its candidates in process as Position Closed. It sends the recruiters no mail.`,
  /** No answer came back from a confirm, and the page's list no longer holds the opening: what became of it is being read. */
  lostChecking: 'No answer came back. ChannelPlex is reading what became of this cancel…',
  /**
   * The same, when the server has no cancel on record for the opening: the confirm never
   * reached it, or was refused before anything was claimed. Nothing was sent. ("Ask again"
   * stays: pressed, it is that same confirm, and the server decides it afresh.)
   */
  lostNothingSent: 'ChannelPlex has no cancel on record for this opening, so nothing was sent to Recruitment for it.',
} as const;

/** The one line a list shows under the pill of a link whose cancel is open. The server's own words for it. */
export const CANCEL_SENDING = 'Cancelling in Recruitment…';
/** Nothing checks a cancel on a timer: the line sends the reader to the opening, where the check is. */
export const CANCEL_NOT_CONFIRMED = 'Recruitment did not confirm the cancel. Open it to check.';

/** A link a person stopped following. The one kind of closed link that can still carry an open cancel. */
export const isStoppedFollowing = (link: HiringLink | null | undefined): boolean =>
  !!link && link.state === 'closed' && link.closedAs === 'released';

/**
 * Whether a cancel of this link's opening is open, as the list says: on a link that is
 * still followed, or on one that was stopped following while its cancel could not be
 * settled (it can still be checked from its card).
 */
export const cancelIsOpen = (link: HiringLink | null | undefined): boolean =>
  !!link && link.cancel != null && (link.state === 'live' || isStoppedFollowing(link));

/** The short line under a pill: where an open cancel stands. Null for every link without one. */
export const cancelShortLine = (link: HiringLink): string | null => {
  if (!cancelIsOpen(link)) return null;
  const said = localiseRaiseMessage(link.cancel?.message);
  if (said) return said;
  return link.cancel?.sendStatus === 'claimed' ? CANCEL_SENDING : CANCEL_NOT_CONFIRMED;
};

/** Whether any of a store's or role's links has a cancel that has not ended. */
export const hasCancelOpen = (links: readonly HiringLink[] | null | undefined): boolean =>
  (links ?? []).some((link) => cancelIsOpen(link));

/** The sentence of the box for a cancel that is open: the last answer's, the list's, or the screen's own. */
export const cancelStandingSentence = (link: HiringLink): string =>
  localiseRaiseMessage(link.cancel?.message) || CANCEL_WORDS.standing;

/**
 * Whether a link moved on between two answers of a cancel: it closed, its cancel ended or
 * changed status, or its open questions changed. A check that found nothing new is not
 * worth re-reading a whole list for.
 */
export const cancelLinkMoved = (before: HiringLink, after: HiringLink): boolean =>
  before.state !== after.state
  || (before.closedAs ?? null) !== (after.closedAs ?? null)
  || (before.cancel?.sendId ?? null) !== (after.cancel?.sendId ?? null)
  || (before.cancel?.sendStatus ?? null) !== (after.cancel?.sendStatus ?? null)
  || (before.attention ?? []).join() !== (after.attention ?? []).join();

/**
 * How an outcome is shown, and what may be offered after it.
 *   cancelled  Recruitment's own list no longer shows the opening. The card becomes the
 *              closed card.
 *   waiting    Recruitment has not confirmed. NEVER offers "Cancel again": the cancel may
 *              have deleted the opening, and only its list can say.
 *   nothing    Nothing was deleted and the link is as it was; cancelling again is safe.
 * An outcome this screen does not know is "waiting": the safe reading.
 */
export type CancelOutcomeKind = 'cancelled' | 'waiting' | 'nothing';

const CANCEL_OUTCOME_KIND: Record<HiringCancelOutcome, CancelOutcomeKind> = {
  cancelled: 'cancelled',
  not_confirmed: 'waiting',
  in_progress: 'waiting',
  refused: 'nothing',
  not_sent: 'nothing',
  not_cancelled: 'nothing',
};

export const CANCEL_OUTCOMES = Object.keys(CANCEL_OUTCOME_KIND);

export const cancelOutcomeKind = (outcome: string | null | undefined): CancelOutcomeKind =>
  (outcome && CANCEL_OUTCOME_KIND[outcome as HiringCancelOutcome]) || 'waiting';

/**
 * What a refusal makes the step do. Every code of a cancel has one, so a new code the
 * server adds is caught by a test (the contract file lists them) and not by a person.
 *   control_goes   Cancel, or hiring itself, was switched off: the control leaves the card.
 *   review_again   what was reviewed is no longer so: a fresh review, with a new key.
 *   list_reread    the page's list is out of date (the link was stopped, cancelled, or a
 *                  cancel of it is already open): the owner reads it again.
 *   close          the sentence is shown, with "Close". Nothing was sent.
 */
export type CancelRefusalStep = 'control_goes' | 'review_again' | 'list_reread' | 'close';

/** The codes the cancel step acts on. Every other code of the API only shows its sentence, with "Close". */
const CANCEL_STEPS_ACTED_ON: Record<string, CancelRefusalStep> = {
  [CANCEL_CODES.cancelNotEnabled]: 'control_goes',
  [HIRING_CODES.notEnabled]: 'control_goes',
  [RAISE_CODES.previewStale]: 'review_again',
  [RAISE_CODES.requestKeyReused]: 'review_again',
  [HIRING_CODES.linkNotLive]: 'list_reread',
  [HIRING_CODES.linkNotFound]: 'list_reread',
  [CANCEL_CODES.cancelInProgress]: 'list_reread',
  [HIRING_CODES.notInBudget]: 'list_reread',
};

/**
 * ONE step for every refusal code the API has (the same list the Raise hiring window is
 * held to, which a test holds to the API's own). Nothing a cancel can be refused with is
 * left to a default.
 */
export const CANCEL_REFUSAL_STEPS: Record<string, CancelRefusalStep> = {
  ...Object.fromEntries(RAISE_REFUSAL_CODES.map((code): [string, CancelRefusalStep] => [code, 'close'])),
  ...CANCEL_STEPS_ACTED_ON,
};

/** What a refused review or confirm makes the step do. A code this screen does not know only shows its sentence. */
export const cancelRefusalStep = (err: unknown): CancelRefusalStep => {
  const code = hiringCodeOf(err);
  return (code && CANCEL_REFUSAL_STEPS[code]) || 'close';
};

/**
 * What a refused cancel call says: the server's own sentence, with its moments in India
 * time. A cancel's refusals say what was being checked and whether anything was sent, and
 * that must not be lost to a shorter wording.
 */
export const cancelErrorText = (err: unknown, fallback: string): string => raiseErrorText(err, fallback);

/**
 * The key of one confirm of a cancel. Made once, when the review arrives, and sent with
 * every repeat of that confirm: the server answers a repeat from the first request, so one
 * review can never delete twice - and a key names one confirm of one review.
 */
export const makeCancelRequestKey = (): string => makeRaiseRequestKey();

/** Whether what was typed can be sent as the reason: 3 to 500 characters once trimmed. The server has the last word. */
export const cancelReasonOk = (typed: string, limits?: { min?: number; max?: number } | null): boolean => {
  const length = typed.trim().length;
  const min = typeof limits?.min === 'number' ? limits.min : CANCEL_REASON_MIN;
  const max = typeof limits?.max === 'number' ? limits.max : CANCEL_REASON_MAX;
  return length >= min && length <= max;
};

/* ── the timeline of a cancel ── */

/** Said on "Stopped following …" when a cancel of that opening was still open at the time. */
export const RELEASED_WITH_CANCEL_OPEN = 'A cancel Recruitment had not confirmed was still open.';

const CANCEL_REFUSED_WORDS: Record<string, string> = {
  offered: 'A candidate has an offer.',
  joined: 'Someone has joined.',
  not_found: 'Recruitment has no such opening.',
  sign_in: 'The 1Office sign-in was not accepted.',
  forbidden: 'Not allowed for that 1Office account.',
  rejected: 'Rejected before anything was deleted.',
};
export const CANCEL_REFUSED_KINDS = [...Object.keys(CANCEL_REFUSED_WORDS), 'recruitment'];

const CANCEL_UNKNOWN_WORDS: Record<string, string> = {
  no_answer: 'No answer came back.',
  server_error: 'Recruitment answered with an error.',
  unreadable_success: 'Its answer could not be read.',
  unexpected_status: 'Its answer could not be read.',
  still_listed: 'Its list still showed the opening.',
  not_read: 'Its list could not be read.',
  restart: 'A server stopped while it was being sent.',
};
export const CANCEL_UNKNOWN_WHY = Object.keys(CANCEL_UNKNOWN_WORDS);

// A raise's own words for "not sent", except the one that names Raise.
const CANCEL_NOT_SENT_WORDS: Record<string, string> = {
  ...NOT_SENT_WORDS,
  switched_off: 'Cancelling was switched off just before it was sent.',
};
const CANCEL_NOT_SENT_OTHER = 'ChannelPlex could not start the cancel.';

/** The kinds of timeline entry a cancel writes. */
export const CANCEL_EVENT_TYPES: readonly string[] = [
  'cancel_claimed', 'cancel_not_sent', 'cancel_refused', 'cancel_unknown', 'cancelled', 'cancel_not_applied',
];

/** One entry of a cancel's timeline in words; null for any other kind of entry. Of candidates there is a count, never who. */
export const cancelTimelineLine = (event: HiringTimelineEvent): TimelineLine | null => {
  const detail = event.detail ?? {};
  const opening = event.openingId ?? (typeof detail.openingId === 'number' ? detail.openingId : null);
  const openingText = opening != null ? `opening ${opening}` : 'an opening';
  // The reason a person gave is the entry's note, exactly as the server kept it (long numbers already masked).
  const reason = textOf(detail.reason);
  const lines = (...parts: (string | null)[]): string[] => parts.filter((part): part is string => !!part);
  switch (event.type) {
    case 'cancel_claimed':
      return {
        title: `Cancel sent to Recruitment for ${openingText}`,
        details: lines(typeof detail.inProcess === 'number' ? `Candidates in process: ${detail.inProcess}` : null),
        note: reason,
      };
    case 'cancelled':
      return {
        title: `${opening != null ? `Opening ${opening}` : 'The opening'} was deleted in Recruitment`,
        details: lines(
          detail.by === 'read_back' ? 'Confirmed by reading Recruitment’s list.' : null,
          detail.linkAlreadyClosed === true ? 'It was no longer followed here by then.' : null,
        ),
        note: reason,
      };
    case 'cancel_refused': {
      const said = textOf(detail.text);
      const why = CANCEL_REFUSED_WORDS[String(detail.kind)] ?? (said ? `Recruitment said: “${said}”` : null);
      return { title: 'Recruitment refused the cancel', details: lines(why), note: null };
    }
    case 'cancel_unknown':
      return { title: 'Recruitment did not confirm the cancel', details: lines(CANCEL_UNKNOWN_WORDS[String(detail.why)] ?? null), note: null };
    case 'cancel_not_sent':
      return { title: 'The cancel was not sent', details: [CANCEL_NOT_SENT_WORDS[String(detail.why)] ?? CANCEL_NOT_SENT_OTHER], note: null };
    case 'cancel_not_applied':
      return {
        title: `Not cancelled: Recruitment still lists ${openingText}`,
        details: lines(detail.answeredDeleted === true ? 'Recruitment had answered that it was deleted.' : null),
        note: null,
      };
    default:
      return null;
  }
};

/** The ways a followed link ends, as a closed link's `closedAs` says them. */
export type ClosedHow = 'released' | 'cancelled' | 'closed_in_recruitment' | 'filled';

/** Which way of ending a link this timeline entry is; null for an entry that closed nothing. */
const closingOf = (event: HiringTimelineEvent): ClosedHow | null => {
  if (event.type === 'released') return 'released';
  // A cancel confirmed AFTER the link was stopped following is not what closed it.
  if (event.type === 'cancelled') return event.detail?.linkAlreadyClosed === true ? null : 'cancelled';
  // The joiner was placed at the store, and the hiring closed as filled with it.
  if (event.type === 'closed_filled') return 'filled';
  // A person answered "Close" (or "Raise again") on an opening Recruitment deleted or closed.
  if (event.type === 'attention_answered' && event.detail?.result === 'closed') return 'closed_in_recruitment';
  return null;
};

/**
 * How a link was closed, read off its timeline: the entry that closed it. A link is closed
 * once - by "stopped following"; by a cancel that Recruitment's list confirmed while it
 * was still followed (a cancel confirmed AFTER the link was stopped following is not what
 * closed it); by a person's "Close" on an opening Recruitment deleted or closed; or by
 * placing its joiner, which closes the hiring as filled. Null when the timeline holds none
 * of these: the screen then says only that it is no longer followed.
 *
 * `events` is the WHOLE timeline, every page of it (`readTimeline`): the entry that closed
 * a link is among its newest, and one page holds only the oldest.
 */
export const closedHowOf = (
  events: readonly HiringTimelineEvent[],
): { closedAs: ClosedHow; closedBy: string | null; closedAt: string | null } | null => {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const event = events[i];
    const how = closingOf(event);
    if (how) return { closedAs: how, closedBy: event.actor ?? null, closedAt: event.occurredAt ?? null };
  }
  return null;
};

/* ── a card's timeline ── */

export const TIMELINE_WORDS = {
  /**
   * The card's timeline stopped short of its end (`cut`). Since 15 Oct 2026 readTimeline reads
   * every page, so it is not said; kept for a reading that ever is. `shown` is how many lines it did read.
   * A timeline is read oldest first, so the lines that are missing are the newest - and
   * with them, perhaps, the one that says how the link ended.
   */
  notAll: (shown: number) =>
    `Only the first ${shown.toLocaleString('en-IN')} lines of this timeline are shown, so the newest ones are missing.`,
} as const;

/* ── the Cancel switch (super admins) ── */

export const CANCEL_SWITCH = {
  label: 'Cancel hiring from ChannelPlex',
  about:
    'Lets people with "Raise & cancel hiring" cancel an opening that is linked to a store or a manager role. '
    + 'Recruitment deletes the opening at once. A person reviews and confirms each one, and gives a reason.',
  needsSee: 'Turn on Show hiring for this project first.',
  /**
   * The second step before Cancel is turned ON for a project. Turning it on is what lets
   * real openings be deleted, so it is never one stray click. Turning it off asks nothing.
   */
  confirmGroupLabel: 'Confirm turning on Cancel',
  confirmLines: [
    'Cancel deletes real openings in Recruitment, in the name of the person who confirms. A deleted opening cannot be brought back.',
    'Recruitment then marks every candidate still in process on a cancelled opening as Position Closed. It sends each of them a WhatsApp message inviting them to apply for other jobs, and a notice in the ChannelPlay Jobs app to those who use it. Recruiters get no mail from Recruitment.',
    'Recruitment refuses a cancel while a candidate has an offer or has joined.',
    'It uses the delete Recruitment has today, which the 1Office team accepted on 15 October 2026.',
    'Only people whose role has "Raise & cancel hiring" ticked in Access Management will be able to cancel.',
    'Turn it on for this project only after the supervised test of Cancel has passed.',
  ],
  confirmNo: 'No, keep it off',
  confirmYes: 'Yes, turn on Cancel',
  /** The server's emergency off of Cancel is set (the settings answer says Cancel is not available). */
  stoppedOnServer:
    'Cancel is stopped on the server for every project (the server setting HIRING_LINK_CHANGE_ENABLED is set to off). '
    + 'Nothing can be cancelled, and this switch cannot be turned on, until that setting is removed. Seeing and raising hiring are not affected.',
  seeHeldOn: 'To hide hiring, turn off Cancel hiring from ChannelPlex first.',
  // Who, as the second step said it: "Raise & cancel hiring", not the Stores tick.
  turnedOn: 'People with "Raise & cancel hiring" can now cancel openings for this project.',
  turnedOff:
    'Cancelling is now off for this project. Nothing changed in Recruitment. A cancel still waiting for Recruitment can still be checked.',
  saveFailed: 'Could not save this switch. Try again.',
} as const;

/* ═══════════════════ The hiring screens of 15 Oct 2026 (the approved mock-up) ═══════════════════ */
//
// The opening view (one followed opening: one status, four numbers, Details · Team ·
// Candidates), the Raise window as a short form, its review as a few label-and-value
// lines, and the confirmer's window. Short words; every caveat behind one (i).

/** A day as "31 Oct": a tag, a row of the candidates table. India time, as every moment here. */
export const formatHiringShortDay = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return tidy(d.toLocaleDateString('en-IN', { timeZone: INDIA, day: '2-digit', month: 'short' }));
};

/** "10:42" in India time. */
const indiaTime = (d: Date): string =>
  tidy(d.toLocaleTimeString('en-IN', { timeZone: INDIA, hour: '2-digit', minute: '2-digit', hour12: false }));
const indiaDayKey = (d: Date): string => d.toLocaleDateString('en-CA', { timeZone: INDIA });

/** "today at 10:42", or "on 09 Mar 2031, 10:42 am": when something was read. */
export const readWhenWords = (iso: string | null | undefined, now: Date = new Date()): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return indiaDayKey(d) === indiaDayKey(now) ? `today at ${indiaTime(d)}` : `on ${formatHiringMoment(iso)}`;
};

/* ── the four statuses ── */

/** The four words of the Hiring dashboard and of an opening's one pill. */
export const OPENING_STATUS_WORDS: Readonly<Record<HiringDashStatus, string>> = {
  being_raised: 'Being raised',
  hiring: 'Hiring',
  filled: 'Filled',
  needs_attention: 'Needs attention',
};
/** The dot of each status: grey, blue, green, amber. */
export const OPENING_STATUS_DOT: Readonly<Record<HiringDashStatus, string>> = {
  being_raised: 'bg-gray-400',
  hiring: 'bg-blue-500',
  filled: 'bg-green-600',
  needs_attention: 'bg-amber-600',
};
const OPENING_PILL_TONE: Readonly<Record<HiringDashStatus | 'closed', string>> = {
  being_raised: 'bg-gray-100 text-gray-700',
  hiring: 'bg-blue-50 text-blue-800',
  filled: 'bg-green-50 text-green-800',
  needs_attention: 'bg-amber-50 text-amber-900',
  closed: 'bg-gray-100 text-gray-600',
};

/**
 * Which of the four a link is. The server's word (`dashStatus`) when it sent one; otherwise
 * the same rule from what the link holds: being sent, a question for a person, a position
 * open or an offer out, else filled. A link no longer followed is `closed`.
 */
export const openingStatusOf = (link: HiringLink): HiringDashStatus | 'closed' => {
  if (link.state === 'closed') return 'closed';
  if (link.dashStatus) return link.dashStatus;
  if (link.state === 'sending') return 'being_raised';
  if (link.needsAttention || (link.attention ?? []).some((reason) => reason !== 'team_unconfirmed') || isGone(link.status)) return 'needs_attention';
  const n = link.numbers;
  if (!n || n.open > 0 || n.offered > 0) return 'hiring';
  return 'filled';
};

/** Why an opening needs attention, in one short line: said on hover of its pill. Null when it does not. */
export const openingAttentionWhy = (link: HiringLink): string | null => {
  if (openingStatusOf(link) !== 'needs_attention') return null;
  const items = link.state === 'live' ? link.attentionItems ?? [] : [];
  if (items.length > 0) return attentionTitle(items[0]);
  const attention = link.attention ?? [];
  if (attention.includes('created_incomplete')) return RAISE_WORDS.incompleteTitle;
  // The same short words as the dashboard's hover (hiringText.dashboard ATTENTION_HOVER).
  if (attention.includes('team_unconfirmed')) return 'Team not confirmed yet';
  if (attention.includes('cancel_open') || link.cancel) return 'A cancel is waiting for Recruitment';
  if (link.status === 'closed_in_recruitment') return 'Cancelled in Recruitment';
  // An opening ChannelPlex raised that the list has not shown yet: the dashboard's words for it.
  if (attention.includes('not_listed')) return 'Not in Recruitment’s list';
  if (link.status === 'not_listed') return DELETED_WORDS.badge;
  // Recruitment cannot be read just now: deleted is what the last read found, not this one.
  if (link.lastKnownStatus === 'not_listed') return DELETED_WORDS.whenLastChecked;
  return null;
};

export interface OpeningPill { text: string; dot: string; className: string; hover: string | null }

/**
 * An opening's one pill: one of the four words with its dot; a link no longer followed says how
 * it ended. An opening deleted in Recruitment reads "Deleted in Recruitment" in the colour of
 * Needs attention (it still sits there until someone stops following it): by the link's status,
 * or - `deleted` - because the window's own read of the opening found it gone.
 */
export const openingPill = (link: HiringLink, deleted = false): OpeningPill => {
  const status = openingStatusOf(link);
  if (status === 'closed') return { text: linkPill(link).text, dot: 'bg-gray-400', className: OPENING_PILL_TONE.closed, hover: null };
  if (deleted || deletedInRecruitment(link)) {
    return { text: DELETED_WORDS.badge, dot: OPENING_STATUS_DOT.needs_attention, className: OPENING_PILL_TONE.needs_attention, hover: null };
  }
  return { text: OPENING_STATUS_WORDS[status], dot: OPENING_STATUS_DOT[status], className: OPENING_PILL_TONE[status], hover: openingAttentionWhy(link) };
};

/**
 * "Brand Promoter · Opening 90778": the opening's designation (its budget's name) and its
 * number. A hiring with no store linked before 15 Oct 2026 keeps the name typed for it, as its
 * dashboard row does (hiringText.dashboard designationOf).
 */
export const openingTitle = (link: Pick<HiringLink, 'budget' | 'openingId' | 'state' | 'closedAs'> & { subject?: HiringLink['subject'] }): string => {
  const typed = link.subject?.kind === 'role' ? String(link.subject.roleLabel ?? '').trim() : '';
  const designation = typed || String(link.budget?.name ?? '').trim();
  const number = link.openingId != null
    ? `Opening ${link.openingId}`
    : link.state === 'sending' || link.closedAs === 'create_failed' ? RAISE_WORDS.requestHeading : 'Opening';
  return designation ? `${designation} · ${number}` : number;
};

/** "Gadget Hub Indiranagar (GHI-03)", or "No store" for a hiring with no store. */
export const openingStoreWords = (store: { storeName?: string | null; storeCode?: string | null } | null | undefined): string => {
  if (!store) return OPENING_WORDS.noStore;
  const name = String(store.storeName ?? '').trim();
  const code = String(store.storeCode ?? '').trim();
  if (name && code) return `${name} (${code})`;
  return name || code || OPENING_WORDS.noStore;
};

/** Every candidate a list's counts hold: the tab's number before the candidates are read. Null when not known. */
export const stageCountsTotal = (stages: HiringStageCounts | null | undefined): number | null => {
  if (!stages) return null;
  const keys: (keyof HiringStageCounts)[] = ['screening', 'interview', 'selected', 'offered', 'joined', 'dropped', 'leftAfterJoining', 'other'];
  return keys.reduce((sum, key) => sum + (typeof stages[key] === 'number' ? stages[key] : 0), 0);
};

export const OPENING_WORDS = {
  tabsLabel: 'Opening',
  tabs: { details: 'Details', team: 'Team' },
  candidatesTab: (count: number | null): string => (count == null ? 'Candidates' : `Candidates (${count})`),
  numbers: { positions: 'Positions', filled: 'Filled', offered: 'Offered', open: 'Open' },
  numbersInfo: 'About these numbers',
  /** The Manage Budgets link in an opening's foot, as the mock-up words it. */
  inManageBudgets: 'Open in Manage Budgets',
  noStore: 'No store',
  /** The Details list, in this order. */
  details: { store: 'Store', address: 'Address', city: 'City', state: 'State', pin: 'PIN', published: 'Published', created: 'Created' },
  channelplayJobs: 'ChannelPlay Jobs',
  apna: (day: string): string => `Apna ${day}`,
  notPublished: 'Not published',
  none: '–',
  /** The Team list. */
  team: { recruiters: 'Recruiters', offerMakers: 'Offer makers', round: (n: number): string => `Round ${n}` },
  client: 'Client',
  nobodyOnIt: 'Nobody',
  /** The Candidates tab. */
  search: 'Search name or mobile',
  /** Typed something that is neither a name nor a number: it is not sent. */
  badSearch: 'Type a name or a mobile number.',
  stage: 'Stage',
  allStages: 'All stages',
  /** A word of the stage filter with how many the opening has at it: "Screened (3)". */
  stageCount: (word: string, count: number): string => `${word} (${count})`,
  /** The table's columns, in this order. */
  columns: { name: 'Name', mobile: 'Mobile', from: 'From', source: 'Source', stage: 'Stage', round: 'Round', applied: 'Applied' },
  /** Behind the From and Source headers. */
  fromHint: 'The city the candidate lives in, as Recruitment holds it',
  sourceHint: 'Where the application came from',
  /** The Round column: the round, and the small grey line under it. */
  roundCell: {
    at: (n: number): string => `Round ${n}`,
    done: (n: number): string => `Round ${n} done`,
    more: (n: number): string => `+${n} more`,
    noShow: 'No-show',
    toSchedule: (n: number): string => `Round ${n} to schedule`,
  },
  shown: (shown: number, total: number): string => `${shown} of ${total}`,
  more: 'Show more',
  noCandidates: 'No candidates yet.',
  noMatch: 'No one matches.',
  /** Reads. */
  loading: 'Reading Recruitment…',
  failed: 'Could not read this from Recruitment.',
  tryAgain: 'Try again',
  /** The foot. */
  stop: 'Stop following',
  stopQuestion: 'Stop following this opening?',
  stopNote: 'Nothing changes in Recruitment.',
  keep: 'No, keep',
  stopYes: 'Yes, stop following',
  /** The opening's history, one click away under Details. */
  history: 'Show history',
  hideHistory: 'Hide history',
  historyLabel: 'History',
} as const;

/**
 * A candidate's Round cell: "Round 1", "Round 3 · 21 Oct", "Round 1 done" or "–", and the
 * small grey line under it ("+1 more", "No-show", "Round 2 to schedule"), when there is one.
 */
export const candidateRoundWords = (row: Pick<OpeningCandidateRow, 'round' | 'roundDone' | 'roundOn' | 'roundNote'>): { line: string; note: string | null } => {
  if (row.round == null) return { line: OPENING_WORDS.none, note: null };
  const words = OPENING_WORDS.roundCell;
  let line = row.roundDone ? words.done(row.round) : words.at(row.round);
  if (!row.roundDone && row.roundOn) line = `${line} · ${formatHiringShortDay(row.roundOn)}`;
  const note = row.roundNote;
  if (!note) return { line, note: null };
  if (note.kind === 'more') return { line, note: words.more(note.count) };
  if (note.kind === 'no_show') return { line, note: words.noShow };
  return { line, note: words.toSchedule(note.round) };
};

/**
 * Where an application came from, in short words (the product owner, 27 Oct 2026). 1Office
 * sends its own word (`application_source`, 'Recruiter' when it holds none); the ones it is
 * known to write are put in plain words here, matched whatever their case or spacing.
 * "Apna Portal -> Self Application" is written by 1Office's Apna sign-up (login-microservices
 * ApnaPortalServiceImpl). Anything else is shown as 1Office sent it, and nothing is "–".
 */
export const CANDIDATE_SOURCE_WORDS: Readonly<Record<string, string>> = {
  'job apply': 'Jobs app',
  'recruiter': 'Added by recruiter',
  'screener': 'Screener',
  'direct offer': 'Direct offer',
  'google sheet': 'Sheet upload',
  'cpj-rehire': 'Rehire',
  'apna portal -> self application': 'Apna',
};

export const candidateSourceWord = (source: string | null | undefined): string => {
  const sent = typeof source === 'string' ? source.replace(/\s+/g, ' ').trim() : '';
  if (!sent) return OPENING_WORDS.none;
  const key = sent.toLowerCase();
  // Own keys only: a source spelt like "constructor" is shown as sent.
  return Object.prototype.hasOwnProperty.call(CANDIDATE_SOURCE_WORDS, key) ? CANDIDATE_SOURCE_WORDS[key] : sent;
};

/**
 * An opening deleted in Recruitment (21 Oct 2026, the product owner: "Yes, please update that
 * window"). In place of its numbers and its tabs, one plain notice; at its foot only "Stop
 * following" and "Raise again" (or "Ask for hiring"). 1Office's delete also closes the
 * opening's candidates ("Position Closed").
 */
export const DELETED_WORDS = {
  badge: 'Deleted in Recruitment',
  /** Why it needs attention, on hover, while Recruitment cannot be read and the last read found it deleted. */
  whenLastChecked: 'Deleted in Recruitment when last checked',
  notice: '1Office no longer has this opening: it was deleted in Recruitment. Its candidates were closed there.',
  /**
   * Under the notice. For a link whose status says so, `seenAt` is the latest read that found
   * the opening gone (the server moves it on at each one): when ChannelPlex last checked. For a
   * link a read still listed - the window's own read of the opening found it gone since - it is
   * that read: when the opening was last seen.
   */
  lastChecked: (when: string): string => `Last checked ${when}`,
  lastSeen: (when: string): string => `Last seen ${when}`,
  raiseAgain: 'Raise again',
  raiseAgainHover: 'Opens Raise hiring for this store. Once it is raised, this deleted opening is no longer followed.',
  raiseAgainHoverNoStore: 'Opens Raise hiring. Once it is raised, this deleted opening is no longer followed.',
  ask: 'Ask for hiring',
  askHover: 'Opens Ask for hiring for this store. This deleted opening stays followed until someone stops following it.',
  askHoverNoStore: 'Opens Ask for hiring. This deleted opening stays followed until someone stops following it.',
  /** The store's details could not be read for the Raise window. */
  openFailed: 'Could not open Raise hiring for this store. Try again.',
  storeNotFound: 'This store is not one of the stores you can raise hiring for.',
  /** In the Raise window's result, once the raise made an opening: the deleted one, let go of in the same step. */
  stopping: (openingId: number | null): string => `Stopping following ${openingId != null ? `Opening ${openingId}` : 'the deleted opening'}…`,
  stopped: (openingId: number | null): string =>
    `${openingId != null ? `Opening ${openingId}` : 'The deleted opening'}, deleted in Recruitment, is no longer followed.`,
  stopFailed: (openingId: number | null): string =>
    `${openingId != null ? `Opening ${openingId}` : 'The deleted opening'} is still followed here. Open it and use Stop following.`,
  /** In the Raise window's result, while Recruitment has not confirmed the raise: nothing is let go of yet. */
  waiting: (openingId: number | null): string =>
    `${openingId != null ? `Opening ${openingId}` : 'The deleted opening'} stays followed until this raise is confirmed.`,
  waitingHover: (openingId: number | null): string =>
    `It is stopped once Recruitment confirms this raise while this window is open. Closed before then, open ${openingId != null ? `Opening ${openingId}` : 'the deleted opening'} and use Stop following.`,
  /** In Ask for hiring's result: a hiring request lets go of nothing. */
  askLeft: (openingId: number | null): string =>
    `${openingId != null ? `Opening ${openingId}` : 'The deleted opening'}, deleted in Recruitment, stays followed until someone stops following it.`,
} as const;

/**
 * Under the notice of a deleted opening: its store and where it was ("Sample Store 04 (SS04) ·
 * 1 Sample Road · Sample City"), as the link was made. Null when nothing of it is known.
 */
export const deletedWhereLine = (link: HiringLink): string | null => {
  const store = link.subject.kind === 'store' ? openingStoreWords(link.subject) : null;
  const where = link.address?.now ?? link.address?.linked ?? null;
  const parts = [store, where?.location, where?.cityName].map((part) => String(part ?? '').trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
};

/**
 * The small grey line under the notice of a deleted opening, from the link's `seenAt`; null
 * when it has none. `byStatus`: the link's status says it was deleted (see DELETED_WORDS).
 */
export const deletedSeenLine = (link: HiringLink, byStatus: boolean): string | null => {
  if (!link.seenAt) return null;
  const when = formatHiringMoment(link.seenAt);
  return byStatus ? DELETED_WORDS.lastChecked(when) : DELETED_WORDS.lastSeen(when);
};

/** The hiring panel's own few words, for a store with several openings (or none). */
export const PANEL_WORDS = {
  link: 'Link an opening',
  noneStore: 'No opening is linked to this store.',
  noneRole: 'No opening is linked to this hiring.',
  linkHint: 'Use Link an opening to follow an opening that already exists in Recruitment.',
} as const;

/** "ChannelPlay Jobs · Apna 03 Oct", or "Not published", or null when it is not known. */
export const publishedWords = (published: { channelplayJobs: boolean | null; apnaOn: string | null } | null | undefined): string | null => {
  if (!published) return null;
  const parts: string[] = [];
  if (published.channelplayJobs === true) parts.push(OPENING_WORDS.channelplayJobs);
  if (published.apnaOn) parts.push(OPENING_WORDS.apna(formatHiringShortDay(published.apnaOn)));
  if (parts.length > 0) return parts.join(' · ');
  return published.channelplayJobs === false ? OPENING_WORDS.notPublished : null;
};

/** "02 Oct 2031 · Asha Rao": when it was raised or linked here, and by whom (the name, else the code). */
export const createdWords = (created: { on: string | null; byName?: string | null; byCode?: string | null } | null | undefined): string | null => {
  if (!created || !created.on) return null;
  const who = String(created.byName ?? '').trim() || String(created.byCode ?? '').trim();
  return who ? `${formatHiringDay(created.on)} · ${who}` : formatHiringDay(created.on);
};

/**
 * The one (i) beside the four numbers: when they were read, that unscreened applications
 * are mostly not counted, and who else follows a shared opening. Short lines, no sentence
 * on the page itself.
 */
export const openingInfoLines = (facts: {
  link: HiringLink;
  readAt?: string | null;
  numbersFrom?: 'recruitment' | 'last_seen' | null;
  reason?: string | null;
  sharedWith?: ReadonlyArray<{ storeName: string | null; storeCode: string | null; roleLabel: string | null }> | null;
  now?: Date;
}): string[] => {
  const { link } = facts;
  const lines: string[] = [];
  const fresh = facts.numbersFrom ? facts.numbersFrom === 'recruitment' : link.numbersFrom === 'live';
  if (facts.reason) lines.push(facts.reason);
  else if (link.status === 'numbers_unavailable' && link.state !== 'closed') lines.push(reasonSentence(link.unavailableReason));
  const readAt = fresh ? facts.readAt ?? link.seenAt : link.seenAt;
  const when = readWhenWords(readAt, facts.now);
  if (link.state === 'closed') lines.push('The last numbers read while it was followed.');
  else if (when) lines.push(fresh ? `Read from Recruitment ${when}.` : `Last read from Recruitment ${when}.`);
  lines.push('Applications not yet screened are mostly not counted.');
  const others = (facts.sharedWith ?? []).map((other) => (other.storeName || other.storeCode
    ? openingStoreWords(other)
    : String(other.roleLabel ?? '').trim())).filter(Boolean);
  if (others.length > 0) lines.push(`This opening is also linked to ${listOf(others)}.`);
  else if (link.shared) lines.push('Several stores share this opening. The numbers are for the whole opening.');
  return lines;
};

/* ── the Raise window as a short form ── */

/** The two reasons, as the segmented buttons say them. */
export const RAISE_SEGMENTS: readonly { value: HiringRaiseReason; label: string }[] = [
  { value: 'replacement', label: 'Replacing someone' },
  { value: 'extra_headcount', label: 'One more person' },
];

export const RAISE_FORM = {
  title: 'Raise hiring',
  askTitle: 'Ask for hiring',
  why: 'Why',
  who: 'Who',
  whoPlaceholder: 'Choose a person',
  whoSearch: 'Type a name or code',
  store: 'Store',
  optional: 'optional',
  noStore: 'No store',
  storeSearch: 'Search stores',
  theirStore: 'Their store',
  showAddress: 'Show address',
  hideAddress: 'Hide address',
  noMapLine: 'No map location or PIN code on record. Edit store.',
  noMapLineNoEdit: 'No map location or PIN code on record.',
  cityPin: 'City · PIN',
  cityInfo: 'The opening is placed at the city’s map point in Recruitment.',
  city: 'City',
  cityPlaceholder: 'Choose a city',
  citySearch: 'Type 2 letters of the city',
  pin: 'PIN',
  address: 'Address',
  addressPlaceholder: 'Start typing the address',
  addressSuggestions: 'Address suggestions',
  /** Said quietly under the box when Google could not be asked: the box is a plain text box then. */
  suggestionsUnavailable: 'Suggestions unavailable',
  storeCity: 'Recruitment city',
  budget: 'Budget',
  budgetPlaceholder: 'Choose a budget',
  budgetSearch: 'Search budgets',
  storeLevel: 'Store-level',
  theirBudget: 'Their budget',
  freeOf: (free: number, total: number): string => `${free} free of ${total}`,
  full: 'Full',
  fullHover: 'All positions are used. A position frees up when someone in it leaves.',
  /** Under the budget list (every budget is in it): how many, and that the full ones cannot be chosen. */
  budgetsShown: (total: number, anyFull: boolean): string =>
    `${total} ${total === 1 ? 'budget' : 'budgets'}${anyFull ? ' · Full ones cannot be chosen' : ''}`,
  /** Under the city list: how many matched (every one is in the list). */
  citiesShown: (total: number): string => `${total} ${total === 1 ? 'city' : 'cities'}`,
  storesNone: 'No store matches.',
  peopleNone: 'No one matches.',
  leavingNone: 'No one to show.',
  /**
   * "Who" for a chosen store with nobody in it (15 Oct 2026): nobody deployed there now, and
   * nobody who left it in the last 30 days - and what to do instead.
   */
  storePeopleNone: 'No one is deployed at this store.',
  storePeopleNoneHint: 'Choose “One more person” to hire here without replacing anyone.',
  noBudgets: 'No active budget is saved for this project on ChannelPlex > Project Management.',
  team: 'Team',
  askTeam: 'The person who confirms picks the team.',
  clear: (what: string): string => `Clear ${what.toLowerCase()}`,
  typeTwo: 'Type 2 letters',
  searching: 'Searching…',
  loadFailed: 'Could not load this. Try again.',
  noMatch: 'No matches',
} as const;

/** The day of a tag: "31 Oct". */
const tagDay = (day: string | null | undefined): string | null => (day ? formatHiringShortDay(day) : null);

export interface PickerTag { text: string; tone: 'blue' | 'amber' | 'grey'; hover?: string }

/** A person's one tag in "Who": Notice · 31 Oct, Resigned, Left · 09 Oct; none for someone working. */
export const personTagOf = (person: Pick<HiringPersonOption, 'tag' | 'day'>): PickerTag | null => {
  const day = tagDay(person.day);
  switch (person.tag) {
    case 'serving_notice': return { text: day ? `Notice · ${day}` : 'Notice', tone: 'amber', hover: 'Serving notice: their last working day.' };
    case 'resigned': return { text: 'Resigned', tone: 'amber', hover: 'A resignation is on file in 1Office. HR has not processed the exit.' };
    case 'left': return { text: day ? `Left · ${day}` : 'Left', tone: 'grey', hover: 'Their last working day, or the day they left the store.' };
    default: return null;
  }
};

/** Why a person cannot be picked, in one word on the right and one line on hover. Null when they can be. */
export const personBlockedOf = (person: Pick<HiringPersonOption, 'pickable' | 'blocked'>): { text: string; hover: string } | null => {
  const blocked = person.blocked;
  if (person.pickable !== false && !blocked) return null;
  switch (blocked?.why) {
    case 'hiring_raised':
      return {
        text: 'Hiring raised',
        hover: blocked.openingId != null ? `Already replaced by opening ${blocked.openingId}` : 'Hiring to replace them is being raised.',
      };
    case 'waiting':
      return {
        text: 'Waiting',
        hover: blocked.requestedBy ? `${blocked.requestedBy} asked for their replacement. It is waiting.` : 'A hiring request for this person is waiting.',
      };
    case 'already_replaced':
      return { text: 'Replaced', hover: 'Recruitment already marks them replaced.' };
    default:
      return { text: 'Cannot be picked', hover: 'This person cannot be replaced from here.' };
  }
};

/** "Kavya Rao", or the code when no name is known. */
export const personName = (person: Pick<HiringPersonOption, 'empCode' | 'name'>): string =>
  (person.name && person.name.trim() ? person.name.trim() : person.empCode);

/** "910482 · Brand Promoter · Tech Bazaar Koramangala" (or "No store"). */
export const personMeta = (person: Pick<HiringPersonOption, 'empCode' | 'name' | 'designation' | 'store'>): string =>
  [
    person.name && person.name.trim() ? person.empCode : null,
    person.designation && person.designation.trim() ? person.designation.trim() : null,
    person.store ? person.store.storeName || person.store.storeCode || RAISE_FORM.noStore : RAISE_FORM.noStore,
  ].filter(Boolean).join(' · ');

/**
 * The line under "Who" (drawn only while there are people to count): how many the list holds -
 * all of them, read a page at a time as it is scrolled. For a chosen store (`atStore`, since
 * 15 Oct 2026) the list is that store's people alone: "8 people at this store". With no store
 * and nothing typed, the ones leaving come first. Typed, how many matched. Null with nobody
 * (the list's own one line says so).
 */
export const peopleFooter = (
  answer: Pick<HiringPeopleSearch, 'q' | 'total' | 'searchable' | 'people'> | null,
  typed: string,
  atStore = false,
): string | null => {
  if (!answer || answer.total === 0) return null;
  const n = answer.total.toLocaleString('en-IN');
  if (typed.trim().length >= 2) return `${n} ${answer.total === 1 ? 'match' : 'matches'}`;
  if (atStore) return `${n} ${answer.total === 1 ? 'person' : 'people'} at this store`;
  return `${n} ${answer.total === 1 ? 'person' : 'people'} · leaving or left first`;
};

/**
 * The one line "Who" shows when its list is empty: nobody matched what was typed; a chosen
 * store with nobody deployed there and nobody who left it in the last 30 days, with what to do
 * instead; or, with no store, nobody to show.
 */
export const peopleEmptyText = (searching: boolean, atStore: boolean): string => {
  if (searching) return RAISE_FORM.peopleNone;
  return atStore ? `${RAISE_FORM.storePeopleNone} ${RAISE_FORM.storePeopleNoneHint}` : RAISE_FORM.leavingNone;
};

/** A store of the store picker: "Gadget Hub Indiranagar (GHI-03)" and its city. */
export const storeOptionWords = (store: Pick<HiringStoreOption, 'storeName' | 'storeCode' | 'city'>): { label: string; meta: string | null } => ({
  label: openingStoreWords(store),
  meta: store.city && store.city.trim() ? store.city.trim() : null,
});

/** The read-only address under a chosen store: "12, 100 Feet Road · 560038". */
export const storeAddressLine = (store: Pick<HiringStoreOption, 'address' | 'pincode'>): string | null => {
  const parts = [store.address, store.pincode].map((part) => String(part ?? '').trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
};

/** The line under the store picker: how many stores the list holds (all of them, read as it is scrolled). Null with none. */
export const storesFooter = (answer: Pick<HiringStoreSearch, 'total' | 'stores'> | null): string | null => {
  if (!answer || answer.total === 0) return null;
  return `${answer.total.toLocaleString('en-IN')} ${answer.total === 1 ? 'store' : 'stores'}`;
};

/** A budget of the budget picker: its tags and its right-hand words. */
export const budgetOptionWords = (
  budget: Pick<HiringRaiseBudget, 'storeLevel' | 'free' | 'total' | 'full'>,
  extra: { theirs?: boolean; withStore?: boolean } = {},
): { tags: PickerTag[]; right: string; full: boolean } => {
  const full = budget.full ?? budgetIsFull(budget);
  const tags: PickerTag[] = [];
  if (extra.theirs) tags.push({ text: RAISE_FORM.theirBudget, tone: 'amber' });
  if (budget.storeLevel && extra.withStore !== false) tags.push({ text: RAISE_FORM.storeLevel, tone: 'blue' });
  return { tags, right: full ? RAISE_FORM.full : RAISE_FORM.freeOf(budget.free, budget.total), full };
};

/** The one grey line above Cancel · Review: what the form still needs, or null when it can be reviewed. */
export const raiseMissingLine = (needs: {
  reason: boolean;
  person: boolean;
  address: string | null;
  budget: boolean;
  team: string | null;
}): string | null => {
  const parts: string[] = [];
  if (needs.reason) parts.push('a reason');
  if (needs.person) parts.push('a person');
  if (needs.budget) parts.push('a budget');
  if (parts.length > 0) {
    // An address problem is said after these in its own words only when it is not plain "missing".
    return `Needs ${listOf(parts)}`;
  }
  if (needs.address) return needs.address;
  if (needs.team) return needs.team;
  return null;
};

/** What a typed address (no store) still needs, in the footer's few words. */
export const typedAddressMissing = (typed: { cityId: number | null; address: string; pincode: string }): string | null => {
  const missing: string[] = [];
  if (typed.cityId == null) missing.push('a city');
  if (!/^[1-9]\d{5}$/.test(typed.pincode.trim())) missing.push('a PIN');
  const address = typed.address.trim().replace(/\s+/g, ' ');
  if (address.length < TYPED_ADDRESS_MIN) missing.push('an address');
  if (missing.length > 0) return `Needs ${listOf(missing)}`;
  // Anything else is the rule's own sentence (too long, or a phone number in it).
  return typedAddressProblem(typed);
};

/** What the team still needs, in the footer's few words. Null when it is complete (or not read yet). */
export const teamMissingLine = (draft: TeamDraft | null): string | null => {
  if (!draft) return null;
  if (draft.recruiters.length === 0) return 'Needs a recruiter';
  if (draft.offerMakers.length === 0) return 'Needs an offer maker';
  for (let i = 0; i < draft.rounds.length; i += 1) {
    const round = draft.rounds[i];
    if (round.kind === 'staff' && round.userId == null) return `Needs the round ${i + 1} interviewer`;
    if (round.kind === 'client') {
      const problem = clientContactProblem(round);
      if (problem) return `Round ${i + 1}: ${problem}`;
    }
  }
  return null;
};

/* ── the review: a few label-and-value lines, then at most two notes ── */

export const REVIEW_WORDS = {
  heading: 'Raise this hiring?',
  askHeading: 'Ask for this hiring?',
  openingFor: 'Opening for',
  replaces: 'Replaces',
  budget: 'Budget',
  address: 'Address',
  team: 'Team',
  usesOne: (free: number): string => `uses 1 of ${free} free`,
  freeNow: (free: number): string => `${free} free now`,
  teamByConfirmer: 'Picked by the person who confirms',
  noteLive: 'Goes live at once. Recruitment mails the team.',
  noteCancel: 'You can cancel it until someone is offered.',
  back: 'Back',
  /** The (i) beside the heading: the server's own lines, word for word. */
  infoLabel: 'What Recruitment will do',
} as const;

/** "Kavya Rao · 910482 · Notice 31 Oct": the person replaced, as the review names them. */
export const reviewReplacesWords = (person: { empCode: string; name?: string | null; tag?: PickerTag | null }): string =>
  [person.name && person.name.trim() ? person.name.trim() : null, person.empCode, person.tag ? person.tag.text.replace(' · ', ' ') : null]
    .filter(Boolean).join(' · ');

/** The review's team, one label and value per role and round: "Recruiters" "Neha Kulkarni, Rohit Bansal". */
export const reviewTeamPairs = (team: HiringReviewTeam): Array<{ label: string; value: string }> => {
  const pairs: Array<{ label: string; value: string }> = [];
  const name = (person: Pick<HiringStaff, 'name' | 'empCode'>) => String(person.name ?? '').trim() || String(person.empCode ?? '').trim();
  const recruiters = team.recruiters ?? [];
  const offerMakers = team.offerMakers ?? [];
  if (recruiters.length > 0) pairs.push({ label: recruiters.length === 1 ? 'Recruiter' : 'Recruiters', value: recruiters.map(name).join(', ') });
  if (offerMakers.length > 0) pairs.push({ label: offerMakers.length === 1 ? 'Offer maker' : 'Offer makers', value: offerMakers.map(name).join(', ') });
  for (const round of team.rounds ?? []) {
    pairs.push({ label: `Round ${round.round}`, value: round.kind === 'client' ? `${round.name} · ${OPENING_WORDS.client}` : name(round) });
  }
  return pairs;
};

/* ── the confirmer's window ── */

export const REQUEST_FORM = {
  title: 'Hiring request',
  askedBy: 'Asked by',
  openingFor: 'Opening for',
  why: 'Why',
  replaces: 'Replaces',
  budget: 'Budget',
  ticked: 'Ticked',
  note: 'Note',
  askedOn: (request: Pick<HiringRequest, 'requestedBy' | 'requestedAt' | 'mine'>): string =>
    `${request.mine ? 'You' : request.requestedBy} · ${formatHiringMoment(request.requestedAt)}`,
} as const;

/** "Sample Store 01 (SS01)", or "No store" for a request with no store. */
export const requestOpeningFor = (request: Pick<HiringRequest, 'subject'>): string =>
  (request.subject.kind === 'store' ? openingStoreWords(request.subject) : RAISE_FORM.noStore);

/* ─────────────────────────────── move to another store ─────────────────────────────── */
//
// "Move to another store" (15 Oct 2026, the product owner: "Yes, build the Move to another
// store button"): ChannelPlex asks Recruitment to move a live opening it follows for ONE store
// to another store of the project (the 1Office team's changeOpeningStore, their reply of 15
// October 2026). Recruitment changes only the opening's city, address, map point and PIN code,
// mails its recruiters, offer makers and interviewers, and tells candidates in process who use
// the ChannelPlay Jobs app; ChannelPlex then follows the opening for the new store.
//
// Its switch is the project's Cancel switch, and its right "Raise & cancel hiring": it is
// offered exactly where "Cancel opening" is, on a store's own opening (not a shared one, not a
// hiring with no store). The review's title, sentences and buttons are the server's, word for
// word; what follows are the screen's own few words around them.
//
// Never offered: a second move while one Recruitment has not confirmed is open (only "Check
// again", which reads and never sends), and a repeat of a confirm with a new key.

/** The refusal codes a move adds. The window acts on a few; every other one only shows its sentence. */
export const MOVE_CODES = {
  notEnabled: 'MOVE_NOT_ENABLED',
  notAStore: 'MOVE_NOT_A_STORE',
  sharedOpening: 'MOVE_SHARED_OPENING',
  sameStore: 'MOVE_SAME_STORE',
  sendInProgress: 'SEND_IN_PROGRESS',
  hasOfferOrJoiner: 'MOVE_HAS_OFFER_OR_JOINER',
  linkNotMoving: 'LINK_NOT_MOVING',
} as const;

/**
 * 1Office's own rule of who may move an opening (CRS OpeningStoreChangeService; seen live on
 * 22 Oct 2026 as ACTOR_NOT_ALLOWED). Said on Move's hover only: the button stays, because
 * ChannelPlex cannot tell 1Office's reporting line, and a refusal is shown as it comes back.
 */
const MOVE_WHO_MAY = 'In 1Office only the project’s manager or its Project HR (or someone above them) can move an opening.';

export const MOVE_WORDS = {
  /** The quiet button in the opening's foot, beside "Cancel opening". */
  button: 'Move to another store',
  // Promises nothing the server may not do (who is told depends on the move): the review says it.
  buttonExplained: `Ask Recruitment to move this opening to another of your stores. You see what it will do before anything is sent. ${MOVE_WHO_MAY}`,
  /** 1Office's rule of who may move, as the hover above ends with it. */
  whoMay: MOVE_WHO_MAY,
  /** Step 1: the new store. */
  groupLabel: (openingId: number | null | undefined): string =>
    (openingId != null ? `Move opening ${openingId} to another store` : 'Move this opening to another store'),
  heading: 'Move to another store',
  nowAt: (store: string): string => `Now at ${store}`,
  store: 'New store',
  storePlaceholder: 'Choose a store',
  /** The store the opening is followed for now: greyed in the list. */
  hereNow: 'Here now',
  hereNowHover: 'The opening is followed for this store now.',
  city: 'Recruitment city',
  cityHelp: 'Pick the new store’s city as Recruitment lists it.',
  /** The ticks, in the words the server's own sentences name them. */
  tickStatus: (staffingStatus: string): string => `Move although the store is marked ${staffingStatus}`,
  tickAdditional: (store: string): string => `${store} needs one more person`,
  review: 'Review',
  reading: 'Reading Recruitment…',
  close: 'Close',
  reviewFailed: 'Could not check this with Recruitment. Nothing was sent. Try again.',
  reviewedAgain: 'Something changed since you reviewed this. Read it again before you confirm.',
  /** Step 2: the review. Its title, its sentences, its buttons and the line under them are the server's. */
  confirmGroupLabel: (openingId: number | null | undefined): string => `Confirm move of opening ${openingId ?? ''}`.trim(),
  from: 'From',
  to: 'To',
  pin: (pincode: string): string => `PIN ${pincode}`,
  distance: (km: number, cityName: string): string => `${km} km from ${cityName}’s map point in Recruitment`,
  // A short line above the server's sentences, which say it in full: its explanation is on its hover.
  alreadyThere: 'Already there',
  alreadyThereHover: 'Recruitment already has this opening at the new store’s address: it changes nothing there and tells nobody.',
  maybeAlreadyThere: 'Maybe already there',
  maybeAlreadyThereHover: 'Recruitment may already have this opening at the new store’s address: ChannelPlex could not read its PIN code there.',
  sentencesLabel: 'What Recruitment will do',
  changeStore: 'Change the store',
  yes: 'Yes, move this opening',
  no: 'No, keep it at this store',
  footnote: 'Sent to Recruitment in your name.',
  /** Step 3: the one confirm. */
  sending: 'Moving in Recruitment…',
  noAnswer: 'No answer came back. Do not move it again.',
  askAgain: 'Ask again',
  /** A move Recruitment has not confirmed. Checked, never sent again. */
  standingLabel: 'Where this move stands',
  notConfirmed: 'Recruitment did not confirm',
  standing: 'A move of this opening was sent, and Recruitment has not confirmed it. Do not move it again.',
  checkAgain: 'Check again',
  checking: 'Checking Recruitment…',
  checkFailed: 'Could not check just now. Try again in a moment.',
  /** "Ask again" found Move switched off: the lost confirm may still have gone out, and only a check can say. */
  lostSwitchedOff: 'No answer came back, and moving openings has since been turned off for this project. The move may have gone out. Press Check again to see where the opening is.',
  /** "Check again" after "has not been confirmed yet (a move or a cancel)" found no move waiting: what is open is a cancel. */
  noMoveWaiting: 'No move of this opening is waiting, so what is open is a cancel. It has its own Check again.',
} as const;

/** "Sample Store 02 (SS02)", as the server names a store in a move's sentences ("this store" when it has neither). */
export const moveStoreLabel = (store: { storeName?: string | null; storeCode?: string | null } | null | undefined): string => {
  const name = String(store?.storeName ?? '').trim();
  const code = String(store?.storeCode ?? '').trim();
  if (!name) return code || 'this store';
  return code ? `${name} (${code})` : name;
};

/**
 * How a move's outcome is shown, and what may be offered after it.
 *   moved    the opening is at the new store in Recruitment, and followed for it here.
 *   waiting  Recruitment has not confirmed: NEVER offers a second move, only "Check again".
 *   nothing  nothing changed and the link is as it was; moving again is safe.
 * An outcome this screen does not know is "waiting": the safe reading.
 */
export type MoveOutcomeKind = 'moved' | 'waiting' | 'nothing';

const MOVE_OUTCOME_KIND: Record<HiringMoveOutcome, MoveOutcomeKind> = {
  moved: 'moved',
  not_confirmed: 'waiting',
  in_progress: 'waiting',
  refused: 'nothing',
  not_sent: 'nothing',
  not_moved: 'nothing',
};

export const MOVE_OUTCOMES = Object.keys(MOVE_OUTCOME_KIND);

export const moveOutcomeKind = (outcome: string | null | undefined): MoveOutcomeKind =>
  (outcome && MOVE_OUTCOME_KIND[outcome as HiringMoveOutcome]) || 'waiting';

/**
 * Whether the answer of a check that nobody's confirm here asked for (the card's own read when
 * it is shown, or "Check again" after "has not been confirmed yet") tells this card something:
 * a move still waiting, or a move that took the opening to another store than the one the card
 * shows (`storeIdNow`). The server's check answers with the LATEST move of the link whatever
 * became of it, so any other answer is the ending of an earlier move - already over - and its
 * sentence is not said again as if it had just happened.
 */
export const moveCheckIsNews = (
  answer: { outcome?: string | null; link?: Record<string, unknown> | null } | null | undefined,
  storeIdNow: string | null | undefined,
): boolean => {
  if (!answer || typeof answer.outcome !== 'string') return false;
  const kind = moveOutcomeKind(answer.outcome);
  if (kind === 'waiting') return true;
  if (kind !== 'moved') return false;
  const subject = (answer.link as Partial<HiringLink> | null | undefined)?.subject;
  const there = subject && subject.kind === 'store' ? String(subject.storeId ?? '').trim().toLowerCase() : '';
  const here = String(storeIdNow ?? '').trim().toLowerCase();
  return !!there && there !== here;
};

/**
 * What a refused review or confirm makes the window do.
 *   control_goes   Move (the project's Cancel switch), or hiring itself, was switched off.
 *   link_gone      someone stopped following it, or cancelled it, first.
 *   review_again   what was reviewed is no longer so: a fresh review, with a new key.
 *   check          something sent for this opening is still open: "Check again" is offered.
 *   list_reread    the page's list is out of date: the owner reads it again.
 *   form           the sentence is shown on step 1, with what it asks for (a city, a tick,
 *                  another store). Nothing was sent.
 */
export type MoveRefusalStep = 'control_goes' | 'link_gone' | 'review_again' | 'check' | 'list_reread' | 'form';

const MOVE_STEPS_ACTED_ON: Record<string, MoveRefusalStep> = {
  [MOVE_CODES.notEnabled]: 'control_goes',
  [HIRING_CODES.notEnabled]: 'control_goes',
  [HIRING_CODES.linkNotLive]: 'link_gone',
  [HIRING_CODES.linkNotFound]: 'link_gone',
  [RAISE_CODES.previewStale]: 'review_again',
  [RAISE_CODES.requestKeyReused]: 'review_again',
  [MOVE_CODES.sendInProgress]: 'check',
  [HIRING_CODES.notInBudget]: 'list_reread',
};

export const moveRefusalStep = (err: unknown): MoveRefusalStep => {
  const code = hiringCodeOf(err);
  return (code && MOVE_STEPS_ACTED_ON[code]) || 'form';
};

/** What a refused move call says: the server's own sentence, with its moments in India time. */
export const moveErrorText = (err: unknown, fallback: string): string => raiseErrorText(err, fallback);

/** The key of one confirm of a move: made once, when the review arrives, and sent with every repeat of that confirm. */
export const makeMoveRequestKey = (): string => makeRaiseRequestKey();

/** What step 1 must show after a refusal: the city picker, and the ticks the server's sentence names. */
export interface MoveNeeds {
  cityPicker: boolean;
  /** "Move although the store is marked …", for this staffing status. */
  staffingStatus: string | null;
  /** "<store> needs one more person": the new store already follows another opening. */
  additional: boolean;
}

export const NO_MOVE_NEEDS: MoveNeeds = { cityPicker: false, staffingStatus: null, additional: false };

const MOVE_CITY_CODES: readonly string[] = [
  RAISE_CODES.cityNotMatched, RAISE_CODES.cityMatchedSeveral, RAISE_CODES.cityNotFound, RAISE_CODES.storeFarFromCity,
];

/** Step 1's needs after a refusal, added to what it already shows. Only what the server's answer asks for is drawn. */
export const moveNeedsAfter = (needs: MoveNeeds, err: unknown): MoveNeeds => {
  const code = hiringCodeOf(err);
  if (!code) return needs;
  const next = { ...needs };
  if (MOVE_CITY_CODES.includes(code)) next.cityPicker = true;
  if (code === RAISE_CODES.staffingHold) {
    const status = hiringDetailOf(err).staffingStatus;
    if (typeof status === 'string' && status.trim()) next.staffingStatus = status.trim();
  }
  if (code === RAISE_CODES.storeHasHiring) next.additional = true;
  return next;
};

/** The body of a review (and of its confirm): the store, and the city and ticks only where step 1 shows them. */
export const buildMoveBody = (pick: {
  storeId: string;
  needs: MoveNeeds;
  cityId: number | null;
  confirmStatus: boolean;
  confirmAdditional: boolean;
}): HiringMoveInput => {
  const body: HiringMoveInput = { storeId: pick.storeId };
  if (pick.needs.cityPicker && pick.cityId != null) body.cityId = pick.cityId;
  if (pick.needs.staffingStatus && pick.confirmStatus) body.confirmStatus = true;
  if (pick.needs.additional && pick.confirmAdditional) body.confirmAdditional = true;
  return body;
};

/** An address of the review, on one line: "1 Sample Road, Sample City". */
export const moveAddressLine = (address: { location?: string | null; cityName?: string | null } | null | undefined): string | null => {
  const location = String(address?.location ?? '').trim();
  const city = String(address?.cityName ?? '').trim();
  if (!location) return city || null;
  return city && !location.toLowerCase().includes(city.toLowerCase()) ? `${location}, ${city}` : location;
};

/**
 * The link as this page has it, after a move the server confirmed: followed for the new store.
 * The figures are the page's (the move's answer did not read them); the store and the address
 * the opening was moved to are the answer's.
 */
export const movedLinkOf = (link: HiringLink, answered: Record<string, unknown> | null | undefined): HiringLink => {
  const after = (answered ?? {}) as Partial<HiringLink>;
  const subject = after.subject && after.subject.kind === 'store' && after.subject.storeId ? after.subject : link.subject;
  const linked = after.address?.linked ?? null;
  return {
    ...link,
    subject,
    address: linked ? { linked, now: linked } : link.address,
    ...(typeof after.updatedAt === 'string' ? { updatedAt: after.updatedAt } : {}),
  };
};

/** The one timeline entry a move writes. */
export const MOVE_EVENT_TYPES: readonly string[] = ['moved_in_channelplex'];

/** A move's timeline entry in words: from which store to which, and the two addresses. Null for any other kind. Never a person. */
export const moveTimelineLine = (event: HiringTimelineEvent): TimelineLine | null => {
  if (event.type !== 'moved_in_channelplex') return null;
  const detail = event.detail ?? {};
  const opening = event.openingId ?? (typeof detail.openingId === 'number' ? detail.openingId : null);
  const side = (raw: unknown) => {
    const s = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const address = s.address && typeof s.address === 'object' ? (s.address as Record<string, unknown>) : null;
    return {
      store: moveStoreLabel({ storeName: textOf(s.storeName), storeCode: textOf(s.storeCode) }),
      address: moveAddressLine({ location: textOf(address?.location), cityName: textOf(address?.cityName) }),
    };
  };
  const from = side(detail.from);
  const to = side(detail.to);
  const details: string[] = [];
  if (from.address) details.push(`Was: ${from.address}`);
  if (to.address) details.push(`Now: ${to.address}`);
  if (detail.changed === false) details.push('Recruitment already had it there: nothing changed and nobody was told.');
  if (detail.by === 'read_back') details.push('Confirmed by reading Recruitment’s list.');
  return {
    title: `${opening != null ? `Opening ${opening}` : 'The opening'} moved from ${from.store} to ${to.store}`,
    details,
    note: null,
  };
};
